(() => {
'use strict';
/* ==========================================================================
   BYŌDŌ City — motor de juego (sin DOM) · v2
   Geometría del BYŌDŌPANAL, solver de operaciones (con ficha X), soluciones
   perfectas, generador determinista de retos, niveles y modo Ciudad (torres).
   ========================================================================== */
const Engine = (() => {
  'use strict';

  /* ---------- Geometría ----------------------------------------------------
     19 fichas hexagonales "flat-top" en 5 columnas (3-4-5-4-3), con la misma
     orientación que en la mesa. Los bordes de las fichas forman los
     operadores al juntarse:
       · vertical (arriba/abajo)             →  =
       · diagonal "/" (arriba-dcha/abajo-izq) →  +
       · diagonal "\" (arriba-izq/abajo-dcha) →  −
  ------------------------------------------------------------------------- */
  const COLS = ['A', 'B', 'C', 'D', 'E'];
  const COL_ROWS = [[2, 3, 4], [1.5, 2.5, 3.5, 4.5], [1, 2, 3, 4, 5], [1.5, 2.5, 3.5, 4.5], [2, 3, 4]];
  const CELLS = [];
  COL_ROWS.forEach((rows, c) => rows.forEach((r, k) => CELLS.push({ id: COLS[c] + (k + 1), c, r, i: CELLS.length })));
  const N = CELLS.length; // 19
  const FULL = (1 << N) - 1;

  const PLUS = 0, MINUS = 1, EQ = 2;
  const OP_CHAR = ['+', '−', '='];
  const OP_ASCII = ['+', '-', '='];
  const X = 10;            // ficha X: incógnita, vale lo que haga cierta la igualdad (0–9)

  const adj = Array.from({ length: N }, () => []);
  const diag = Array.from({ length: N }, () => []);
  const vert = Array.from({ length: N }, () => []);
  const EDGES = [];
  const opBetween = Array.from({ length: N }, () => new Int8Array(N).fill(-1));
  for (let i = 0; i < N; i++) {
    for (let j = i + 1; j < N; j++) {
      const dc = CELLS[j].c - CELLS[i].c, dr = CELLS[j].r - CELLS[i].r;
      let op = -1;
      if (dc === 0 && Math.abs(dr) === 1) op = EQ;
      else if (Math.abs(dc) === 1 && Math.abs(dr) === 0.5) op = (dc * dr < 0) ? PLUS : MINUS;
      if (op < 0) continue;
      EDGES.push({ a: i, b: j, op });
      opBetween[i][j] = opBetween[j][i] = op;
      adj[i].push({ j, op }); adj[j].push({ j: i, op });
      if (op === EQ) { vert[i].push(j); vert[j].push(i); }
      else { diag[i].push({ j, op }); diag[j].push({ j: i, op }); }
    }
  }
  const idToIndex = Object.fromEntries(CELLS.map(c => [c.id, c.i]));

  /* Coordenadas en "unidades de ficha": distancia entre caras planas = 100 */
  const FLAT = 100, RAD = FLAT / Math.sqrt(3);
  const center = i => [(CELLS[i].c - 2) * 1.5 * RAD, (CELLS[i].r - 3) * FLAT];

  /* Formas de panal (para niveles): máscara de 19 bits */
  const SHAPES = {
    flor: [4, 5, 8, 9, 10, 13, 14],                       // 7: centro y su corona
    centro: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],  // 13: las tres columnas centrales
    panal: Array.from({ length: N }, (_, i) => i)            // 19
  };

  /* ---------- Utilidades --------------------------------------------------- */
  const popcount = m => { m = m - ((m >>> 1) & 0x55555555); m = (m & 0x33333333) + ((m >>> 2) & 0x33333333); return (((m + (m >>> 4)) & 0x0F0F0F0F) * 0x01010101) >>> 24; };
  const maskOf = cells => cells.reduce((m, c) => m | (1 << c), 0);
  const cellsOf = m => { const r = []; for (let i = 0; i < N; i++) if (m >>> i & 1) r.push(i); return r; };
  const sym = v => v === X ? 'X' : String(v);
  const opsOfPath = path => path.slice(1).map((c, k) => opBetween[path[k]][c]);

  function exprString(vals, path, ops, ascii) {
    const s0 = ascii ? OP_ASCII : OP_CHAR;
    let s = sym(vals[path[0]]);
    for (let k = 0; k < ops.length; k++) s += ' ' + s0[ops[k]] + ' ' + sym(vals[path[k + 1]]);
    return s;
  }

  /* Lado de una igualdad como forma lineal a + c·x (x = valor de la ficha X) */
  function sideLinear(vals, path, ops, from, to) {
    let a = 0, c = 0, nx = 0;
    const v0 = vals[path[from]];
    if (v0 === X) { c = 1; nx = 1; } else a = v0;
    for (let k = from; k < to; k++) {
      const v = vals[path[k + 1]], sg = ops[k] === PLUS ? 1 : -1;
      if (v === X) { c += sg; nx++; } else a += sg * v;
    }
    return { a, c, nx };
  }

  /* Evalúa un camino arbitrario (lo que traza el jugador) */
  function evaluatePath(vals, path, rule) {
    rule = rule || 'classic';
    if (path.length < 2) return { ok: false, reason: 'short' };
    const ops = [];
    for (let k = 0; k + 1 < path.length; k++) {
      const op = opBetween[path[k]][path[k + 1]];
      if (op < 0) return { ok: false, reason: 'notadjacent' };
      ops.push(op);
    }
    const eqs = ops.reduce((acc, o, k) => (o === EQ ? acc.concat(k) : acc), []);
    if (eqs.length === 0) return { ok: false, reason: 'noeq', ops };
    if (eqs.length > 1) return { ok: false, reason: 'manyeq', ops };
    if (path.length < 3) return { ok: false, reason: 'short', ops };
    const e = eqs[0];
    if (rule === 'classic' && e !== ops.length - 1) return { ok: false, reason: 'eqpos', ops, e };
    const L = sideLinear(vals, path, ops, 0, e), R = sideLinear(vals, path, ops, e + 1, path.length - 1);
    if (L.nx + R.nx > 1) return { ok: false, reason: 'manyx', ops, e };
    if (L.nx + R.nx === 0) return { ok: L.a === R.a, reason: L.a === R.a ? 'ok' : 'wrong', ops, left: L.a, right: R.a, e, xv: null };
    const coef = L.c - R.c, rhs = R.a - L.a;          // coef·x = rhs, coef = ±1
    const x = rhs * coef;
    const ok = x >= 0 && x <= 9;
    return { ok, reason: ok ? 'ok' : 'xrange', ops, e, xv: ok ? x : null, xNeed: x, left: L.a, right: R.a };
  }

  /* ---------- Solver: operaciones válidas -----------------------------------
     Regla clásica: n1 ±… n(k-1) = nk  (el = justo antes del resultado, k ≥ 3)
     Regla libre:   el = puede ir en cualquier posición (sin ficha X)
     Ficha X: como mucho una por operación; su valor se deduce (0–9).
  ------------------------------------------------------------------------- */
  function present(vals) { let m = 0; for (let i = 0; i < N; i++) if (vals[i] >= 0) m |= 1 << i; return m; }
  const OPS_CAP = 40000;

  function operationsClassic(vals, maxLen) {
    maxLen = maxLen || N;
    const out = [];
    const path = new Int8Array(N), ops = new Int8Array(N);
    const record = (depth, w, xv) => {
      const p = Array.from(path.subarray(0, depth)); p.push(w);
      const o = Array.from(ops.subarray(0, depth - 1)); o.push(EQ);
      out.push(makeOp(vals, p, o, depth - 1, xv));
    };
    function dfs(u, depth, visited, a, c, hx) {
      if (out.length >= OPS_CAP) return;
      if (depth >= 2) {
        const vs = vert[u];
        for (let t = 0; t < vs.length; t++) {
          const w = vs[t];
          if (visited >>> w & 1) continue;
          const vw = vals[w];
          if (vw < 0) continue;
          if (vw === X) { if (!hx && a >= 0 && a <= 9) record(depth, w, a); }
          else if (!hx) { if (a === vw) record(depth, w, null); }
          else { const x = (vw - a) * c; if (x >= 0 && x <= 9) record(depth, w, x); }
        }
      }
      if (depth + 1 >= maxLen) return;
      const ds = diag[u];
      for (let t = 0; t < ds.length; t++) {
        const { j, op } = ds[t];
        if (visited >>> j & 1) continue;
        const vj = vals[j];
        if (vj < 0 || (vj === X && hx)) continue;
        const sg = op === PLUS ? 1 : -1;
        path[depth] = j; ops[depth - 1] = op;
        if (vj === X) dfs(j, depth + 1, visited | (1 << j), a, c + sg, true);
        else dfs(j, depth + 1, visited | (1 << j), a + sg * vj, c, hx);
      }
    }
    for (let s = 0; s < N; s++) {
      const v = vals[s]; if (v < 0) continue;
      path[0] = s;
      if (v === X) dfs(s, 1, 1 << s, 0, 1, true); else dfs(s, 1, 1 << s, v, 0, false);
    }
    return finalize(out);
  }

  function operationsFree(vals, maxLen) {
    maxLen = maxLen || N;
    const usable = v => v >= 0 && v !== X;
    const byEnd = Array.from({ length: N }, () => []);
    const byStart = Array.from({ length: N }, () => new Map());
    const path = new Int8Array(N), ops = new Int8Array(N);
    let walks = 0;
    function dfs(s, u, depth, visited, value) {
      if (++walks > 400000) return;
      const w = { p: Array.from(path.subarray(0, depth)), o: Array.from(ops.subarray(0, depth - 1)), m: visited, v: value };
      byEnd[u].push(w);
      let mm = byStart[s].get(value); if (!mm) byStart[s].set(value, mm = []); mm.push(w);
      if (depth + 1 >= maxLen) return;
      const ds = diag[u];
      for (let t = 0; t < ds.length; t++) {
        const { j, op } = ds[t];
        if ((visited >>> j & 1) || !usable(vals[j])) continue;
        path[depth] = j; ops[depth - 1] = op;
        dfs(s, j, depth + 1, visited | (1 << j), op === PLUS ? value + vals[j] : value - vals[j]);
      }
    }
    for (let s = 0; s < N; s++) { if (!usable(vals[s])) continue; path[0] = s; dfs(s, s, 1, 1 << s, vals[s]); }
    const seen = new Map(), out = [];
    for (const { a, b, op } of EDGES) {
      if (op !== EQ || !usable(vals[a]) || !usable(vals[b])) continue;
      for (const [u, w] of [[a, b], [b, a]]) {
        for (const L of byEnd[u]) {
          if (out.length >= OPS_CAP) break;
          const Rs = byStart[w].get(L.v); if (!Rs) continue;
          for (const R of Rs) {
            if (L.m & R.m) continue;
            const len = L.p.length + R.p.length;
            if (len < 3 || len > maxLen) continue;
            const p = L.p.concat(R.p), o = L.o.concat([EQ], R.o);
            const key = canonKey(p);
            const prev = seen.get(key);
            const cand = makeOp(vals, p, o, L.p.length - 1, null);
            if (prev === undefined) { seen.set(key, out.length); out.push(cand); }
            else if (prefer(cand, out[prev])) out[prev] = cand;
          }
        }
      }
    }
    return finalize(out);
  }
  const prefer = (a, b) => (a.path.length - 1 - a.eq) < (b.path.length - 1 - b.eq);
  function canonKey(p) { const f = p.join(','), r = p.slice().reverse().join(','); return f < r ? f : r; }

  function makeOp(vals, path, ops, eqIndex, xv) {
    const last = vals[path[path.length - 1]];
    return { path, ops, eq: eqIndex, mask: maskOf(path), len: path.length, result: last === X ? xv : last, resX: last === X, xv: xv == null ? null : xv, hasX: path.some(c => vals[c] === X), expr: exprString(vals, path, ops) };
  }
  function finalize(list) {
    const truncated = list.length >= OPS_CAP;
    list.sort((x, y) => x.len - y.len || x.result - y.result || (x.expr < y.expr ? -1 : x.expr > y.expr ? 1 : 0));
    list.forEach((o, k) => { o.k = k; o.key = canonKey(o.path); });
    list.truncated = truncated;
    return list;
  }
  function operations(vals, rule, maxLen) { return rule === 'free' ? operationsFree(vals, maxLen) : operationsClassic(vals, maxLen); }

  /* ---------- Soluciones perfectas (cobertura exacta) ----------------------- */
  function perfect(ops, full, opts) {
    opts = opts || {};
    const limit = opts.limit == null ? 400 : opts.limit;
    const cap = opts.cap == null ? 200000 : opts.cap;
    const fixed = opts.fixed || 0;
    const byMask = new Map();
    for (const op of ops) { let l = byMask.get(op.mask); if (!l) byMask.set(op.mask, l = []); l.push(op.k); }
    const masks = Array.from(byMask.keys());
    const cellMasks = Array.from({ length: N }, () => []);
    masks.forEach((m, k) => { for (let c = 0; c < N; c++) if (m >>> c & 1) cellMasks[c].push(k); });
    let count = 0, capped = false;
    const sols = [], stack = [];
    function rec(covered) {
      if (count >= cap) { capped = true; return; }
      if (covered === full) { count++; if (sols.length < limit) sols.push(stack.map(k => masks[k])); return; }
      let best = -1, bestN = 1e9;
      for (let c = 0; c < N; c++) {
        if (!(full >>> c & 1) || (covered >>> c & 1)) continue;
        let n = 0; const cm = cellMasks[c];
        for (let t = 0; t < cm.length; t++) if ((masks[cm[t]] & covered) === 0) n++;
        if (n < bestN) { bestN = n; best = c; if (n === 0) return; }
      }
      const cm = cellMasks[best];
      for (let t = 0; t < cm.length; t++) {
        const m = masks[cm[t]];
        if ((m & covered) !== 0 || (m & ~full) !== 0) continue;
        stack.push(cm[t]); rec(covered | m); stack.pop();
        if (capped) return;
      }
    }
    rec(fixed);
    return { count, capped, sols, byMask };
  }

  function normRules(r) {
    if (typeof r === 'string' || r == null) r = { eq: r || 'classic' };
    return { eq: r.eq === 'free' ? 'free' : 'classic', maxLen: Math.max(3, Math.min(N, r.maxLen || N)) };
  }
  /* Reglas por defecto del reto: = al final y máximo 5 fichas para cerrar el panal */
  const CLOSE_RULES = { eq: 'classic', maxLen: 5 };
  const HUNT_MAX = 6;                                      // la caza llega hasta 6 fichas
  const HUNT_LEVELS = [3, 4, 5, 6];                        // fácil, media, difícil, experta

  function analyze(vals, rules, opts) {
    rules = normRules(rules);
    const t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    const ops = operations(vals, rules.eq, rules.maxLen);
    const full = present(vals);
    const pf = perfect(ops, full, opts);
    const perTile = new Int32Array(N);
    for (const op of ops) for (const c of op.path) perTile[c]++;
    const lens = {};
    for (const op of ops) lens[op.len] = (lens[op.len] || 0) + 1;
    const t1 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    return { vals: Array.from(vals), rules, ops, truncated: !!ops.truncated, full, perfect: pf, perTile: Array.from(perTile), lens, ms: t1 - t0 };
  }

  /* Caza: operaciones únicas por expresión (la misma cuenta escrita en dos
     sitios del panal es UNA operación, como una palabra en el Paraulògic) */
  function huntIndex(vals, maxLen) {
    const ops = operationsClassic(vals, maxLen || HUNT_MAX);
    const byExpr = new Map();
    for (const o of ops) {
      let e = byExpr.get(o.expr);
      if (!e) byExpr.set(o.expr, e = { expr: o.expr, len: o.len, result: o.result, paths: [], keys: [] });
      e.paths.push(o.path); e.keys.push(o.key);
    }
    const list = Array.from(byExpr.values()).sort((a, b) => a.len - b.len || a.result - b.result || (a.expr < b.expr ? -1 : 1));
    return { list, byExpr, byKey: new Map(ops.map(o => [o.key, o.expr])) };
  }
  const points = len => len - 2;

  /* ---------- Aleatoriedad determinista ------------------------------------ */
  function hashStr(s) { let h = 1779033703 ^ s.length; for (let i = 0; i < s.length; i++) { h = Math.imul(h ^ s.charCodeAt(i), 3432918353); h = h << 13 | h >>> 19; } return () => { h = Math.imul(h ^ (h >>> 16), 2246822507); h = Math.imul(h ^ (h >>> 13), 3266489909); return (h ^= h >>> 16) >>> 0; }; }
  function mulberry32(a) { return () => { a |= 0; a = a + 0x6D2B79F5 | 0; let t = Math.imul(a ^ a >>> 15, 1 | a); t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t; return ((t ^ t >>> 14) >>> 0) / 4294967296; }; }
  function rngFrom(seedStr) { return mulberry32(hashStr(seedStr)()); }
  const pick = (rng, arr) => arr[Math.floor(rng() * arr.length)];
  function shuffle(rng, arr) { for (let i = arr.length - 1; i > 0; i--) { const j = Math.floor(rng() * (i + 1)); [arr[i], arr[j]] = [arr[j], arr[i]]; } return arr; }

  /* Juego físico: 6 fichas de cada número 0-9 (y 6 X, 6 Y) */
  const SET_COPIES = 6;
  function inventory(vals) { const n = new Array(11).fill(0); for (const v of vals) if (v >= 0) n[v]++; return n; }
  function setsNeeded(vals) { return Math.max(1, ...inventory(vals).map(x => Math.ceil(x / SET_COPIES))); }
  function randomBoard(rng, mask) {
    const bag = []; for (let d = 0; d <= 9; d++) for (let k = 0; k < SET_COPIES; k++) bag.push(d);
    shuffle(rng, bag);
    const cells = cellsOf(mask == null ? FULL : mask);
    const vals = new Array(N).fill(-1); cells.forEach((c, k) => { vals[c] = bag[k]; });
    return vals;
  }

  /* ---------- Generador con solución "plantada" -----------------------------
     1) Trocea el panal (o la forma) en caminos-operación (±…± y un = final).
     2) Rellena cada camino con números que cumplan la igualdad, sin negativos
        por el camino (como 6 − 3 + 1 + 5 = 9). Opcional: una ficha X.
     3) El solver comprueba cuántas soluciones perfectas hay y la dificultad.
  ------------------------------------------------------------------------- */
  function randomTiling(rng, minLen, maxLen, mask) {
    mask = mask == null ? FULL : mask;
    for (let attempt = 0; attempt < 200; attempt++) {
      let covered = FULL & ~mask; const groups = []; let ok = true;
      while (covered !== FULL) {
        let best = -1, bestN = 99;
        for (let c = 0; c < N; c++) {
          if (covered >>> c & 1) continue;
          let n = 0; for (const { j } of adj[c]) if (!(covered >>> j & 1)) n++;
          if (n < bestN || (n === bestN && rng() < 0.5)) { bestN = n; best = c; }
        }
        const g = growOperation(rng, best, covered, minLen, maxLen);
        if (!g) { ok = false; break; }
        groups.push(g); covered |= maskOf(g.path);
      }
      if (ok) return groups;
    }
    return null;
  }
  function growOperation(rng, must, covered, minLen, maxLen) {
    const target = minLen + Math.floor(rng() * (maxLen - minLen + 1));
    const cands = [];
    const path = [], ops = [];
    function dfs(u, visited) {
      if (path.length >= 2 && path.length + 1 >= minLen) {
        for (const w of vert[u]) {
          if ((visited >>> w & 1) || (covered >>> w & 1)) continue;
          const p = path.concat([w]);
          if (p.includes(must)) cands.push({ path: p, ops: ops.concat([EQ]) });
        }
      }
      if (path.length >= maxLen - 1 || cands.length > 4000) return;
      for (const { j, op } of diag[u]) {
        if ((visited >>> j & 1) || (covered >>> j & 1)) continue;
        path.push(j); ops.push(op); dfs(j, visited | (1 << j)); path.pop(); ops.pop();
      }
    }
    for (let s = 0; s < N; s++) { if (covered >>> s & 1) continue; path.length = 0; ops.length = 0; path.push(s); dfs(s, (1 << s)); }
    if (!cands.length) return null;
    cands.sort((x, y) => Math.abs(x.path.length - target) - Math.abs(y.path.length - target));
    const bestD = Math.abs(cands[0].path.length - target);
    const pool = cands.filter(c => Math.abs(c.path.length - target) <= bestD + 1);
    return pick(rng, pool);
  }
  function fillGroup(rng, g, counts, maxPerDigit, zeroBias, xPos, maxZeros) {
    if (maxZeros == null) maxZeros = 2;
    for (let attempt = 0; attempt < 500; attempt++) {
      const vals = [];
      for (let k = 0; k < g.path.length - 1; k++) {
        let d = Math.floor(rng() * 10);
        if (d === 0 && rng() > zeroBias) d = 1 + Math.floor(rng() * 9);
        vals.push(d);
      }
      let v = vals[0], neg = false;
      for (let k = 1; k < vals.length; k++) { v = g.ops[k - 1] === PLUS ? v + vals[k] : v - vals[k]; if (v < 0) neg = true; }
      if (neg || v < 0 || v > 9) continue;
      vals.push(v);
      if (xPos != null && xPos >= 0) vals[xPos] = X;
      const c2 = counts.slice(); let ok = true;
      for (const d of vals) { c2[d]++; if (d !== X && (c2[d] > maxPerDigit || (d === 0 && c2[0] > maxZeros))) { ok = false; break; } }
      if (!ok) continue;
      for (let d = 0; d < c2.length; d++) counts[d] = c2[d];
      return vals;
    }
    return null;
  }
  function plantedBoard(rng, opt) {
    opt = Object.assign({ minLen: 3, maxLen: 5, maxPerDigit: 4, zeroBias: 0.3, maxZeros: 2, mask: FULL, xCount: 0 }, opt || {});
    for (let attempt = 0; attempt < 60; attempt++) {
      const groups = randomTiling(rng, opt.minLen, opt.maxLen, opt.mask);
      if (!groups) continue;
      const xGroups = new Set(shuffle(rng, groups.map((_, k) => k)).slice(0, opt.xCount));
      const vals = new Array(N).fill(-1); const counts = new Array(11).fill(0); let ok = true;
      for (let k = 0; k < groups.length; k++) {
        const g = groups[k];
        const xPos = xGroups.has(k) ? Math.floor(rng() * g.path.length) : -1;
        const v = fillGroup(rng, g, counts, opt.maxPerDigit, opt.zeroBias, xPos, opt.maxZeros);
        if (!v) { ok = false; break; }
        g.path.forEach((c, i) => { vals[c] = v[i]; });
      }
      if (ok) return { vals, groups };
    }
    return null;
  }

  /* ---------- Dificultad ----------------------------------------------------
     Simula a una persona que conoce las operaciones: coloca las forzadas y,
     cuando no hay, prueba. Cuenta los callejones sin salida hasta cerrar.
  ------------------------------------------------------------------------- */
  function searchEffort(a, rng, K) {
    K = K || 8;
    const masks = Array.from(a.perfect.byMask.keys());
    const full = a.full;
    const cellMasks = Array.from({ length: N }, () => []);
    masks.forEach(m => { for (let c = 0; c < N; c++) if (m >>> c & 1) cellMasks[c].push(m); });
    function propagate(covered) {
      for (;;) {
        let forced = -1;
        for (let c = 0; c < N; c++) {
          if (!(full >>> c & 1) || (covered >>> c & 1)) continue;
          let n = 0, last = 0; const cm = cellMasks[c];
          for (let t = 0; t < cm.length; t++) if ((cm[t] & covered) === 0 && (cm[t] & ~full) === 0) { n++; last = cm[t]; if (n > 1) break; }
          if (n === 0) return -1;
          if (n === 1) { forced = last; break; }
        }
        if (forced === -1) return covered;
        covered |= forced;
      }
    }
    let total = 0, nodesTotal = 0;
    for (let k = 0; k < K; k++) {
      let backtracks = 0, nodes = 0;
      const dfs = covered => {
        nodes++;
        if (nodes > 20000) return true;
        covered = propagate(covered);
        if (covered < 0) { backtracks++; return false; }
        if (covered === full) return true;
        let best = -1, bestN = 1e9;
        for (let c = 0; c < N; c++) {
          if (!(full >>> c & 1) || (covered >>> c & 1)) continue;
          let n = 0; for (const m of cellMasks[c]) if ((m & covered) === 0) n++;
          if (n < bestN) { bestN = n; best = c; }
        }
        const opts = cellMasks[best].filter(m => (m & covered) === 0 && (m & ~full) === 0);
        shuffle(rng, opts);
        for (const m of opts) if (dfs(covered | m)) return true;
        return false;
      };
      dfs(0);
      total += backtracks; nodesTotal += nodes;
    }
    return { backtracks: total / K, nodes: nodesTotal / K };
  }
  const LEVELS = ['Sin solución', 'Fácil', 'Media', 'Difícil', 'Experta'];
  let DIF_T = [2.6, 3.3, 3.8];          // calibrado para cerrar con máximo 5 fichas
  function difficulty(a) {
    const P = a.perfect.count;
    if (!P) return { level: 0, label: LEVELS[0], score: 0 };
    const eff = searchEffort(a, rngFrom('dif-' + a.vals.join('') + '-' + a.rules.eq + a.rules.maxLen), a.ops.length > 1500 ? 6 : 24);
    const used = new Set(); for (const s of a.perfect.sols) for (const m of s) used.add(m);
    const decoys = a.ops.filter(o => !used.has(o.mask)).length;
    const score = Math.log2(1 + eff.backtracks) * 1.0 + Math.log2(1 + decoys) * 0.45 - Math.log2(P) * 0.6;
    const level = score < DIF_T[0] ? 1 : score < DIF_T[1] ? 2 : score < DIF_T[2] ? 3 : 4;
    return { level, label: LEVELS[level], score: +score.toFixed(2), backtracks: +eff.backtracks.toFixed(2), decoys, solutions: P };
  }

  /* ---------- Generador por dificultad --------------------------------------
     Fácil:   operaciones de 3 y 4 fichas (máx. 4), varias soluciones posibles
     Media:   máx. 5 fichas, solución con operaciones cortas, pocas soluciones
     Difícil: máx. 5 fichas, solución única
     Experta: máx. 5 fichas, solución única y muchas operaciones señuelo
  ------------------------------------------------------------------------- */
  const PRESETS = {
    1: { maxLen: 4, plantMax: 4, maxSol: 8, band: [-99, 2.0] },
    2: { maxLen: 5, plantMax: 4, maxSol: 3, band: [2.4, 3.3] },
    3: { maxLen: 5, plantMax: 5, maxSol: 1, band: [3.2, 3.8] },
    4: { maxLen: 5, plantMax: 5, maxSol: 1, band: [3.7, 99] }
  };
  function generatePuzzle(rng, level, extra) {
    level = Math.max(1, Math.min(4, level | 0 || 2));
    const pr = Object.assign({}, PRESETS[level], extra || {});
    const rules = { eq: 'classic', maxLen: pr.maxLen };
    const mask = pr.mask == null ? FULL : pr.mask;
    let best = null, bestDist = 1e9;
    for (let attempt = 0; attempt < 180; attempt++) {
      const pb = plantedBoard(rng, { maxLen: Math.min(pr.plantMax, pr.maxLen), maxPerDigit: pr.maxPerDigit || 4, mask, xCount: pr.xCount || 0 });
      if (!pb) continue;
      const a = analyze(pb.vals, rules, { limit: 60, cap: 4000 });
      const P = a.perfect.count;
      if (!P) continue;
      const d = difficulty(a);
      let dist = 0;
      if (P > pr.maxSol) dist += (P - pr.maxSol) * 2;
      if (d.score < pr.band[0]) dist += pr.band[0] - d.score;
      if (d.score > pr.band[1]) dist += d.score - pr.band[1];
      const res = { vals: pb.vals, analysis: a, difficulty: d, planted: pb.groups, attempt, level, rules };
      if (dist === 0) return res;
      if (dist < bestDist) { bestDist = dist; best = Object.assign(res, { attempt: -1 }); }
    }
    return best;
  }

  /* ---------- Reto diario ----------------------------------------------------
     Determinista por fecha (calendario de Madrid). GEN_V congela el algoritmo:
     si se cambia, se cambia la versión (y los retos futuros).
     Semana: lunes y martes fácil · miércoles a viernes media · fin de semana difícil.
  ------------------------------------------------------------------------- */
  const GEN_V = 'v2';
  let EPOCH = '2026-10-12';   // reto #1 (lunes). Antes de esa fecha: "reto de prueba"
  const WEEK_LEVEL = { 1: 1, 2: 1, 3: 2, 4: 2, 5: 2, 6: 3, 0: 3 }; // getUTCDay(): 0 = domingo
  const ymd = s => s.split('-').map((x, i) => i === 1 ? x - 1 : +x);
  function dayNumber(dateStr) { return Math.round((Date.UTC(...ymd(dateStr)) - Date.UTC(...ymd(EPOCH))) / 86400000) + 1; }
  function weekday(dateStr) { return new Date(Date.UTC(...ymd(dateStr))).getUTCDay(); }
  const dailyCache = new Map();
  function dailyBoard(dateStr) {
    if (dailyCache.has(dateStr)) return dailyCache.get(dateStr);
    const rng = rngFrom('byodo-city-' + GEN_V + '-' + dateStr);
    const level = WEEK_LEVEL[weekday(dateStr)];
    const g = generatePuzzle(rng, level);
    const res = { date: dateStr, number: dayNumber(dateStr), level, vals: g.vals, analysis: g.analysis, difficulty: g.difficulty, rules: g.rules, planted: g.planted, attempt: g.attempt };
    dailyCache.set(dateStr, res);
    return res;
  }

  /* ---------- Modo Ciudad (torres) -------------------------------------------
     Como BYŌDŌ City en la mesa: cada operación correcta cubre sus fichas con
     fichas nuevas de la bolsa y el panal crece en torres. Objetivos por nivel.
     Una ficha es {v, g}: v = valor (0–9 o X), g = dorada.
  ------------------------------------------------------------------------- */
  function cityNew(level) {
    const stacks = Array.from({ length: N }, (_, i) => (level.vals[i] >= 0 ? [{ v: level.vals[i], g: (level.gold || []).includes(i) }] : []));
    return { stacks, bag: level.bag.map(t => (typeof t === 'number' ? { v: t, g: false } : t)), bi: 0, moves: 0, score: 0, goldUsed: 0, history: [] };
  }
  const cityTop = st => st.stacks.map(s => (s.length ? s[s.length - 1].v : -1));
  const cityHeights = st => st.stacks.map(s => s.length);
  function cityPoints(len, golds) { return 10 * len * (len - 1) * (1 + golds); }
  function cityApply(st, path) {
    const ns = { stacks: st.stacks.map(s => s.slice()), bag: st.bag, bi: st.bi, moves: st.moves + 1, score: st.score, goldUsed: st.goldUsed, history: st.history.concat([JSON.stringify({ stacks: st.stacks, bi: st.bi, moves: st.moves, score: st.score, goldUsed: st.goldUsed })]) };
    let golds = 0;
    for (const c of path) if (ns.stacks[c][ns.stacks[c].length - 1].g) golds++;
    const placed = [];
    for (const c of path) { if (ns.bi < ns.bag.length) { ns.stacks[c].push(ns.bag[ns.bi++]); placed.push(c); } }
    const pts = cityPoints(path.length, golds);
    ns.score += pts; ns.goldUsed += golds;
    return { state: ns, points: pts, golds, placed };
  }
  function cityUndo(st) {
    if (!st.history.length) return st;
    const prev = JSON.parse(st.history[st.history.length - 1]);
    return Object.assign({}, st, prev, { history: st.history.slice(0, -1) });
  }
  function cityCoverage(st, mask) { let n = 0, tot = 0; for (let i = 0; i < N; i++) { if (!(mask >>> i & 1)) continue; tot++; if (st.stacks[i].length >= 2) n++; } return { n, tot }; }
  function cityShuffle(st, rng, maxLen) {
    // baraja las fichas de arriba (mismos valores) hasta que haya alguna operación
    const cells = st.stacks.map((s, i) => (s.length ? i : -1)).filter(i => i >= 0);
    for (let t = 0; t < 60; t++) {
      const tops = shuffle(rng, cells.map(i => st.stacks[i][st.stacks[i].length - 1]));
      const ns = Object.assign({}, st, { stacks: st.stacks.map(s => s.slice()) });
      cells.forEach((i, k) => { ns.stacks[i][ns.stacks[i].length - 1] = tops[k]; });
      if (operationsClassic(cityTop(ns), maxLen).length) return ns;
    }
    return null;
  }

  /* Codificación del tablero: 19 caracteres en orden A1…E3 (0–9, X = incógnita, . = vacía) */
  const encode = vals => vals.map(v => v < 0 ? '.' : v === X ? 'X' : String(v)).join('');
  function decode(str) {
    if (typeof str !== 'string') return null;
    const s = str.trim().replace(/[\s,;|/]+/g, '');
    if (s.length !== N || !/^[0-9xX._-]+$/.test(s)) return null;
    return s.split('').map(ch => (ch === 'X' || ch === 'x') ? X : /[._-]/.test(ch) ? -1 : +ch);
  }

  return {
    CELLS, N, FULL, EDGES, adj, diag, vert, opBetween, idToIndex, PLUS, MINUS, EQ, OP_CHAR, OP_ASCII, X, SHAPES,
    FLAT, RAD, center, popcount, maskOf, cellsOf, exprString, evaluatePath, present, sym, opsOfPath,
    operations, perfect, analyze, difficulty, points, normRules, CLOSE_RULES, HUNT_MAX, HUNT_LEVELS, huntIndex, searchEffort, canonKey,
    rngFrom, mulberry32, hashStr, shuffle, pick, inventory, setsNeeded, SET_COPIES, randomBoard,
    plantedBoard, generatePuzzle, PRESETS, dailyBoard, dayNumber, weekday, GEN_V, WEEK_LEVEL, LEVELS, encode, decode,
    cityNew, cityTop, cityHeights, cityApply, cityUndo, cityCoverage, cityShuffle, cityPoints,
    setThresholds: t => { DIF_T = t; }, setEpoch: d => { EPOCH = d; dailyCache.clear(); }, get EPOCH() { return EPOCH; }
  };
})();


const TARGET = "web";
const BC_VERSION = "2.4.305f8ee";
const BC_DEFAULTS = {"shareUrl":"https://www.byodogamesystem.com/pages/retos","productUrl":"https://www.byodogamesystem.com/products/byodo-premium-pack","productName":"BYŌDŌ® Premium Pack","productPrice":"59 €"};
const BC_SPRITE = "<path id=\"bc-tile-shape\" d=\"M53.77 6.88L52.24 9.52C50.28 8.4 48.49 7.56 46.84 6.97C38.53 4.08 33.68 7.95 28.64 16.95C23.32 25.88 22.66 32.17 30.98 38.28C31.98 39.02 33.12 39.75 34.38 40.48L32.84 43.13C30.38 47.38 25.84 50 20.93 50L17.87 50C17.85 47.18 17.61 44.8 17.09 42.81C15.13 35.19 9.43 33.16-0.35 33.28C-9.64 33.15-15.24 35.18-17.14 42.93C-17.63 44.91-17.87 47.24-17.88 50L-20.93 50C-25.84 50-30.38 47.38-32.84 43.13L-34.37 40.48C-34.34 40.47-34.32 40.45-34.3 40.44C-33.12 39.76-32.06 39.08-31.12 38.38C-22.77 32.34-23.4 26.01-28.67 16.9C-33.79 8.01-38.64 4.13-46.83 6.97C-48.47 7.54-50.24 8.38-52.16 9.48C-52.2 9.51-52.22 9.51-52.24 9.54L-53.77 6.88C-56.23 2.62-56.23-2.62-53.77-6.88L-52.24-9.53C-52.22-9.51-52.2-9.51-52.16-9.49C-50.22-8.36-48.45-7.52-46.81-6.96C-38.64-4.12-33.85-7.92-28.96-16.37C-23.39-26.03-22.77-32.44-31.34-38.55C-32.22-39.17-33.21-39.8-34.3-40.44C-34.32-40.44-34.34-40.47-34.37-40.47L-32.84-43.12C-30.38-47.38-25.84-50-20.93-50L-17.88-50L-17.88-49.91C-17.88-47.27-17.65-45.03-17.19-43.12C-15.33-35.36-9.71-33.27-0.3-33.27C9.52-33.27 15.23-35.34 17.14-42.97C17.62-44.9 17.87-47.2 17.87-49.91L17.87-50L20.93-50C25.84-50 30.38-47.38 32.84-43.12L34.38-40.47C33.18-39.79 32.1-39.1 31.15-38.4C22.68-32.25 23.32-25.86 28.99-16.34C33.72-7.86 38.5-4.05 46.82-6.96C48.47-7.55 50.27-8.39 52.24-9.53L53.77-6.88C56.23-2.62 56.23 2.62 53.77 6.88\"/><g id=\"bc-tile-marks\"><path fill=\"#F6A000\" d=\"M-28.96-16.37C-33.85-7.92-38.64-4.12-46.81-6.96C-48.45-7.53-50.23-8.36-52.17-9.48C-52.2-9.51-52.21-9.51-52.25-9.53L-45.37-21.42C-45.36-21.42-45.36-21.42-45.35-21.42C-45.06-21.25-44.78-21.08-44.49-20.93C-42.28-19.67-40.17-18.56-39.14-18.18C-37.06-17.46-35.54-18.06-34.48-19.9C-33.41-21.76-33.66-23.36-35.3-24.82C-36.27-25.6-38.71-27.11-41.22-28.57C-41.24-28.57-41.24-28.57-41.24-28.57C-41.25-28.58-41.25-28.58-41.26-28.58C-41.04-28.98-40.82-29.36-40.58-29.73L-34.37-40.48C-34.35-40.46-34.32-40.45-34.3-40.44C-33.21-39.8-32.22-39.18-31.34-38.54C-22.78-32.44-23.39-26.03-28.96-16.37\"/><path fill=\"#F6A000\" d=\"M52.24 9.53L45.38 21.44C45.36 21.44 45.36 21.43 45.35 21.43C45.06 21.26 44.76 21.09 44.47 20.92C42.23 19.65 40.07 18.52 39.02 18.11C36.95 17.43 35.55 18.08 34.49 19.91C33.42 21.76 33.57 23.29 35.19 24.74C36.17 25.53 38.63 27.06 41.15 28.52C41.17 28.54 41.2 28.55 41.23 28.57C41.24 28.57 41.24 28.58 41.25 28.58L34.37 40.49C33.11 39.75 31.98 39.02 30.98 38.29C22.66 32.16 23.32 25.88 28.64 16.95C33.68 7.95 38.53 4.08 46.84 6.97C48.49 7.56 50.28 8.4 52.24 9.53\"/><path fill=\"#EC680E\" d=\"M52.24-9.53C50.27-8.39 48.48-7.54 46.82-6.96C38.5-4.05 33.73-7.86 28.99-16.34C23.32-25.86 22.68-32.25 31.16-38.4C32.1-39.1 33.18-39.79 34.37-40.48L38.23-33.8L38.21-33.8C37.98-33.67 37.77-33.53 37.58-33.38C36.23-32.38 35.82-31.08 36.41-29.28C36.65-28.66 37.14-27.66 37.77-26.48C36.63-25.77 35.72-25.15 35.2-24.73C33.58-23.29 33.43-21.76 34.5-19.91C35.56-18.08 36.97-17.43 39.02-18.11C39.65-18.35 40.64-18.84 41.83-19.46C42.49-18.39 43.08-17.52 43.49-17.02C44.2-16.21 44.95-15.73 45.75-15.6C46.57-15.45 47.45-15.66 48.38-16.19C48.38-16.19 48.39-16.19 48.39-16.2ZM52.24-9.53\"/><path fill=\"#EC680E\" d=\"M-31.12 38.38C-32.06 39.07-33.12 39.76-34.3 40.44C-34.32 40.45-34.35 40.47-34.37 40.48L-38.22 33.81C-37.98 33.67-37.77 33.53-37.58 33.38C-36.24 32.34-35.87 30.96-36.47 29.14C-36.7 28.55-37.18 27.6-37.76 26.48C-36.69 25.81-35.83 25.22-35.33 24.82C-33.68 23.38-33.44 21.76-34.49 19.93C-35.57 18.06-37.08 17.48-39.17 18.18C-39.76 18.4-40.71 18.88-41.83 19.47C-42.53 18.33-43.16 17.41-43.57 16.9C-44.27 16.1-45.01 15.67-45.78 15.56C-46.59 15.42-47.45 15.66-48.39 16.2L-52.25 9.53C-52.21 9.52-52.2 9.51-52.17 9.49C-50.24 8.37-48.47 7.54-46.83 6.97C-38.64 4.13-33.8 8-28.67 16.9C-23.4 26.01-22.78 32.34-31.12 38.38\"/><path fill=\"#41A7AD\" d=\"M17.86 50L10.31 50C10.31 47.58 10.24 45.14 10.1 43.66C10.08 43.35 10.04 43.08 10.01 42.86C9.56 40.73 8.3 39.84 6.19 39.84C4.06 39.84 2.8 40.73 2.35 42.86C2.31 43.15 2.27 43.55 2.23 44.03C2.11 45.53 2.06 47.77 2.06 50L-2.07 50C-2.07 47.77-2.13 45.54-2.24 44.05C-2.28 43.56-2.32 43.15-2.37 42.86C-2.8 40.73-4.07 39.84-6.19 39.84C-8.32 39.84-9.58 40.73-10.01 42.86C-10.05 43.1-10.08 43.4-10.11 43.75C-10.24 45.24-10.31 47.63-10.31 50L-17.88 50C-17.87 47.24-17.63 44.9-17.14 42.93C-15.24 35.18-9.63 33.15-0.35 33.28C9.43 33.16 15.13 35.19 17.09 42.81C17.6 44.8 17.86 47.18 17.86 50\"/><path fill=\"#41A7AD\" d=\"M17.86-50L17.86-49.91C17.86-47.2 17.62-44.9 17.14-42.97C15.23-35.33 9.52-33.27-0.3-33.27C-9.71-33.27-15.34-35.36-17.19-43.13C-17.66-45.03-17.88-47.28-17.88-49.91L-17.88-50L-10.31-50C-10.31-47.64-10.24-45.3-10.11-43.85C-10.08-43.52-10.05-43.23-10.01-43.01C-9.59-40.85-8.32-39.84-6.19-39.84C-4.05-39.84-2.78-40.85-2.34-43.01C-2.3-43.28-2.26-43.63-2.23-44.05C-2.12-45.5-2.06-47.75-2.07-50L2.06-50C2.06-47.75 2.12-45.5 2.24-44.05C2.28-43.63 2.32-43.28 2.35-43.01C2.77-40.85 4.06-39.84 6.19-39.84C8.33-39.84 9.59-40.85 10.03-43.01C10.06-43.21 10.08-43.47 10.11-43.76C10.25-45.2 10.31-47.6 10.31-50ZM17.86-50\"/></g><path id=\"bc-g0\" d=\"M6.78-1.72C6.78-4.85 4.5-6.3-0.08-6.3C-4.58-6.3-6.95-4.85-6.95-1.72C-6.95 1.41-4.73 2.78-0.08 2.78C4.65 2.78 6.78 1.56 6.78-1.72M23.49 3.24C23.72 19.41 19.07 27.04-0.16 27.04C-18.92 27.04-23.5 19.41-23.5 3.31C-23.5-13.01-16.56-23.23-0.16-23.23C16.71-23.23 23.72-13.01 23.49 3.24\"/><path id=\"bc-g1\" d=\"M11.24-18.54C12.68-13.5 11.46-8.39 11.92-1.98C12.38 6.26 15.43 9.54 14.52 16.56C13.53 22.74 9.25 25.18 1.55 25.18C-7.3 25.18-12.03 22.51-12.03 13.66C-12.03 4.35-6.62 1.37-6.62-5.19C-6.62-8.54-7.99-9-9.59-9.3C-12.03-9.69-12.87-11.37-12.87-14.04C-12.87-20.68-6.01-25.18 1.47-25.18C7.19-25.18 9.79-22.96 11.24-18.54\"/><path id=\"bc-g2\" d=\"M3.01 3.63C7.82 3.63 10.79 1.8 16.44 1.8C21.4 1.8 23.69 5.38 23.69 12.09C23.69 20.56 19.11 25.06 0.27 25.06C-20.1 25.06-23.69 20.34-23.69 10.41C-23.69-0.64-17.36-3.39-5.53-5.3C-1.64-5.99 0.42-6.52 0.42-8.43C0.42-10.11-1.11-10.87-3.09-10.87C-8.43-10.87-11.56-7.21-15.98-7.21C-19.49-7.21-21.55-10.19-21.55-14.53C-21.55-21.02-16.14-25.06 0.72-25.06C16.67-25.06 21.7-20.25 21.7-12.02C21.7-3.47 15.22-2.32 4.08-0.88C1.11-0.34 0.19 0.35 0.19 1.72C0.19 3.02 1.11 3.63 3.01 3.63\"/><path id=\"bc-g3\" d=\"M0.6-9C0.6-10.83-0.77-11.59-2.68-11.59C-7.94-11.59-9.85-9.53-15.65-9.53C-19.16-9.53-20.83-12.2-20.83-16.17C-20.83-22.81-16.34-25.18 0.53-25.18C15.1-25.18 20.29-21.82 20.29-14.42C20.29-9.08 18.46-7.02 13.35-5.34C12.28-4.88 11.74-4.5 11.74-3.66C11.74-2.82 12.28-2.29 13.35-2.14C20.67-0.99 23.57 2.75 23.57 9.54C23.57 20.3 16.78 25.18-1.53 25.18C-19.92 25.18-23.66 20.83-23.66 11.98C-23.66 5.57-20.91 2.22-15.57 2.22C-9.47 2.22-7.18 4.66-2.45 4.66C-0.77 4.66 0.37 3.9 0.37 2.22C0.37 0.46-0.85-0.53-3.67-0.53C-7.71-0.53-11.07 0-14.2 0C-17.02 0-18.32-1.45-18.32-3.58C-18.32-6.1-17.02-7.25-14.05-7.25C-10.16-7.25-7.79-6.41-3.29-6.41C-0.69-6.41 0.6-7.25 0.6-9\"/><path id=\"bc-g4\" d=\"M-7.55 17.59C-7.55 12.4-1.6 9.88-1.6 6.98C-1.6 5.61-2.44 4.85-3.81 4.85C-6.02 4.85-10.53 6.68-14.57 6.68C-19.45 6.68-21.82 5.61-22.5 1.72C-22.81-0.49-22.35-2.86-21.97-6.44C-21.59-11.1-21.28-14.23-20.14-18.35C-18.69-23.53-15.56-25.14-9.23-25.14C-3.35-25.14-1.22-23-1.14-18.12C-0.99-11.79-5.26-9.49-5.26-6.14C-5.18-4.61-4.04-3.92-1.9-3.92C1.99-4.08 3.28-4.92 3.36-6.9C3.44-9.49 2.6-13.24 2.75-17.35C2.83-21.24 6.18-23.07 10.84-23.07C15.49-23.07 18.31-21.7 19.46-18.12C20.76-13.62 19.92-7.89 20.37-1.56C20.76 6.37 23.27 10.34 22.43 16.52C21.52 22.62 17.09 25.14 6.79 25.14C-3.12 25.14-7.55 23.08-7.55 17.59\"/><path id=\"bc-g5\" d=\"M2.74-8.09C5.56-8.09 8.23-8.4 11.82-8.4C19.15-8.4 23.26-4.43 23.26 5.72C23.26 16.47 17.47 23.26 0.53 23.26C-16.48 23.26-23.43 18.46-23.43 9.38C-23.43 3.5-21.14-0.08-16.64-0.08C-11.07-0.08-8.55 2.59-3.13 2.59C-1.15 2.59 0 1.52 0 0.07C0-1.68-1.83-2.52-4.96-2.52C-8.93-2.52-12.82-2.14-16.03-2.14C-19.31-2.14-20.22-3.9-20.22-6.18C-20.22-7.56-19.84-9.31-19.84-11.45C-19.84-13.13-20.22-15.8-20.07-18.17C-19.84-24.42-15.34-27.01 0.3-27.01C18.38-27.01 20.82-23.96 20.82-17.94C20.82-14.58 18.69-11.83 14.42-11.83C9.76-11.83 6.48-12.98 2.89-12.98C0.07-12.98-1.15-12.21-1.15-10.38C-1.15-8.78 0.38-8.09 2.74-8.09\"/><path id=\"bc-g6\" d=\"M6.41 1.26C6.41-0.96 4.5-2.1 0.68-2.1C-3.89-2.1-6.71-0.73-6.71 1.56C-6.71 3.55-4.88 4.54-0.08 4.54C4.66 4.54 6.41 3.55 6.41 1.26M23.27 8.89C23.27 18.42 18.77 25.14 0.15 25.14C-18.69 25.14-23.42 17.59-23.42 1.72C-23.42-16.52-15.64-25.14 0.76-25.14C13.5-25.14 20.68-21.71 20.68-15.68C20.68-11.94 18.69-9.35 14.65-9.35C8.47-9.35 5.65-12.32-1.45-12.32C-4.43-12.32-6.49-11.02-6.49-8.2C-6.49-6.91-5.72-6.22-4.05-6.22C-1.91-6.22 2.9-7.29 9-7.29C17.7-7.29 23.27-3.85 23.27 8.89\"/><path id=\"bc-g7\" d=\"M-3.05-5.42C-6.25-5.42-8.77-3.59-12.43-3.59C-15.56-3.59-18.84-6.19-18.84-12.6C-18.84-21.6-14.18-24.27 2.22-24.27C17.17-24.27 21.6-21.22 22.13-13.67C22.44-7.64 21.83-5.12 20.3-2.52C19.31-0.62 18.16 2.66 17.78 5.48C17.25 9.38 18.01 13.35 16.79 17.69C15.19 22.35 12.21 25.7 0.92 25.7C-9.61 25.7-15.1 22.35-15.1 16.24C-15.1 9.91-9.91 4.95-4.11 0.3C-1.29-1.68-0.61-2.45-0.61-3.67C-0.61-4.97-1.37-5.42-3.05-5.42\"/><path id=\"bc-g8\" d=\"M7.2 1.64C7.2-0.73 5.14-1.87 1.7-1.87C-1.73-1.87-3.79-0.73-3.79 1.64C-3.79 4-2.04 5 1.7 5C5.36 5 7.2 4 7.2 1.64M7.2-9.65C7.2-11.86 5.29-12.93 1.7-12.93C-1.81-12.93-3.79-11.86-3.79-9.65C-3.79-7.36-1.66-6.3 1.7-6.3C4.98-6.3 7.2-7.44 7.2-9.65M-22.41 10.19C-22.41 3.09-19.05-0.88-12.64-2.48C-11.34-2.79-10.81-3.17-10.81-4.08C-10.81-5-11.34-5.38-12.49-5.84C-16.84-7.29-19.05-9.57-19.05-14.31C-19.05-21.86-12.64-25.14 1.7-25.14C15.97-25.14 22.38-21.86 22.38-14.31C22.38-9.57 20.24-7.29 15.89-5.84C14.75-5.38 14.21-5 14.21-4.08C14.21-3.17 14.75-2.79 15.97-2.48C22.46-0.88 25.74 3.09 25.74 10.19C25.74 20.25 18.64 25.14 1.7 25.14C-15.23 25.14-22.41 20.25-22.41 10.19\"/><path id=\"bc-g9\" d=\"M-5.15-10.59C-5.15-8.53-3.48-7.54 0.41-7.54C5.22-7.54 7.74-8.68 7.74-10.9C7.74-13.03 5.75-14.18 1.33-14.18C-3.17-14.18-5.15-13.03-5.15-10.59M-19.35-12.8C-19.35-20.89-13.78-26.84 1.18-26.84C17.27-26.84 24.75-18.07 24.75 0.55C24.75 17.03 19.26 23.43 0.87 23.43C-14.39 23.43-21.71 18.78-21.71 10.54C-21.71 4.13-18.97-0.29-13.47-0.29C-6.38-0.29-4.24 2.99 3.31 2.91C6.13 2.91 7.89 1.62 7.89-1.28C7.89-2.81 7.13-3.49 5.45-3.49C3.23-3.49-1.95-2.27-8.44-2.27C-15.69-2.27-19.35-4.49-19.35-12.8\"/><path id=\"bc-gX\" d=\"M-1.03 6.69C-1.71 7.44-2.06 8.13-2.06 9.02C-2.06 10.46-1.16 12.45-1.16 15.19C-1.16 19.51-4.52 22.6-12.82 22.6C-21.53 22.6-25.64 20.27-25.64 13.34C-25.64 4.9-21.87 2.92-16.46 2.92C-12.82 2.92-11.11 1.07-11.11 0.1C-11.11-0.85-13.03-2.98-16.46-2.98C-21.87-2.98-25.64-4.83-25.64-13.06C-25.64-19.78-22.08-22.59-12.75-22.59C-3.77-22.59-0.55-18.48-1.58-13.47C-2.26-9.43-2.26-8.26-1.03-6.89C-0.75-6.68-0.48-6.48 0-6.48C0.41-6.48 0.69-6.68 0.96-6.89C2.19-8.26 2.19-9.43 1.51-13.47C0.48-18.48 3.71-22.59 12.69-22.59C22.01-22.59 25.65-19.78 25.65-13.06C25.65-4.83 21.81-2.98 16.46-2.98C12.96-2.98 11.04-0.92 11.04 0.1C11.04 1.13 12.82 2.92 16.46 2.92C21.81 2.92 25.65 4.9 25.65 13.34C25.65 20.27 21.46 22.6 12.75 22.6C4.46 22.6 1.1 19.51 1.1 15.19C1.1 12.45 1.99 10.46 1.99 9.02C1.99 8.13 1.72 7.44 0.96 6.69C0.69 6.34 0.41 6.21 0 6.21C-0.48 6.21-0.75 6.34-1.03 6.69\"/><path id=\"bc-gY\" d=\"M-24.18 13.6C-24.18 9.37-21.71 6.76-16.92 6.76C-11.63 6.76-7.48 8.17-3.32 8.17C-1.28 8.17 0.49 7.61 0.49 5.14C0.49 3.45-0.71 2.89-1.98 2.89C-4.87 2.89-7.48 4.08-12.55 4.08C-17.55 4.08-20.02 2.54-21.57-0.21C-22.7-2.05-22.98-4.3-22.98-6.77C-22.98-10.08-24.74-12.69-24.39-15.93C-23.4-21-19.6-23.12-12.48-23.12C-0.57-23.12 0.07-16.42-3.18-11.84C-4.94-9.16-4.94-5.85-0.64-5.85C5.91-5.85 5.28-9.37 3.38-12.4C1.05-16.77 1.83-23.26 12.54-23.26C19.8-23.26 22.83-20.72 24.1-16.21C25.22-12.05 23.11-7.33 23.11-1.34C23.11 5.99 23.95 10.22 22.26 15.15C19.94 20.08 14.65 23.25 0.07 23.25C-18.96 23.25-24.18 20.93-24.18 13.6\"/><g id=\"bc-logo-mark\"><path fill=\"#DAB495\" d=\"M14.11 2.46C18.69 2.46 21.98 4.14 21.98 11.25C21.98 17.1 18.4 19.06 10.93 19.06C3.82 19.06 0.94 16.46 0.94 12.82C0.94 10.49 1.71 8.82 1.71 7.61C1.71 6.87 1.46 6.28 0.82 5.64C0.58 5.35 0.34 5.24 0 5.24C-0.42 5.24-0.65 5.35-0.88 5.64C-1.47 6.28-1.76 6.87-1.76 7.61C-1.76 8.82-1 10.49-1 12.82C-1 16.46-3.89 19.06-10.99 19.06C-18.47 19.06-22 17.1-22 11.25C-22 4.14-18.76 2.46-14.12 2.46C-10.99 2.46-9.53 0.89-9.53 0.09C-9.53-0.72-11.17-2.52-14.12-2.52C-18.76-2.52-22-4.08-22-11.01C-22-16.68-18.94-19.05-10.94-19.05C-3.24-19.05-0.48-15.58-1.36-11.36C-1.94-7.95-1.94-6.96-0.88-5.81C-0.65-5.64-0.42-5.46 0-5.46C0.34-5.46 0.58-5.64 0.82-5.81C1.88-6.96 1.88-7.95 1.28-11.36C0.4-15.58 3.17-19.05 10.87-19.05C18.87-19.05 21.98-16.68 21.98-11.01C21.98-4.08 18.69-2.52 14.11-2.52C11.11-2.52 9.47-0.78 9.47 0.09C9.47 0.95 10.99 2.46 14.11 2.46M53.77-6.87L52.23-9.52C40.39-2.72 34.67-6.17 28.98-16.33C22.67-26.93 22.61-33.66 34.37-40.48L32.84-43.12C30.38-47.38 25.84-50 20.93-50L17.87-50L17.87-49.91C17.87-36.51 12.01-33.26-0.3-33.26C-12-33.26-17.86-36.51-17.86-49.91L-17.86-50L-20.93-50C-25.83-50-30.38-47.38-32.83-43.12L-34.36-40.47C-34.34-40.46-34.31-40.44-34.29-40.43C-22.68-33.73-22.8-27.03-28.95-16.38C-34.81-6.23-40.56-2.77-52.16-9.48C-52.18-9.49-52.21-9.5-52.23-9.51L-53.76-6.87C-56.22-2.62-56.22 2.62-53.76 6.87L-52.23 9.51C-52.21 9.51-52.18 9.49-52.16 9.48C-40.56 2.78-34.81 6.23-28.66 16.89C-22.8 27.03-22.68 33.73-34.29 40.43C-34.31 40.44-34.34 40.46-34.36 40.47L-32.83 43.12C-30.38 47.38-25.83 50-20.93 50L-17.86 50C-17.84 36.35-11.98 33.1-0.35 33.27C12 33.1 17.85 36.4 17.87 50L20.93 50C25.84 50 30.38 47.38 32.84 43.12L34.37 40.48C22.56 33.63 22.67 26.93 28.64 16.94C34.67 6.17 40.44 2.75 52.23 9.53L53.77 6.87C56.22 2.62 56.22-2.62 53.77-6.87\"/><path fill=\"#EA680C\" d=\"M-35.32 24.81C-35.82 25.22-36.68 25.8-37.75 26.47C-37.16 27.59-36.69 28.54-36.46 29.14C-35.77 31.22-36.36 32.74-38.21 33.81C-40.06 34.87-41.66 34.63-43.1 32.97C-43.51 32.47-44.1 31.61-44.77 30.54C-45.95 31.17-46.95 31.65-47.57 31.88C-49.64 32.56-51.04 31.92-52.1 30.09C-53.16 28.25-53.01 26.71-51.4 25.27C-50.87 24.84-49.96 24.23-48.82 23.52C-49.46 22.34-49.94 21.34-50.18 20.71C-50.86 18.66-50.21 17.25-48.38 16.19C-46.54 15.13-45 15.27-43.55 16.89C-43.14 17.41-42.51 18.33-41.81 19.46C-40.7 18.87-39.75 18.4-39.15 18.17C-37.07 17.47-35.56 18.06-34.48 19.92C-33.42 21.75-33.66 23.37-35.32 24.81\"/><path fill=\"#F59F09\" d=\"M-51.37-25.24C-52.99-26.69-53.14-28.23-52.08-30.07C-51.01-31.9-49.61-32.55-47.55-31.87C-46.36-31.41-43.75-30.02-41.21-28.55C-38.7-27.11-36.26-25.59-35.29-24.81C-33.64-23.35-33.39-21.75-34.46-19.9C-35.52-18.06-37.05-17.46-39.12-18.17C-40.28-18.61-42.83-19.97-45.33-21.41C-47.87-22.88-50.38-24.44-51.37-25.24\"/><path fill=\"#42A6AA\" d=\"M-10 56.99C-10.2 55.77-10.3 52.89-10.31 49.99C-10.31 47.06-10.21 44.11-10 42.85C-9.56 40.72-8.31 39.83-6.18 39.83C-4.06 39.83-2.79 40.72-2.35 42.85C-2.16 44.11-2.06 47.06-2.06 49.99C-2.06 52.89-2.15 55.77-2.33 56.99C-2.77 59.14-4.04 60.16-6.18 60.16C-8.31 60.16-9.59 59.14-10 56.99\"/><path fill=\"#42A6AA\" d=\"M-10-43C-10.2-44.22-10.3-47.1-10.31-49.99C-10.31-52.93-10.21-55.88-10-57.14C-9.56-59.27-8.31-60.16-6.18-60.16C-4.06-60.16-2.79-59.27-2.35-57.14C-2.16-55.88-2.06-52.93-2.06-49.99C-2.06-47.1-2.15-44.22-2.33-43C-2.77-40.84-4.04-39.83-6.18-39.83C-8.31-39.83-9.59-40.84-10-43\"/><path fill=\"#42A6AA\" d=\"M2.36 56.99C2.17 55.77 2.07 52.89 2.07 49.99C2.07 47.06 2.17 44.11 2.36 42.85C2.81 40.72 4.07 39.83 6.19 39.83C8.31 39.83 9.57 40.72 10.01 42.85C10.21 44.11 10.31 47.06 10.32 49.99C10.32 52.89 10.22 55.77 10.03 56.99C9.59 59.14 8.34 60.16 6.19 60.16C4.07 60.16 2.79 59.14 2.36 56.99\"/><path fill=\"#42A6AA\" d=\"M2.36-43C2.17-44.22 2.07-47.1 2.07-49.99C2.07-52.93 2.17-55.88 2.36-57.14C2.81-59.27 4.07-60.16 6.19-60.16C8.31-60.16 9.57-59.27 10.01-57.14C10.21-55.88 10.31-52.93 10.32-49.99C10.32-47.1 10.22-44.22 10.03-43C9.59-40.84 8.34-39.83 6.19-39.83C4.07-39.83 2.79-40.84 2.36-43\"/><path fill=\"#F59F09\" d=\"M51.28 25.16C52.92 26.62 53.17 28.22 52.1 30.08C51.04 31.91 49.52 32.52 47.44 31.81C46.28 31.36 43.75 30.01 41.23 28.56C38.69 27.1 36.18 25.54 35.19 24.74C33.57 23.29 33.43 21.75 34.48 19.91C35.56 18.08 36.96 17.43 39.02 18.11C40.2 18.57 42.81 19.96 45.35 21.42C47.86 22.87 50.31 24.39 51.28 25.16\"/><path fill=\"#EA680C\" d=\"M35.19-24.74C35.72-25.15 36.63-25.76 37.77-26.48C37.13-27.66 36.65-28.65 36.42-29.28C35.73-31.34 36.38-32.74 38.21-33.8C40.06-34.86 41.6-34.72 43.04-33.1C43.45-32.59 44.08-31.66 44.78-30.53C45.89-31.12 46.84-31.59 47.44-31.83C49.52-32.52 51.03-31.94 52.11-30.08C53.17-28.25 52.93-26.62 51.28-25.18C50.78-24.78 49.91-24.2 48.84-23.52C49.43-22.4 49.9-21.45 50.13-20.86C50.83-18.77 50.24-17.26 48.38-16.19C46.54-15.12 44.92-15.37 43.49-17.03C43.08-17.52 42.49-18.38 41.82-19.46C40.65-18.83 39.65-18.34 39.03-18.11C36.96-17.43 35.55-18.07 34.5-19.91C33.44-21.74 33.58-23.28 35.19-24.74\"/></g><g id=\"bc-wordmark\"><path style=\"fill:var(--wm,#DAB495)\" d=\"M422.84 11.54C417.25 11.39 411.29 11.39 406.6 11.39C401.76 11.39 395.43 11.27 390.36 11.54C385.78 11.78 383.24 12.41 383.24 18.54C383.24 24.46 386.14 24.98 390.46 25.38C395.55 25.64 401.64 25.48 406.48 25.48C411.42 25.48 417.37 25.48 422.46 25.38C426.39 25.14 429.45 24.75 429.45 18.26C429.45 12.41 426.67 11.78 422.84 11.54\"/><path style=\"fill:var(--wm,#DAB495)\" d=\"M241.48 11.54C235.89 11.39 229.93 11.39 225.25 11.39C220.4 11.39 214.07 11.27 208.98 11.54C204.42 11.78 201.87 12.41 201.87 18.54C201.87 24.46 204.79 24.98 209.1 25.38C214.19 25.64 220.28 25.48 225.12 25.48C230.07 25.48 236.01 25.48 241.11 25.38C245.04 25.14 248.09 24.75 248.09 18.26C248.09 12.41 245.31 11.78 241.48 11.54\"/><path style=\"fill:var(--wm,#DAB495)\" d=\"M40.71 61.04C34.11 61.04 32.44 59.54 32.44 56.36C32.44 51.64 35.14 50.4 41.73 50.4C50.46 50.4 53.64 52.17 53.64 55.59C53.64 58.91 49.72 61.04 40.71 61.04M41.97 83.54C34.73 83.54 32.1 82.28 32.1 78.2C32.1 74.01 34.24 72.24 41.61 72.24C49.97 72.24 53.64 74.37 53.64 77.94C53.64 81.75 50.24 83.54 41.97 83.54M70.05 68.95C68.23 68.67 67.38 67.79 67.38 66.38C67.38 65 68.13 64.34 70.05 63.61C78.27 60.8 81.59 57.4 81.59 48.38C81.59 36.05 71.17 30.71 38.57 30.71C15.05 30.71 9.46 33.87 6.67 42.52C4.39 50.78 5.65 58.64 4.78 69.7C4.13 81.64 1.21 90.28 2.75 99.29C4.62 109.7 11.75 114.42 38.68 114.42C75.23 114.66 87.57 106.4 87.57 88.85C87.57 77.43 82.61 70.84 70.05 68.95\"/><path style=\"fill:var(--wm,#DAB495)\" d=\"M175.07 97.89C172.01 108.56 161.34 114.25 133.8 114.25C99.26 114.25 89.73 109.96 89.73 95.85C89.73 85.43 94.56 78.45 103.82 78.45C113.85 78.45 121.75 80.99 129.49 80.99C132.91 80.99 136.08 79.98 136.08 75.66C136.08 72.75 133.93 71.61 131.62 71.61C126.16 71.61 120.57 73.75 111.94 73.75C102.92 73.75 99.26 70.98 97.22 66.13C95.82 62.98 95.57 59.41 95.82 55.58C96.33 50.13 94.29 45.67 95.3 40.61C96.84 33.64 102.29 30.71 111.71 30.71C127.84 30.71 128.85 39.97 125.41 47.85C123.5 52.68 124.39 58.01 133.29 58.01C144.97 58.01 144.71 52.17 142.68 47.34C140.25 39.85 141.43 30.45 156.16 30.45C166.04 30.45 170.48 34.01 172.91 41.26C175.21 48.36 173.29 56.99 174.05 67.66C174.82 81.25 177.24 88.62 175.07 97.89\"/><path style=\"fill:var(--wm,#DAB495)\" d=\"M236.52 78.2C234.53 78.55 232.89 78.35 231.21 78.09C231.47 79.77 231.68 81.36 231.37 83.4C230.6 86.94 228.54 88.61 225.06 88.61L225.04 88.61C221.56 88.61 219.44 86.94 218.75 83.4C218.43 81.36 218.64 79.75 218.89 78.04C217.06 78.32 215.41 78.55 213.32 78.2C209.82 77.49 208.37 75.39 208.37 71.93C208.37 68.45 209.82 66.38 213.32 65.67C215.41 65.3 217.08 65.52 218.95 65.79C218.64 63.95 218.43 62.29 218.75 60.18C219.47 56.68 221.56 55.25 225.04 55.25L225.06 55.25C228.52 55.25 230.57 56.68 231.33 60.18C231.68 62.29 231.45 63.94 231.19 65.74C232.88 65.52 234.49 65.3 236.52 65.61C240.09 66.34 241.73 68.39 241.73 71.93C241.73 75.39 240.09 77.54 236.52 78.2M224.74 30.55C193.36 30.55 180.01 47.59 180.01 74.77C180.01 101.55 188.78 114.25 224.74 114.25C261.66 114.25 270.19 101.55 270.07 74.64C270.19 47.47 256.98 30.55 224.74 30.55\"/><path style=\"fill:var(--wm,#DAB495)\" d=\"M311.35 75.16C306.14 75.16 302.95 73.24 302.95 66.01C302.95 59.66 306 58.25 311.47 58.25C322.41 58.25 327.09 60.94 327.09 66.38C327.09 72.5 322.51 75.16 311.35 75.16M306.65 30.71C286.31 30.71 280.73 33.89 277.95 42.52C275.64 50.91 276.93 58.76 276.15 69.7C275.43 80.99 272.49 90.39 274.02 99.54C275.93 109.84 283.04 114.39 309.19 114.39C344.74 114.39 357.83 101.67 357.59 74.54C357.83 47.08 342.48 30.71 306.65 30.71\"/><path style=\"fill:var(--wm,#DAB495)\" d=\"M417.91 78.2C415.89 78.55 411.19 78.67 406.4 78.67C401.57 78.67 396.75 78.55 394.68 78.2C391.19 77.45 389.73 75.39 389.73 71.93C389.73 68.45 391.19 66.38 394.68 65.62C396.75 65.3 401.57 65.16 406.4 65.16C411.19 65.16 415.89 65.3 417.91 65.61C421.4 66.32 423.07 68.39 423.07 71.93C423.07 75.39 421.4 77.54 417.91 78.2M406.08 30.55C374.72 30.55 361.37 47.59 361.37 74.77C361.37 101.55 370.16 114.25 406.08 114.25C443.04 114.25 451.55 101.55 451.43 74.64C451.55 47.47 438.36 30.55 406.08 30.55\"/><path style=\"fill:var(--wm,#DAB495)\" d=\"M449.69 10.81L450.93 10.81C452.38 10.81 453.55 10.33 453.55 9.16C453.55 8.13 452.79 7.44 451.14 7.44C450.45 7.44 449.97 7.51 449.69 7.58ZM449.63 17.22L447.01 17.22L447.01 5.93C448.04 5.72 449.49 5.58 451.35 5.58C453.48 5.58 454.45 5.93 455.27 6.41C455.89 6.89 456.38 7.78 456.38 8.89C456.38 10.12 455.41 11.09 454.03 11.5L454.03 11.64C455.13 12.05 455.75 12.88 456.1 14.39C456.45 16.12 456.65 16.8 456.93 17.22L454.1 17.22C453.76 16.8 453.55 15.77 453.21 14.46C453 13.23 452.31 12.68 450.86 12.68L449.63 12.68ZM442.67 11.3C442.67 16.32 446.39 20.32 451.48 20.32C456.45 20.32 460.09 16.32 460.09 11.36C460.09 6.34 456.45 2.27 451.41 2.27C446.39 2.27 442.67 6.34 442.67 11.3M462.99 11.3C462.99 17.7 457.96 22.73 451.41 22.73C444.94 22.73 439.77 17.7 439.77 11.3C439.77 5.03 444.94 0 451.41 0C457.96 0 462.99 5.03 462.99 11.3\"/></g><g id=\"bc-tagline\"><path d=\"M19.05 128.93L19.05 131.11C19.05 131.92 18.4 132.57 17.59 132.57L12.23 132.57L12.23 154.28C12.23 155.09 11.62 155.73 10.82 155.73L8.19 155.73C7.43 155.73 6.78 155.09 6.78 154.28L6.78 132.57L1.41 132.57C0.64 132.57 0 131.92 0 131.11L0 128.93C0 128.12 0.64 127.48 1.41 127.48L17.59 127.48C18.4 127.48 19.05 128.12 19.05 128.93\"/><path d=\"M41.45 143.3L41.45 154.28C41.45 155.09 40.8 155.73 40 155.73L37.77 155.73C36.97 155.73 36.32 155.09 36.32 154.28L36.32 144.35C36.32 141.73 35.23 139.75 32.33 139.75C29.3 139.75 27.97 141.97 27.97 144.35L27.97 154.28C27.97 155.09 27.36 155.73 26.55 155.73L24.3 155.73C23.53 155.73 22.88 155.09 22.88 154.28L22.88 128.93C22.88 128.12 23.53 127.48 24.3 127.48L26.55 127.48C27.36 127.48 27.97 128.12 27.97 128.93L27.97 137.77C29.1 136.12 30.95 134.79 33.94 134.79C39.11 134.79 41.45 138.94 41.45 143.3\"/><path d=\"M60.7 143.54C60.18 140.84 58.36 139.35 55.61 139.35C53.11 139.35 51.13 140.92 50.53 143.54ZM65.79 146.09C65.79 146.85 65.14 147.5 64.37 147.5L50.53 147.5C51.25 150.2 53.52 151.66 56.34 151.66C58.2 151.66 59.57 150.89 60.38 150.2C60.74 149.88 61.22 149.84 61.63 150.08L63.93 151.45C64.54 151.82 64.61 152.62 64.13 153.15C62.36 154.93 59.61 156.21 56.34 156.21C49.56 156.21 45.08 151.74 45.08 145.52C45.08 139.1 49.88 134.79 55.54 134.79C61.34 134.79 66.07 138.86 65.79 146.09\"/><path d=\"M105.18 127.48L108.36 127.48C109.17 127.48 109.82 128.12 109.82 128.93L109.82 154.28C109.82 155.09 109.17 155.73 108.36 155.73L105.78 155.73C104.97 155.73 104.33 155.09 104.33 154.28L104.33 136.96L96.21 150.2C96.02 150.48 95.65 150.69 95.29 150.69L94.68 150.69C94.28 150.69 93.96 150.48 93.75 150.2L85.6 137L85.6 154.28C85.6 155.09 85 155.73 84.19 155.73L81.57 155.73C80.8 155.73 80.15 155.09 80.15 154.28L80.15 128.93C80.15 128.12 80.8 127.48 81.57 127.48L84.79 127.48C85.52 127.48 86.21 127.88 86.61 128.49L94.96 142.01L103.36 128.49C103.72 127.84 104.45 127.48 105.18 127.48\"/><path d=\"M129.96 145.52C129.96 142.33 127.94 139.71 124.43 139.71C120.92 139.71 118.9 142.37 118.9 145.52C118.9 148.63 120.92 151.33 124.43 151.33C127.94 151.33 129.96 148.67 129.96 145.52M131.61 135.27L133.83 135.27C134.64 135.27 135.29 135.91 135.29 136.72L135.29 154.28C135.29 155.09 134.64 155.73 133.83 155.73L131.61 155.73C130.8 155.73 130.16 155.09 130.16 154.28L130.16 153.31C128.87 155 126.81 156.22 123.62 156.22C117.29 156.22 113.65 150.85 113.65 145.52C113.65 140.15 117.29 134.79 123.62 134.79C126.81 134.79 128.87 136 130.16 137.69L130.16 136.72C130.16 135.91 130.8 135.27 131.61 135.27\"/><path d=\"M148.64 139.95L145.74 139.95L145.74 147.42C145.74 149.76 146.06 151.13 148.88 151.13L148.96 151.13C149.57 151.13 150.1 151.61 150.1 152.22L150.1 154.88C150.1 155.45 149.69 155.89 149.17 155.97C148.8 156.02 148.44 156.02 147.96 156.02C142.71 156.02 140.61 152.5 140.61 148.27L140.61 139.95L138.75 139.95C137.99 139.95 137.34 139.3 137.34 138.54L137.34 136.72C137.34 135.92 137.99 135.27 138.75 135.27L140.61 135.27L140.61 132.45C140.61 131.64 141.21 130.99 142.02 130.99L144.29 130.99C145.09 130.99 145.74 131.64 145.74 132.45L145.74 135.27L148.64 135.27C149.45 135.27 150.1 135.92 150.1 136.72L150.1 138.54C150.1 139.3 149.45 139.95 148.64 139.95\"/><path d=\"M172.9 143.3L172.9 154.28C172.9 155.09 172.25 155.73 171.45 155.73L169.23 155.73C168.42 155.73 167.77 155.09 167.77 154.28L167.77 144.35C167.77 141.73 166.68 139.75 163.78 139.75C160.75 139.75 159.42 141.97 159.42 144.35L159.42 154.28C159.42 155.09 158.81 155.73 158.01 155.73L155.75 155.73C154.98 155.73 154.33 155.09 154.33 154.28L154.33 128.93C154.33 128.12 154.98 127.48 155.75 127.48L158.01 127.48C158.81 127.48 159.42 128.12 159.42 128.93L159.42 137.77C160.55 136.12 162.41 134.79 165.39 134.79C170.56 134.79 172.9 138.94 172.9 143.3\"/><path d=\"M203.57 139.75L213.3 139.75C214.11 139.75 214.75 140.39 214.75 141.16L214.75 154.64C214.75 155.25 214.27 155.73 213.66 155.73L212.69 155.73C212.17 155.73 211.72 155.37 211.64 154.84L211.08 151.7C209.18 154.44 206.16 156.21 201.75 156.21C193.4 156.21 187.43 149.88 187.43 141.61C187.43 133.33 193.4 126.96 201.75 126.96C205.79 126.96 209.26 128.45 211.8 130.91C212.45 131.56 212.37 132.65 211.6 133.13L209.46 134.54C208.9 134.91 208.17 134.83 207.69 134.38C206.23 133.05 204.26 132.2 201.75 132.2C195.95 132.2 192.88 136.93 192.88 141.61C192.88 146.29 195.95 151.01 201.75 151.01C206.68 151.01 209.38 148.14 210.19 144.51L203.57 144.51C202.77 144.51 202.12 143.87 202.12 143.06L202.12 141.16C202.12 140.39 202.77 139.75 203.57 139.75\"/><path d=\"M234.49 145.52C234.49 142.33 232.47 139.71 228.96 139.71C225.45 139.71 223.43 142.37 223.43 145.52C223.43 148.63 225.45 151.33 228.96 151.33C232.47 151.33 234.49 148.67 234.49 145.52M236.14 135.27L238.36 135.27C239.17 135.27 239.81 135.91 239.81 136.72L239.81 154.28C239.81 155.09 239.17 155.73 238.36 155.73L236.14 155.73C235.34 155.73 234.69 155.09 234.69 154.28L234.69 153.31C233.4 155 231.34 156.22 228.15 156.22C221.81 156.22 218.18 150.85 218.18 145.52C218.18 140.15 221.81 134.79 228.15 134.79C231.34 134.79 233.4 136 234.69 137.69L234.69 136.72C234.69 135.91 235.34 135.27 236.14 135.27\"/><path d=\"M274.16 142.94L274.16 154.28C274.16 155.09 273.52 155.73 272.75 155.73L270.49 155.73C269.68 155.73 269.04 155.09 269.04 154.28L269.04 143.99C269.04 141.45 267.98 139.75 265.61 139.75C263.22 139.75 261.97 141.65 261.97 143.99L261.97 154.28C261.97 155.09 261.32 155.73 260.52 155.73L258.3 155.73C257.49 155.73 256.85 155.09 256.85 154.28L256.85 143.99C256.85 141.45 255.8 139.75 253.41 139.75C251.04 139.75 249.79 141.65 249.79 143.99L249.79 154.28C249.79 155.09 249.14 155.73 248.33 155.73L246.11 155.73C245.3 155.73 244.66 155.09 244.66 154.28L244.66 136.72C244.66 135.91 245.3 135.27 246.11 135.27L248.33 135.27C249.14 135.27 249.79 135.91 249.79 136.72L249.79 137.45C250.83 135.96 252.49 134.79 255.19 134.79C257.98 134.79 259.79 136.08 260.84 137.97C262.09 136.2 264.15 134.79 267.02 134.79C271.74 134.79 274.16 138.58 274.16 142.94\"/><path d=\"M293.41 143.54C292.89 140.84 291.07 139.35 288.33 139.35C285.82 139.35 283.85 140.92 283.24 143.54ZM298.5 146.09C298.5 146.85 297.85 147.5 297.09 147.5L283.24 147.5C283.97 150.2 286.23 151.66 289.05 151.66C290.91 151.66 292.28 150.89 293.09 150.2C293.45 149.88 293.94 149.84 294.34 150.08L296.64 151.45C297.25 151.82 297.33 152.62 296.84 153.15C295.07 154.93 292.32 156.21 289.05 156.21C282.27 156.21 277.79 151.74 277.79 145.52C277.79 139.1 282.6 134.79 288.25 134.79C294.06 134.79 298.78 138.86 298.5 146.09\"/><path d=\"M303.82 141.57L313.88 141.57C314.68 141.57 315.33 142.21 315.33 142.98L315.33 144.43C315.33 145.24 314.68 145.84 313.88 145.84L303.82 145.84C303.02 145.84 302.37 145.24 302.37 144.43L302.37 142.98C302.37 142.21 303.02 141.57 303.82 141.57\"/><path d=\"M340.84 147.5C340.84 151.94 337.73 156.21 330.3 156.21C325.66 156.21 321.42 154.56 319.57 150.97C319.16 150.16 319.57 149.2 320.41 148.95L322.96 148.14C323.6 147.94 324.29 148.18 324.61 148.75C325.42 150.12 327.11 151.45 330.26 151.45C333.09 151.45 335.35 150.36 335.35 147.9C335.35 145.12 332.16 144.91 327.68 143.79C321.99 142.37 320.09 139.55 320.09 135.63C320.09 130.3 324.17 126.96 330.06 126.96C334.14 126.96 337.48 128.73 339.1 132C339.5 132.81 339.02 133.78 338.13 134.02L335.59 134.62C334.98 134.79 334.34 134.5 334.02 133.94C333.37 132.77 332 131.76 329.78 131.76C327.48 131.76 325.5 132.97 325.5 135.27C325.5 138.42 329.09 138.9 331.71 139.51C338.41 141.04 340.84 142.98 340.84 147.5\"/><path d=\"M358.51 135.27L361.22 135.27C361.98 135.27 362.51 136.04 362.23 136.76C360.53 141 355.73 153.15 354.68 155.73C352.74 160.5 350.93 163.52 346.81 163.52C346.45 163.52 346 163.48 345.56 163.4C345.04 163.36 344.63 162.87 344.63 162.35L344.63 160.41C344.63 159.85 345.07 159.36 345.64 159.32C347.78 159.24 348.99 157.95 350.12 154.52C349.84 153.84 344.27 141.2 342.29 136.8C341.97 136.08 342.49 135.27 343.3 135.27L345.92 135.27C346.69 135.27 347.34 135.71 347.62 136.44L352.38 148.75L356.82 136.48C357.1 135.75 357.75 135.27 358.51 135.27\"/><path d=\"M381.07 149.43C381.07 153.43 378.17 156.21 372.4 156.21C368.97 156.21 365.54 155.05 364.08 152.42C363.76 151.86 364.04 151.09 364.73 150.89L367.59 150C368.08 149.84 368.56 150.04 368.84 150.48C369.29 151.21 370.34 151.94 372.48 151.94C374.62 151.94 375.83 151.13 375.83 149.76C375.83 147.86 372.88 147.9 370.46 147.42C366.5 146.61 364.49 144.67 364.49 141.2C364.49 137.33 367.76 134.79 372.52 134.79C375.63 134.79 378.65 136.12 379.95 138.82C380.23 139.43 379.86 140.15 379.22 140.31L376.59 140.96C376.15 141.08 375.67 140.88 375.43 140.48C374.94 139.71 374.01 139.1 372.52 139.1C371.02 139.1 369.49 139.63 369.49 141.16C369.49 142.98 372.23 143.14 373.85 143.5C378.89 144.59 381.07 145.96 381.07 149.43\"/><path d=\"M393.43 139.95L390.52 139.95L390.52 147.42C390.52 149.76 390.84 151.13 393.67 151.13L393.75 151.13C394.35 151.13 394.88 151.61 394.88 152.22L394.88 154.88C394.88 155.45 394.47 155.89 393.95 155.97C393.59 156.02 393.22 156.02 392.74 156.02C387.49 156.02 385.39 152.5 385.39 148.27L385.39 139.95L383.54 139.95C382.77 139.95 382.13 139.3 382.13 138.54L382.13 136.72C382.13 135.92 382.77 135.27 383.54 135.27L385.39 135.27L385.39 132.45C385.39 131.64 386 130.99 386.8 130.99L389.07 130.99C389.88 130.99 390.52 131.64 390.52 132.45L390.52 135.27L393.43 135.27C394.23 135.27 394.88 135.92 394.88 136.72L394.88 138.54C394.88 139.3 394.23 139.95 393.43 139.95\"/><path d=\"M413.12 143.54C412.6 140.84 410.78 139.35 408.04 139.35C405.53 139.35 403.55 140.92 402.95 143.54ZM418.2 146.09C418.2 146.85 417.56 147.5 416.79 147.5L402.95 147.5C403.68 150.2 405.94 151.66 408.76 151.66C410.62 151.66 411.99 150.89 412.8 150.2C413.16 149.88 413.64 149.84 414.05 150.08L416.35 151.45C416.95 151.82 417.04 152.62 416.55 153.15C414.77 154.93 412.03 156.21 408.76 156.21C401.98 156.21 397.5 151.74 397.5 145.52C397.5 139.1 402.3 134.79 407.95 134.79C413.77 134.79 418.49 138.86 418.2 146.09\"/><path d=\"M451.38 142.94L451.38 154.28C451.38 155.09 450.73 155.73 449.97 155.73L447.71 155.73C446.9 155.73 446.25 155.09 446.25 154.28L446.25 143.99C446.25 141.45 445.21 139.75 442.82 139.75C440.45 139.75 439.19 141.65 439.19 143.99L439.19 154.28C439.19 155.09 438.55 155.73 437.74 155.73L435.52 155.73C434.71 155.73 434.07 155.09 434.07 154.28L434.07 143.99C434.07 141.45 433.02 139.75 430.64 139.75C428.25 139.75 427 141.65 427 143.99L427 154.28C427 155.09 426.36 155.73 425.55 155.73L423.33 155.73C422.52 155.73 421.88 155.09 421.88 154.28L421.88 136.72C421.88 135.91 422.52 135.27 423.33 135.27L425.55 135.27C426.36 135.27 427 135.91 427 136.72L427 137.45C428.05 135.96 429.71 134.79 432.41 134.79C435.2 134.79 437.01 136.08 438.06 137.97C439.31 136.2 441.37 134.79 444.24 134.79C448.96 134.79 451.38 138.58 451.38 142.94\"/></g>";
/* ======================================================================
   txt.js — textos compartidos: un solo nombre para cada cosa y concordancia
   de número (n = 0, 1, 2…). Sin DOM: se prueba en Node (test/check.js).
   Regla: ninguna plantilla de la interfaz pone un verbo o un adjetivo fijo
   delante de un número («Quedan ${n}…»); se usa una función de aquí.
   ====================================================================== */
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;
const TXT = {
  daily: 'Reto del día',
  undo: 'Deshacer', hint: 'Pista', restart: 'Reiniciar', retry: 'Reintentar', replay: 'Repetir', map: 'Mapa', next: 'Siguiente nivel',
  tiles: n => plural(n, 'ficha', 'fichas'),
  ops: n => plural(n, 'operación', 'operaciones'),
  errors: n => plural(n, 'fallo', 'fallos'),
  hints: n => plural(n, 'pista', 'pistas'),
  moves: n => plural(n, 'movimiento', 'movimientos'),
  points: n => plural(n, 'punto', 'puntos'),
  /* tamaño de operación que se enseña: el máximo real del nivel (y nunca más fichas de las que hay) */
  span(maxLen, tilesOnBoard) {
    const max = Math.min(maxLen, tilesOnBoard || maxLen);
    return max <= 3 ? `Usa las ${max} fichas` : `De 3 a ${max} fichas`;
  },
  starRule: 'Sin pistas y como mucho 1 fallo',
  ops3: '+ y − van en diagonal; el =, en vertical',
  huntPoints: 'Cada operación da tantos puntos como fichas tiene, menos dos (3 fichas = 1 punto).',
  minus: n => (n < 0 ? '−' + Math.abs(n) : String(n)),
  /* verbo + número + nombre, siempre concordados */
  left: (n, one = 'ficha', many = 'fichas') => (n === 1 ? `Queda 1 ${one}` : `Quedan ${n} ${many}`),
  missing: (n, one = 'ficha', many = 'fichas') => (n === 1 ? `Falta 1 ${one}` : `Faltan ${n} ${many}`),
  youMiss: (n, what = '★') => (n === 1 ? `Te falta 1 ${what}` : `Te faltan ${n} ${what}`),
  /* al perder (v4): lo que faltaba, para que se vea el «casi» */
  lacked: (n, one = 'ficha', many = 'fichas') => (n === 1 ? `Te faltaba 1 ${one}` : `Te faltaban ${n} ${many}`),
  movesLeft: n => (n === 1 ? 'Te queda 1 movimiento' : `Te quedan ${n} movimientos`),
  secs: n => plural(n, 'segundo', 'segundos'),
  the: (n, one, many) => (n === 1 ? `La ${one}` : `Las ${n} ${many}`),
  marked: n => (n === 1 ? 'Marcada la ficha que va después del =' : `Marcadas las ${n} fichas que van después de un =`),
  found: (got, total) => `${got} de ${total} ${total === 1 ? 'encontrada' : 'encontradas'}`,
  /* «1 operación de 4 fichas» · «5 operaciones de 4, 4, 4, 4 y 3 fichas» */
  opSizes(sizes) {
    const n = sizes.length;
    if (!n) return '0 operaciones';
    if (n === 1) return `1 operación de ${sizes[0]} fichas`;
    return `${n} operaciones de ${sizes.slice(0, -1).join(', ')} y ${sizes[n - 1]} fichas`;
  }
};


/* ======================================================================
   dates.js — calendario de Madrid sin DOM (se prueba en Node: test/check.js)
   Crear un Intl.DateTimeFormat cuesta varios ms en un móvil, así que para
   Europe/Madrid (la zona del reto) la hora se calcula con la regla de la UE:
   UTC+2 desde el último domingo de marzo a la 01:00 UTC hasta el último
   domingo de octubre a la 01:00 UTC; UTC+1 el resto del año. Para cualquier
   otra zona se usa Intl. Los nombres de días y meses son los de es-ES.
   ====================================================================== */
const MADRID_TZ = 'Europe/Madrid';
const _dtf = {};
function dtf(key, locale, opts) { return _dtf[key] || (_dtf[key] = new Intl.DateTimeFormat(locale, opts)); }
const _tz = () => (typeof CONFIG !== 'undefined' && CONFIG.timeZone) || MADRID_TZ;
/* último domingo del mes m (0-11) del año y, a la 01:00 UTC */
function _lastSunday(y, m) { const x = new Date(Date.UTC(y, m + 1, 0)); return Date.UTC(y, m, x.getUTCDate() - x.getUTCDay(), 1); }
function madridOffsetMs(t) { const y = new Date(t).getUTCFullYear(); return t >= _lastSunday(y, 2) && t < _lastSunday(y, 9) ? 7200000 : 3600000; }
/* diferencia (ms) entre la hora local de la zona del reto y UTC en el instante t */
function tzOffsetMs(t) {
  const tz = _tz();
  if (tz === MADRID_TZ) return madridOffsetMs(t);
  const o = {};
  for (const p of dtf('parts|' + tz, 'en-GB', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' }).formatToParts(new Date(t))) o[p.type] = p.value;
  return Date.UTC(+o.year, +o.month - 1, +o.day, +o.hour % 24, +o.minute, +o.second) - Math.floor(t / 1000) * 1000;
}
/* «AAAA-MM-DD» de hoy (o de la fecha d) en la zona del reto */
function todayStr(d) {
  const t = (d || new Date()).getTime();
  try { return new Date(t + tzOffsetMs(t)).toISOString().slice(0, 10); }
  catch (e) { return new Date(t).toISOString().slice(0, 10); }
}
function addDays(ds, n) { const [y, m, d] = ds.split('-').map(Number); const t = new Date(Date.UTC(y, m - 1, d + n)); return t.toISOString().slice(0, 10); }
const WEEKDAYS_ES = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const WEEKDAYS_ES_SHORT = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
const MONTHS_ES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const MONTHS_ES_SHORT = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sept', 'oct', 'nov', 'dic'];
/* «Lunes, 12 de octubre» · «Lun, 12 oct» (lo mismo que Intl es-ES, sin crearlo) */
function dateLabel(ds, style) {
  const [y, m, d] = ds.split('-').map(Number);
  const wd = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  const t = style === 'short' ? `${WEEKDAYS_ES_SHORT[wd]}, ${d} ${MONTHS_ES_SHORT[m - 1]}` : `${WEEKDAYS_ES[wd]}, ${d} de ${MONTHS_ES[m - 1]}`;
  return t.charAt(0).toUpperCase() + t.slice(1);
}
/* «Octubre de 2026» (calendario) */
function monthLabel(y, m) { return MONTHS_ES[m - 1].charAt(0).toUpperCase() + MONTHS_ES[m - 1].slice(1) + ' de ' + y; }
/* ms hasta la próxima medianoche de la zona del reto, con la fecha real de mañana:
   los días de cambio de hora (25 h o 23 h) también cuadran */
function msToMadridMidnight(nowMs) {
  const now = nowMs == null ? Date.now() : nowMs;
  try {
    const [y, m, d] = addDays(todayStr(new Date(now)), 1).split('-').map(Number);
    const base = Date.UTC(y, m - 1, d);
    let t = base - tzOffsetMs(now);
    t = base - tzOffsetMs(t);                                // por si el cambio de hora cae entre medias
    return Math.max(0, t - now);
  } catch (e) { return 0; }
}


/* ======================================================================
   core.js — configuración, utilidades, almacenamiento, sonido, vibración
   ====================================================================== */
const E = Engine;
/* TARGET lo fija el empaquetador: 'internal' (artifact), 'web' (tienda) o 'app' (beta PWA) */
const CONFIG = Object.assign({
  shareUrl: '',
  timeZone: 'Europe/Madrid',
  storeKey: 'byodo-city:v2',
  epoch: '2026-10-12',
  rules: E.CLOSE_RULES,                                  // cerrar: = al final, máx. 5 fichas
  penalties: { check: 10, structure: 20, results: 30, reveal: 45 }, // segundos
  groupColors: ['#F6A000', '#EC680E', '#41A7AD', '#9BC255', '#E7799A', '#9C8BDB'],
  // Tienda (versión web): todo opcional
  productUrl: '', productName: 'BYŌDŌ', productPrice: '', productBlurb: '', discountCode: '', discountText: '',
  newsletterUrl: '', educatorsUrl: '', appUrl: '',
  prizeText: '', prizeContact: '',
  // analítica sin cookies (vacío = no se envía nada): endpoint propio, Umami o Plausible
  analyticsUrl: '', analyticsKind: '', analyticsSite: '',
  supabaseUrl: '', supabaseKey: '',
  // fin del Mundo 1 (BYD-032, lo decide Gabriel): 'ficha' = «Una ficha nueva te espera…» · 'neutro' = sin adelantar contenido
  world2Teaser: 'ficha'
}, (typeof BC_DEFAULTS !== 'undefined' ? BC_DEFAULTS : {}), readPageConfig());
function readPageConfig() {
  // <script type="application/json" id="bc-config"> en la página (sección de Shopify); los campos vacíos no pisan los valores por defecto
  try {
    const el = document.getElementById('bc-config');
    const raw = el ? JSON.parse(el.textContent || '{}') : {};
    return Object.fromEntries(Object.entries(raw || {}).filter(([, v]) => v != null && v !== ''));
  } catch (e) { return {}; }
}
E.setEpoch(CONFIG.epoch);

/* ---------- Sprite de marca (fichas, cifras, logo) ---------- */
function ensureSprite() {
  if (document.getElementById('bc-sprite') || typeof BC_SPRITE === 'undefined') return;
  const go = () => { if (!document.getElementById('bc-sprite')) document.body.insertAdjacentHTML('beforeend', '<svg id="bc-sprite" width="0" height="0" style="position:absolute;width:0;height:0;overflow:hidden" aria-hidden="true" focusable="false"><defs>' + BC_SPRITE + '</defs></svg>'); };
  if (document.body) go(); else document.addEventListener('DOMContentLoaded', go);
}
ensureSprite();
// versiones a pantalla completa (artifact, app): el fondo de mesa va en <body>
(function pageBg() {
  const r = document.getElementById('bc-root');
  if (r && document.body && /\bbc-(full|app)\b/.test(r.className)) document.body.classList.add('bc-page');
})();

/* ---------- DOM ---------- */
const $ = (sel, root) => (root || document).querySelector(sel);
const $$ = (sel, root) => Array.from((root || document).querySelectorAll(sel));
const ROOT = () => document.getElementById('bc-root');
const SVGNS = 'http://www.w3.org/2000/svg';
function applyAttrs(el, attrs, isSvg) {
  if (!attrs) return;
  for (const [k, v] of Object.entries(attrs)) {
    if (v == null || v === false) continue;
    if (k === 'class') el.setAttribute('class', v);
    else if (k === 'style' && typeof v === 'object') Object.assign(el.style, v);
    else if (k === 'dataset') Object.assign(el.dataset, v);
    else if (k.startsWith('on') && typeof v === 'function') el.addEventListener(k.slice(2), v);
    else if (k === 'text') el.textContent = v;
    else if (k === 'html') el.innerHTML = v;
    else if (!isSvg && (k === 'value' || k === 'checked' || k === 'disabled')) el[k] = v;
    else el.setAttribute(k, v === true ? '' : v);
  }
}
function appendKids(el, kids) {
  for (const c of kids.flat(Infinity)) {
    if (c == null || c === false) continue;
    el.appendChild(c instanceof Node ? c : document.createTextNode(String(c)));
  }
}
function h(tag, attrs, ...kids) { const el = document.createElement(tag); applyAttrs(el, attrs, false); appendKids(el, kids); return el; }
function s(tag, attrs, ...kids) { const el = document.createElementNS(SVGNS, tag); applyAttrs(el, attrs, true); appendKids(el, kids); return el; }

/* ---------- Iconos (trazo 24×24) ---------- */
const ICONS = {
  heart: '<path d="M12 20.3s-7.6-4.6-9.3-9.4C1.6 7.8 3.7 4.6 7.1 4.6c2.1 0 3.7 1.1 4.9 2.8 1.2-1.7 2.8-2.8 4.9-2.8 3.4 0 5.5 3.2 4.3 6.3-1.6 4.8-9.2 9.4-9.2 9.4z"/>',
  coin: '<circle cx="12" cy="12" r="8.6"/><circle cx="12" cy="12" r="4.8"/>',
  hammer: '<path d="m13.4 5.4 4.2-1.8 2.8 2.8-1.8 4.2"/><path d="m11.3 7.5 5.2 5.2"/><path d="M14 10.2 4.6 19.6a1.6 1.6 0 0 1-2.2-2.2l9.4-9.4"/>',
  video: '<rect x="3" y="6" width="13" height="12" rx="2.5"/><path d="m16 10.5 5-3v9l-5-3"/>',
  shield: '<path d="M12 3.2 19 6v5.1c0 4.4-2.9 8.1-7 9.7-4.1-1.6-7-5.3-7-9.7V6z"/>',
  bag: '<path d="M5 8h14l-1.2 11.2a2 2 0 0 1-2 1.8H8.2a2 2 0 0 1-2-1.8z"/><path d="M9 8V6.5a3 3 0 0 1 6 0V8"/>',
  parts: '<path d="M12 2.5 3.8 7.2v9.6l8.2 4.7 8.2-4.7V7.2z"/><path d="M12 2.5v19" stroke-dasharray="2.4 2.2"/>',
  wand: '<path d="m3.5 20.5 11-11"/><path d="m14.5 9.5 2-2"/><path d="M18 2.5v3M16.5 4h3M20.5 9v2.4M19.3 10.2h2.4M11 3v2M10 4h2"/>',
  x: '<path d="M18 6 6 18M6 6l12 12"/>',
  check: '<path d="M20 6 9 17l-5-5"/>',
  undo: '<path d="M3 4v6h6"/><path d="M3.5 15a9 9 0 1 0 2.1-9.4L3 10"/>',
  restart: '<path d="M1 4v6h6M23 20v-6h-6"/><path d="M20.5 9A9 9 0 0 0 5.6 5.6L1 10m22 4-4.6 4.4A9 9 0 0 1 3.5 15"/>',
  help: '<circle cx="12" cy="12" r="10"/><path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3M12 17h.01"/>',
  chart: '<path d="M18 20V10M12 20V4M6 20v-6"/>',
  sliders: '<path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6"/>',
  bulb: '<path d="M9 18h6M10 22h4M12 2a7 7 0 0 0-4 12.7V16h8v-1.3A7 7 0 0 0 12 2z"/>',
  share: '<circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><path d="m8.6 13.5 6.8 4M15.4 6.5l-6.8 4"/>',
  copy: '<rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>',
  download: '<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"/>',
  play: '<path d="M6 3l14 9-14 9V3z"/>',
  calendar: '<rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/>',
  trash: '<path d="M3 6h18M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>',
  shuffle: '<path d="M16 3h5v5M4 20 21 3M21 16v5h-5M15 15l6 6M4 4l5 5"/>',
  zap: '<path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z"/>',
  eye: '<path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>',
  left: '<path d="m15 18-6-6 6-6"/>',
  right: '<path d="m9 18 6-6-6-6"/>',
  clock: '<circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>',
  volume: '<path d="M11 5 6 9H2v6h4l5 4V5z"/><path d="M19.1 4.9a10 10 0 0 1 0 14.2M15.5 8.5a5 5 0 0 1 0 7"/>',
  mute: '<path d="M11 5 6 9H2v6h4l5 4V5zM23 9l-6 6M17 9l6 6"/>',
  award: '<circle cx="12" cy="8" r="7"/><path d="M8.2 13.9 7 23l5-3 5 3-1.2-9.1"/>',
  search: '<circle cx="11" cy="11" r="8"/><path d="m21 21-4.4-4.4"/>',
  hex: '<path d="M21 16V8a2 2 0 0 0-1-1.7l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.7l7 4a2 2 0 0 0 2 0l7-4a2 2 0 0 0 1-1.7z"/>',
  link: '<path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7"/><path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7"/>',
  image: '<rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/>',
  edit: '<path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>',
  monitor: '<rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/>',
  pause: '<rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/>',
  user: '<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>',
  list: '<path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01"/>',
  flame: '<path d="M12 22c4.4 0 7-2.9 7-7 0-3.5-2.2-6.2-4-8 .2 2.3-1 4-2.5 4.5C13 8 11.5 4.5 8.5 2c.4 3.6-1.5 5.8-3 7.7A8.4 8.4 0 0 0 5 15c0 4.1 2.6 7 7 7z"/>',
  plus: '<path d="M12 5v14M5 12h14"/>',
  lock: '<rect x="4" y="11" width="16" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/>',
  map: '<path d="M1 6v16l7-4 8 4 7-4V2l-7 4-8-4-7 4zM8 2v16M16 6v16"/>',
  star: '<path d="m12 2 3.1 6.3 6.9 1-5 4.9 1.2 6.8L12 17.8 5.8 21l1.2-6.8-5-4.9 6.9-1z"/>',
  gift: '<rect x="3" y="8" width="18" height="4" rx="1"/><path d="M12 8v13M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7M7.5 8a2.5 2.5 0 0 1 0-5C11 3 12 8 12 8s1-5 4.5-5a2.5 2.5 0 0 1 0 5"/>',
  mail: '<rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 6-10 7L2 6"/>',
  cart: '<circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.7 13.4a2 2 0 0 0 2 1.6h9.7a2 2 0 0 0 2-1.6L23 6H6"/>',
  school: '<path d="M22 10 12 5 2 10l10 5 10-5z"/><path d="M6 12v5c3 2 9 2 12 0v-5"/>',
  phone: '<rect x="5" y="2" width="14" height="20" rx="2"/><path d="M12 18h.01"/>',
  // v4: familias de nivel (buscar / deducir), dado, menú Experimental y opiniones de los prototipos
  puzzle: '<path d="M4 7h4a2 2 0 1 1 4 0h4v4a2 2 0 1 1 0 4v4h-4a2 2 0 1 0-4 0H4v-4a2 2 0 1 0 0-4z"/>',
  dice: '<rect x="3.5" y="3.5" width="17" height="17" rx="4"/><path d="M8.5 8.5h.01M15.5 15.5h.01M12 12h.01M15.5 8.5h.01M8.5 15.5h.01" stroke-width="3.2"/>',
  flask: '<path d="M9 3h6M10 3v6.2L4.6 18.6A1.6 1.6 0 0 0 6 21h12a1.6 1.6 0 0 0 1.4-2.4L14 9.2V3"/><path d="M7.2 15h9.6"/>',
  thumb: '<path d="M7 10.5V20H3.5v-9.5zM7 10.5 11 3a2.2 2.2 0 0 1 3.3 1.9V9h5a2 2 0 0 1 2 2.3l-1.3 7A2 2 0 0 1 18 20H7"/>',
  note: '<path d="M4 4h16v12H9l-5 4z"/><path d="M8 9h8M8 12h5"/>',
  vibrate: '<rect x="7" y="4" width="10" height="16" rx="2"/><path d="M3 9v6M21 9v6"/>',
  snow: '<path d="M12 2.8v18.4M4 7.4l16 9.2M4 16.6l16-9.2"/><path d="m9.4 4.6 2.6 2 2.6-2M9.4 19.4l2.6-2 2.6 2M4.4 10.6l3.2-.6-1.1-3.1M19.6 13.4l-3.2.6 1.1 3.1M4.4 13.4l3.2.6-1.1 3.1M19.6 10.6l-3.2-.6 1.1-3.1"/>'
};
function icon(name, extra) {
  const el = document.createElementNS(SVGNS, 'svg');
  el.setAttribute('class', 'ico' + (extra ? ' ' + extra : ''));
  el.setAttribute('viewBox', '0 0 24 24');
  el.setAttribute('aria-hidden', 'true');
  el.innerHTML = ICONS[name] || '';
  return el;
}

/* trabajo que puede esperar a que el navegador esté libre (sin bloquear el arranque) */
function whenIdle(fn, timeout) {
  if (typeof requestIdleCallback === 'function') requestIdleCallback(() => fn(), { timeout: timeout || 2000 });
  else setTimeout(fn, 200);
}
function fmtTime(ms) {
  ms = Math.max(0, ms | 0);
  const t = Math.floor(ms / 1000), hh = Math.floor(t / 3600), mm = Math.floor((t % 3600) / 60), ss = t % 60;
  return hh ? `${hh}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}` : `${mm}:${String(ss).padStart(2, '0')}`;
}
/* cuenta atrás corta (vidas, relojes de los niveles): 1:05 · 1:02:03 */
const fmtMMSS = ms => {
  const t = Math.max(0, Math.ceil(ms / 1000)), hh = Math.floor(t / 3600), mm = Math.floor((t % 3600) / 60), ss = t % 60;
  return hh ? `${hh}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}` : `${mm}:${String(ss).padStart(2, '0')}`;
};
function fmtCountdown(ms) { const t = Math.max(0, Math.floor(ms / 1000)); return `${String(Math.floor(t / 3600)).padStart(2, '0')}:${String(Math.floor((t % 3600) / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`; }
function retoLabel(n) { return n >= 1 ? 'Reto #' + n : 'Reto de prueba'; }

/* ---------- Almacenamiento local (comodidades por dispositivo) ---------- */
const Store = (() => {
  let data = {};
  try { data = JSON.parse(localStorage.getItem(CONFIG.storeKey) || '{}') || {}; } catch (e) { data = {}; }
  let timer = 0, dirty = false;
  const flush = () => {
    clearTimeout(timer); timer = 0;
    if (!dirty) return;
    try { localStorage.setItem(CONFIG.storeKey, JSON.stringify(data)); dirty = false; } catch (e) { /* almacenamiento no disponible */ }
  };
  /* En el móvil los temporizadores se congelan al pasar a segundo plano y el sistema
     puede cerrar la app sin avisar: se guarda en el mismo instante en que la página
     deja de verse (y en pagehide / freeze, por si acaso). */
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') flush(); });
    document.addEventListener('freeze', flush);
    window.addEventListener('pagehide', flush);
  }
  return {
    get(k, def) { return data[k] === undefined ? def : data[k]; },
    set(k, v) { data[k] = v; dirty = true; clearTimeout(timer); timer = setTimeout(flush, 120); },
    /* para lo importante (estrellas, reto cerrado, ajustes): se escribe ya */
    setNow(k, v) { data[k] = v; dirty = true; flush(); },
    flush,
    reset() { data = {}; dirty = true; flush(); },
    raw: () => data
  };
})();
/* contadores para el informe técnico (sin datos personales), BYD-027: fichas añadidas
   arrastrando y cuántas de ellas puso el «puente» (gesto rápido que se salta una ficha) */
const Diag = {
  get() { return Object.assign({ dragTiles: 0, bridged: 0 }, Store.get('diag', {})); },
  add(k) { const d = this.get(); d[k] = (d[k] || 0) + 1; Store.set('diag', d); }
};
const Settings = {
  get() { return Object.assign({ sound: true, haptics: true, badges: true, relax: false, alias: '' }, Store.get('settings', {})); },
  set(patch) { Store.setNow('settings', Object.assign(this.get(), patch)); }
};

/* ---------- Sonido (sintetizado, sin archivos) ---------- */
const Sound = (() => {
  let ctx = null;
  const ensure = () => {
    if (!Settings.get().sound) return null;
    try {
      if (!ctx) { const AC = window.AudioContext || window.webkitAudioContext; if (!AC) return null; ctx = new AC(); }
      if (ctx.state === 'suspended') ctx.resume();
      return ctx;
    } catch (e) { return null; }
  };
  function knock(freq, vol, dur) {
    const c = ensure(); if (!c) return;
    const t = c.currentTime;
    const o = c.createOscillator(), g = c.createGain();
    o.type = 'triangle'; o.frequency.setValueAtTime(freq, t); o.frequency.exponentialRampToValueAtTime(freq * .55, t + dur);
    g.gain.setValueAtTime(vol, t); g.gain.exponentialRampToValueAtTime(.0001, t + dur);
    o.connect(g).connect(c.destination); o.start(t); o.stop(t + dur + .02);
    const len = Math.floor(c.sampleRate * .03), buf = c.createBuffer(1, len, c.sampleRate), ch = buf.getChannelData(0);
    for (let i = 0; i < len; i++) ch[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 3);
    const n = c.createBufferSource(), f = c.createBiquadFilter(), ng = c.createGain();
    n.buffer = buf; f.type = 'bandpass'; f.frequency.value = freq * 2.2; f.Q.value = 2.5; ng.gain.value = vol * .9;
    n.connect(f).connect(ng).connect(c.destination); n.start(t);
  }
  function tone(freq, start, dur, vol, type) {
    const c = ensure(); if (!c) return;
    const t = c.currentTime + start;
    const o = c.createOscillator(), g = c.createGain();
    o.type = type || 'sine'; o.frequency.setValueAtTime(freq, t);
    g.gain.setValueAtTime(.0001, t); g.gain.exponentialRampToValueAtTime(vol, t + .015); g.gain.exponentialRampToValueAtTime(.0001, t + dur);
    o.connect(g).connect(c.destination); o.start(t); o.stop(t + dur + .05);
  }
  const SCALE = [392, 440, 523.25, 587.33, 659.25, 783.99, 880, 1046.5];
  return {
    unlock: ensure,
    step(n) { knock(SCALE[Math.min(n, SCALE.length - 1)], .22, .09); },
    back() { knock(260, .16, .07); },
    good() { tone(659.25, 0, .18, .18); tone(987.77, .09, .3, .16); },
    found() { tone(783.99, 0, .14, .14, 'triangle'); tone(1174.66, .07, .22, .12, 'triangle'); },
    bad() { tone(196, 0, .22, .16, 'triangle'); tone(155.56, .08, .25, .12, 'triangle'); },
    remove() { knock(330, .18, .08); },
    star(k) { tone([783.99, 987.77, 1174.66][k % 3], 0, .35, .16, 'triangle'); },
    drop() { knock(180, .2, .12); },
    thud() { knock(120, .2, .06); },                                   // movimiento no permitido: «toc» sordo
    key() { knock(520, .12, .05); },                                   // teclado de la X
    win() { [523.25, 659.25, 783.99, 1046.5, 1318.5].forEach((f, i) => tone(f, i * .09, .5, .14, 'triangle')); }
  };
})();

/* ---------- Vibración ----------------------------------------------------
   Android: Vibration API con patrones. iPhone (Safari no tiene vibrate):
   pequeño "clic" háptico del interruptor nativo de iOS 18 (mejor esfuerzo).
------------------------------------------------------------------------- */
const Haptics = (() => {
  const nav = typeof navigator !== 'undefined' ? navigator : {};
  const canVibrate = typeof nav.vibrate === 'function';
  const isIOS = /iP(hone|ad|od)/.test(nav.userAgent || '') || (nav.platform === 'MacIntel' && nav.maxTouchPoints > 1);
  let label = null;
  function iosTick() {
    try {
      if (!label) {
        const id = 'bc-haptic-' + Math.random().toString(36).slice(2);
        const input = document.createElement('input');
        input.type = 'checkbox'; input.setAttribute('switch', ''); input.id = id; input.tabIndex = -1; input.setAttribute('aria-hidden', 'true');
        label = document.createElement('label'); label.htmlFor = id; label.setAttribute('aria-hidden', 'true');
        for (const el of [input, label]) Object.assign(el.style, { position: 'fixed', left: '-200px', top: '0', width: '1px', height: '1px', opacity: '0', pointerEvents: 'none' });
        document.body.append(input, label);
      }
      label.click();
    } catch (e) { /* sin háptica */ }
  }
  function play(pattern) {
    if (!Settings.get().haptics) return;
    if (canVibrate) { try { nav.vibrate(pattern); } catch (e) { /* bloqueado */ } return; }
    if (!isIOS) return;
    const arr = Array.isArray(pattern) ? pattern : [pattern];
    let t = 0;
    for (let i = 0; i < arr.length; i += 2) { if (t === 0) iosTick(); else setTimeout(iosTick, t); t += arr[i] + (arr[i + 1] || 0); }
  }
  return {
    supported: canVibrate || isIOS,
    tick(n) { play(Math.min(14 + (n || 0) * 4, 34)); },         // tu · tu · tu: cada ficha del trazo vibra un poco más
    back() { play(10); },
    ok() { play([18, 60, 30]); },
    place() { play([14, 50, 22]); },
    bad() { play([45, 40, 45, 40, 80]); },
    remove() { play(12); },
    block() { play(10); },                                             // movimiento no permitido
    key() { play(8); },
    star() { play(28); },
    win() { play([30, 70, 30, 70, 40, 90, 140]); },
    test() { play([20, 80, 20, 80, 60]); }
  };
})();

/* ---------- Capas flotantes (ventanas y avisos) ----------------------------
   Viven directamente en <body> con la clase .bc: así ningún contenedor del tema
   de la tienda (transform, overflow, z-index) puede recortarlas o taparlas.
------------------------------------------------------------------------- */
function layer(id) {
  let el = document.getElementById(id);
  if (!el) { el = document.createElement('div'); el.id = id; if (id === 'bc-toast-root') el.setAttribute('aria-live', 'polite'); }
  el.classList.add('bc', 'bc-layer');
  if (typeof TARGET !== 'undefined' && TARGET === 'app') el.classList.add('bc-app-layer');   // ventanas de la app: objetivos de 44 px
  if (el.parentNode !== document.body) document.body.appendChild(el);
  return el;
}

/* ---------- Avisos ---------- */
function toast(msg, opts) {
  opts = opts || {};
  const root = layer('bc-toast-root');
  const t = h('div', { class: 'toast' + (opts.type ? ' ' + opts.type : ''), role: 'status' }, msg);
  if (opts.action) t.appendChild(h('button', { onclick: () => { opts.action.fn(); close(); } }, opts.action.label));
  root.appendChild(t);
  while (root.children.length > 3) root.firstChild.remove();
  const close = () => { t.classList.add('out'); setTimeout(() => t.remove(), 260); };
  setTimeout(close, opts.ms || 2200);
  return close;
}

/* ---------- Ventanas ---------- */
let modalStack = [];
function openModal(opts) {
  const prevFocus = document.activeElement;
  const titleId = 'bc-m-' + Math.random().toString(36).slice(2);
  const closeBtn = h('button', { class: 'icon-btn', 'aria-label': 'Cerrar' }, icon('x'));
  const body = h('div', { class: 'modal-body' });
  const modal = h('div', { class: 'modal' + (opts.cls ? ' ' + opts.cls : ''), role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': titleId, tabindex: '-1' },
    h('header', null, h('h2', { id: titleId }, opts.title || ''), opts.noClose ? null : closeBtn), body);
  const back = h('div', { class: 'modal-backdrop' }, modal);
  const api = {
    el: modal, body,
    close() {
      if (!back.isConnected) return;
      back.remove(); modalStack = modalStack.filter(m => m !== api);
      document.removeEventListener('keydown', onKey);
      if (opts.onClose) opts.onClose();
      if (prevFocus && prevFocus.focus) try { prevFocus.focus({ preventScroll: true }); } catch (e) { /* noop */ }
    }
  };
  const onKey = e => { if (e.key === 'Escape' && !opts.noClose && modalStack[modalStack.length - 1] === api) api.close(); };
  closeBtn.addEventListener('click', api.close);
  back.addEventListener('pointerdown', e => { if (e.target === back && !opts.noClose) api.close(); });
  document.addEventListener('keydown', onKey);
  layer('bc-modal-root').appendChild(back);
  modalStack.push(api);
  if (opts.render) opts.render(body, api);
  setTimeout(() => { try { modal.focus({ preventScroll: true }); } catch (e) { /* noop */ } }, 30);
  return api;
}

function closeAllModals() { modalStack.slice().reverse().forEach(m => m.close()); }

/* ---------- Portapapeles ---------- */
async function copyText(text) {
  try { await navigator.clipboard.writeText(text); return true; }
  catch (e) {
    try {
      const ta = h('textarea', { style: { position: 'fixed', top: '-1000px', opacity: '0' } }); ta.value = text;
      document.body.appendChild(ta); ta.select(); const ok = document.execCommand('copy'); ta.remove(); return ok;
    } catch (e2) { return false; }
  }
}

/* ---------- Confeti con los colores de las fichas ---------- */
function confetti(x, y, n) {
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const cv = h('canvas', { class: 'bc-fx-canvas', style: { position: 'fixed', inset: '0', width: '100%', height: '100%', pointerEvents: 'none', zIndex: '9999' } });
  document.body.appendChild(cv);
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  cv.width = innerWidth * dpr; cv.height = innerHeight * dpr;
  const ctx = cv.getContext('2d'); ctx.scale(dpr, dpr);
  const cols = ['#F6A000', '#EC680E', '#41A7AD', '#FFECDD', '#D0AA8A'];
  const ps = Array.from({ length: n || 120 }, () => {
    const a = Math.random() * Math.PI * 2, v = 4 + Math.random() * 9;
    return { x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v - 6, r: 4 + Math.random() * 6, c: cols[(Math.random() * cols.length) | 0], rot: Math.random() * 6, vr: (Math.random() - .5) * .4, hex: Math.random() < .55 };
  });
  let frame = 0;
  (function loop() {
    frame++;
    ctx.clearRect(0, 0, innerWidth, innerHeight);
    for (const p of ps) {
      p.vy += .32; p.vx *= .985; p.x += p.vx; p.y += p.vy; p.rot += p.vr;
      ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.rot); ctx.fillStyle = p.c; ctx.globalAlpha = Math.max(0, 1 - frame / 110);
      if (p.hex) { ctx.beginPath(); for (let k = 0; k < 6; k++) { const a = k * Math.PI / 3; ctx.lineTo(Math.cos(a) * p.r, Math.sin(a) * p.r); } ctx.closePath(); ctx.fill(); }
      else ctx.fillRect(-p.r, -p.r / 3, p.r * 2, p.r * .7);
      ctx.restore();
    }
    if (frame < 115) requestAnimationFrame(loop); else cv.remove();
  })();
}

/* ---------- Varios ---------- */
const keyOf = path => E.canonKey(path);
const exprOf = (vals, path) => E.exprString(vals, path, E.opsOfPath(path));
function debounce(fn, ms) { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; }
function levelDots(level) { return h('span', { class: 'level-dots', 'aria-hidden': 'true' }, [1, 2, 3, 4].map(k => h('i', { class: k <= level ? 'on' : '' }))); }
function mixHex(a, b, t) {
  const p = x => [parseInt(x.slice(1, 3), 16), parseInt(x.slice(3, 5), 16), parseInt(x.slice(5, 7), 16)];
  const A = p(a), B = p(b);
  return '#' + A.map((v, i) => Math.round(v * t + B[i] * (1 - t)).toString(16).padStart(2, '0')).join('');
}
function emitEvent(name, detail) {
  // Para analítica de la tienda (Shopify/Meta/GA): window.addEventListener('byodo-city', …)
  try { window.dispatchEvent(new CustomEvent('byodo-city', { detail: Object.assign({ name }, detail || {}) })); } catch (e) { /* noop */ }
}

/* ---------- Analítica mínima y registro de errores ----------------------------
   Sin cookies ni identificadores: cada evento va solo con su nombre, sus datos de
   juego, la versión y el tamaño de pantalla. Solo se envía si hay un destino
   configurado (CONFIG.analyticsUrl: endpoint propio, Umami o Plausible); vacío = nada.
------------------------------------------------------------------------- */
const Analytics = (() => {
  const url = CONFIG.analyticsUrl || '';
  const kind = CONFIG.analyticsKind || (/plausible/.test(url) ? 'plausible' : /umami/.test(url) ? 'umami' : 'json');
  const ver = (typeof BC_VERSION !== 'undefined' ? BC_VERSION : 'dev');
  function send(name, props) {
    if (!url) return false;
    let body;
    if (kind === 'plausible') body = { name, url: location.origin + location.pathname, domain: CONFIG.analyticsSite || location.hostname, props };
    else if (kind === 'umami') body = { type: 'event', payload: { website: CONFIG.analyticsSite || '', name, data: props, url: location.pathname, hostname: location.hostname, language: 'es' } };
    else body = { event: name, props, v: ver, t: Date.now() };
    const json = JSON.stringify(body);
    try {
      // text/plain: no hace falta permiso CORS previo; sin credenciales: nada de cookies
      if (navigator.sendBeacon && kind === 'json' && navigator.sendBeacon(url, new Blob([json], { type: 'text/plain' }))) return true;
      fetch(url, { method: 'POST', body: json, keepalive: true, credentials: 'omit', mode: 'no-cors', headers: { 'Content-Type': 'text/plain' } }).catch(() => {});
      return true;
    } catch (e) { return false; }
  }
  const TRACK = new Set(['app_open', 'level_start', 'level_complete', 'level_fail', 'level_quit', 'daily_start', 'daily_solved', 'share', 'install', 'start', 'solved',
    'view', 'reward_shown', 'reward_click', 'product_click', 'newsletter_click', 'educators_click',
    // economía de la app (BYD-036): qué pasa, nunca quién (ni edad ni identificadores)
    'life_lost', 'out_of_lives', 'out_of_lives_shown', 'level_continue', 'booster_used', 'coins_earned', 'coins_spent',
    'purchase_start', 'purchase_success', 'purchase_cancel', 'purchase_error', 'purchase_restore',
    'ad_rewarded_start', 'ad_rewarded_complete', 'ad_rewarded_skip', 'ad_rewarded_error', 'ad_interstitial_show',
    // v4: derrota por tiempo o movimientos, «ya no se puede cerrar», repetidos de la caza y racha
    'level_dead', 'hunt_repeat', 'streak_milestone']);
  window.addEventListener('byodo-city', e => {
    if (!url) return;                                       // sin destino configurado: nada que hacer
    const d = Object.assign({}, e.detail || {}); const name = d.name; delete d.name;
    if (!name || !TRACK.has(name)) return;
    // fuera del camino crítico (leer innerWidth puede obligar a recalcular el diseño)
    setTimeout(() => { d.v = ver; d.w = Math.round(innerWidth); d.target = typeof TARGET !== 'undefined' ? TARGET : ''; send(name, d); }, 0);
  });
  // errores: los 20 últimos se guardan en este dispositivo (Ajustes → Datos los muestra)
  function logError(msg, where) {
    try {
      const list = Store.get('errors', []);
      list.push({ t: new Date().toISOString(), m: String(msg).slice(0, 300), w: String(where || '').slice(0, 200), v: ver });
      Store.set('errors', list.slice(-20));
      send('error', { m: String(msg).slice(0, 200) });
    } catch (e) { /* noop */ }
  }
  window.addEventListener('error', e => logError(e.message || 'error', (e.filename || '') + ':' + (e.lineno || '')));
  window.addEventListener('unhandledrejection', e => logError((e.reason && (e.reason.message || e.reason)) || 'promise', 'promise'));
  return { send, logError, enabled: !!url };
})();

/* ======================================================================
   art.js — dibujos pequeños que necesita el primer fotograma (mapa, bienvenida):
   mini-panal, Hexi y estrellas. Usan las fichas del sprite tal cual (no se tocan).
   ====================================================================== */
const OP_COLORS = ['#EC680E', '#F6A000', '#41A7AD'];   // +  −  =  (como las marcas de las fichas)
const OP_TEXT_COLORS = ['#fff', '#003D47', '#fff'];
const glyphRef = v => '#bc-g' + (v === E.X ? 'X' : v);
const reducedMotion = () => !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

/* estrella sin texto: el «★» lo pone el CSS (::before), así que ni textContent ni
   innerText la leen como carácter, y el contenedor va con aria-hidden o role="img" (BYD-043).
   Es texto y no SVG porque 30 SVG en el mapa encarecen el primer dibujado. */
function starIco(on) { return h('i', { class: 'star' + (on ? ' on' : '') }); }
/* ★★★ que se apagan al perder estrellas (regla visible antes de empezar) */
/* earned: en los niveles por puntos el medidor cuenta las estrellas que ya llevas (no las que aún puedes conseguir) */
function starMeter(n, label, earned) {
  return h('span', { class: 'chip-o stars-chip', role: 'img', title: label || '', 'aria-label': `${n} de 3 estrellas ${earned ? 'conseguidas' : 'posibles'}. ${label || ''}` },
    [1, 2, 3].map(k => starIco(k <= n)));
}

/* Mini-panal decorativo (ayuda e introducciones): fichas en posiciones libres */
function miniBoard(cells, opts) {
  opts = opts || {};
  const pts = cells.map(c => c.pos);
  const xs = pts.map(p => p[0]), ys = pts.map(p => p[1]);
  const pad = 62;
  const vb = [Math.min(...xs) - pad, Math.min(...ys) - pad + 6 - (opts.top || 0), Math.max(...xs) - Math.min(...xs) + pad * 2, Math.max(...ys) - Math.min(...ys) + pad * 2 + (opts.top || 0)];
  const svg = s('svg', { viewBox: vb.map(v => v.toFixed(1)).join(' '), 'aria-hidden': 'true', class: 'mini' + (opts.cls ? ' ' + opts.cls : '') });
  if (opts.glow && opts.path) {
    const u = 'm' + Math.random().toString(36).slice(2, 7);
    svg.appendChild(s('defs', null, s('radialGradient', { id: 'bc-mj-' + u }, s('stop', { offset: 0, 'stop-color': '#FFE08A' }), s('stop', { offset: .45, 'stop-color': '#FFB21A', 'stop-opacity': .95 }), s('stop', { offset: 1, 'stop-color': '#F6A000', 'stop-opacity': 0 }))));
    for (let k = 0; k + 1 < opts.path.length; k++) {
      const a = cells[opts.path[k]].pos, b = cells[opts.path[k + 1]].pos;
      svg.appendChild(s('circle', { cx: (a[0] + b[0]) / 2, cy: (a[1] + b[1]) / 2, r: 34, fill: `url(#bc-mj-${u})` }));
    }
  }
  for (const c of cells) svg.appendChild(s('use', { href: '#bc-tile-shape', transform: `translate(${c.pos[0]},${c.pos[1] + 6})`, fill: '#8F6B4E' }));
  for (const c of cells) {
    svg.appendChild(s('g', { transform: `translate(${c.pos[0]},${c.pos[1]})`, style: c.gold ? '--tb:#F6C94C' : c.hl ? '--tb:#FFE6C9' : '' },
      s('use', { href: '#bc-tile-shape', style: 'fill:var(--tb,#D0AA8A)' }), s('use', { href: '#bc-tile-marks' })));
  }
  if (opts.path && !opts.noTrail) svg.appendChild(s('polyline', { class: 'trail current', points: opts.path.map(k => cells[k].pos.join(',')).join(' ') }));
  for (const c of cells) svg.appendChild(s('use', { href: glyphRef(c.v), transform: `translate(${c.pos[0]},${c.pos[1]})`, style: c.v === E.X ? 'fill:#EC680E' : 'fill:#005461' }));
  if (opts.path && !opts.noBadges) for (let k = 0; k + 1 < opts.path.length; k++) {
    const a = cells[opts.path[k]].pos, b = cells[opts.path[k + 1]].pos, op = opts.ops[k];
    svg.appendChild(s('g', { class: 'opbadge', transform: `translate(${(a[0] + b[0]) / 2},${(a[1] + b[1]) / 2})` },
      s('circle', { r: 16, fill: OP_COLORS[op] }), s('text', { fill: OP_TEXT_COLORS[op] }, E.OP_CHAR[op])));
  }
  return svg;
}

/* ---------------------------------------------------------------------------
   Hexi, la mascota: es la ficha «1» real del sprite, sin tocarla, con unos ojos
   superpuestos (y brazos por detrás cuando celebra). mood: 'idle' | 'happy' | 'oops' | 'cheer'
--------------------------------------------------------------------------- */
function hexi(mood, cls) {
  mood = mood || 'idle';
  const svg = s('svg', { viewBox: '-80 -86 160 168', class: 'hexi hexi-' + mood + (cls ? ' ' + cls : ''), 'aria-hidden': 'true' });
  if (mood === 'cheer') {
    svg.append(
      s('path', { class: 'arm', d: 'M-44 4 C -66 -20, -70 -46, -62 -70' }), s('circle', { class: 'hand', cx: -62, cy: -72, r: 9 }),
      s('path', { class: 'arm', d: 'M44 4 C 66 -20, 70 -46, 62 -70' }), s('circle', { class: 'hand', cx: 62, cy: -72, r: 9 }));
  }
  svg.append(
    s('ellipse', { class: 'hexi-shadow', cx: 0, cy: 70, rx: 46, ry: 8 }),
    s('g', { class: 'hexi-body' },
      s('use', { href: '#bc-tile-shape', transform: 'translate(0,7)', fill: '#8F6B4E' }),
      s('use', { href: '#bc-tile-shape', style: 'fill:#D0AA8A' }),
      s('use', { href: '#bc-tile-marks' }),
      s('use', { href: '#bc-g1', style: 'fill:#005461' }),
      s('g', { class: 'eyes' },
        ...[-17, 17].map(x => s('g', { transform: `translate(${x},-19)` },
          mood === 'happy' || mood === 'cheer'
            ? [s('circle', { class: 'eye-w', r: 11 }), s('path', { class: 'eye-happy', d: 'M-6 3 Q 0 -5, 6 3' })]
            : [s('circle', { class: 'eye-w', r: 11 }), s('circle', { class: 'eye-p', cx: mood === 'oops' ? -2 : 1.5, cy: mood === 'oops' ? 2 : 1, r: 5.4 }), s('circle', { class: 'eye-l', cx: 3.5, cy: -2.5, r: 1.8 })])))));
  return svg;
}

/* ======================================================================
   board.js — el BYŌDŌPANAL en SVG: dibujo, estados, torres y gesto de trazar
   Regla de oro: las fichas (sprite #bc-tile-shape, #bc-tile-marks, #bc-g*) no
   se redibujan ni se recolorean. Todo lo que ayuda a leer el panal (brillos,
   halos, sombras, etiquetas) va por debajo o por encima, fuera de la ficha.
   ====================================================================== */
const CENTERS = Array.from({ length: E.N }, (_, i) => E.center(i));
const HEX_R = E.RAD;
const LIFT = 10;                                         // altura de cada piso en el modo Ciudad
const DEPTH_ORDER = Array.from({ length: E.N }, (_, i) => i).sort((a, b) => CENTERS[a][1] - CENTERS[b][1] || CENTERS[a][0] - CENTERS[b][0]);
const FULL_VIEWBOX = '-250 -268 500 540';
/* estados que se marcan con luz por debajo de la ficha (nunca con un contorno encima: decisión de Gabriel) */
const MARK_KINDS = ['is-hint', 'is-dead', 'is-picked', 'is-sel', 'is-end', 'is-target', 'is-xq'];

function roundedHexPath(R, r) {
  const pts = Array.from({ length: 6 }, (_, k) => [R * Math.cos(k * Math.PI / 3), R * Math.sin(k * Math.PI / 3)]);
  let d = '';
  for (let k = 0; k < 6; k++) {
    const p = pts[k], a = pts[(k + 5) % 6], b = pts[(k + 1) % 6];
    const la = Math.hypot(a[0] - p[0], a[1] - p[1]), lb = Math.hypot(b[0] - p[0], b[1] - p[1]);
    const p1 = [p[0] + (a[0] - p[0]) * r / la, p[1] + (a[1] - p[1]) * r / la];
    const p2 = [p[0] + (b[0] - p[0]) * r / lb, p[1] + (b[1] - p[1]) * r / lb];
    d += (k ? 'L' : 'M') + p1[0].toFixed(2) + ' ' + p1[1].toFixed(2) + 'Q' + p[0].toFixed(2) + ' ' + p[1].toFixed(2) + ' ' + p2[0].toFixed(2) + ' ' + p2[1].toFixed(2);
  }
  return d + 'Z';
}
const RING_D = roundedHexPath(HEX_R * .93, 9);
const PLOT_D = roundedHexPath(HEX_R * .94, 12);          // «solares»: casillas tapadas de un panal por partes (interfaz, no fichas)
/* tono más profundo del color del grupo para que el trazo destaque sobre la ficha teñida */
function trailColor(c) { return mixHex(c, '#18282C', .74); }
function distToSeg(px, py, ax, ay, bx, by) {
  const dx = bx - ax, dy = by - ay, L2 = dx * dx + dy * dy;
  const t = L2 ? Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / L2)) : 0;
  return Math.hypot(px - (ax + dx * t), py - (ay + dy * t));
}

class BoardView {
  constructor(opts) {
    this.o = Object.assign({ interactive: true, editable: false, maxLen: 5, stacks: false, label: 'Panal de fichas' }, opts || {});
    this.vals = new Array(E.N).fill(-1);
    this.mask = E.FULL;
    this.heights = new Array(E.N).fill(1);
    this.path = [];
    this.pathMode = null;                                  // 'tap' (tocando una a una) | 'drag' (arrastrando)
    this.blocked = () => false;
    this.cursor = -1;
    this.selected = -1;
    this.timers = new Set();
    this.build();
  }
  later(fn, ms) { const t = setTimeout(() => { this.timers.delete(t); fn(); }, ms); this.timers.add(t); return t; }

  build() {
    const uid = BoardView.uid = (BoardView.uid || 0) + 1;
    this.uid = uid;
    const svg = s('svg', { class: 'board' + (this.o.stacks ? ' is-stacks' : ''), viewBox: FULL_VIEWBOX, role: 'img', 'aria-label': this.o.label });
    const defs = s('defs');
    // brillos (degradados, sin filtros): halo de las fichas del trazo y luz en las juntas
    const grad = (id, stops) => s('radialGradient', { id }, stops.map(([o, c, a]) => s('stop', { offset: o, 'stop-color': c, 'stop-opacity': a })));
    defs.append(
      grad('bc-halo-' + uid, [[0, '#FFB21A', .9], [.62, '#F6A000', .55], [1, '#F6A000', 0]]),
      grad('bc-joint-' + uid, [[0, '#FFE08A', 1], [.45, '#FFB21A', .95], [1, '#F6A000', 0]]),
      grad('bc-jointko-' + uid, [[0, '#E6EEF0', .95], [.5, '#9DB3B8', .7], [1, '#9DB3B8', 0]]),
      grad('bc-hintglow-' + uid, [[0, '#FFFFFF', .75], [.6, '#FFF3DA', .4], [1, '#FFF3DA', 0]]),
      // marcas bajo la ficha: selección de teclado, operación elegida, pista, operación que no encaja, extremo del trazo
      grad('bc-mk-sel-' + uid, [[0, '#FFD54A', 1], [.55, '#FFB21A', .8], [1, '#F6A000', 0]]),
      grad('bc-mk-picked-' + uid, [[0, '#FFFFFF', 1], [.55, '#FFF3DA', .75], [1, '#FFF3DA', 0]]),
      grad('bc-mk-hint-' + uid, [[0, '#FFFFFF', .9], [.6, '#FFF3DA', .55], [1, '#FFF3DA', 0]]),
      grad('bc-mk-dead-' + uid, [[0, '#FF6B55', 1], [.55, '#E8432B', .75], [1, '#E8432B', 0]]),
      grad('bc-mk-end-' + uid, [[0, '#FFC23A', 1], [.55, '#FF9A1A', .8], [1, '#F6A000', 0]]),
      // fichas que el martillo puede quitar (BYD-036)
      grad('bc-mk-target-' + uid, [[0, '#E9FFFB', 1], [.55, '#7FD8D6', .8], [1, '#41A7AD', 0]]),
      // la X del trazo mientras se dice cuánto vale (BYD-046)
      grad('bc-mk-xq-' + uid, [[0, '#FFB000', 1], [.55, '#F07800', .85], [1, '#E86A00', 0]]));
    svg.appendChild(defs);
    this.lGlow = s('g', { class: 'glow-layer' });
    this.lMarks = s('g', { class: 'mark-layer' });
    this.lPlots = s('g', { class: 'plot-layer' });
    this.lBase = s('g');
    this.lEdges = s('g');
    this.lTiles = s('g');
    this.lTrails = s('g');
    this.lGlyphs = s('g');
    this.lBadges = s('g');
    this.lFx = s('g', { class: 'fx-layer' });
    this.lBase.append(this.lEdges, this.lTiles);
    svg.append(this.lGlow, this.lMarks, this.lPlots, this.lBase, this.lTrails, this.lGlyphs, this.lBadges, this.lFx);
    this.tiles = []; this.glyphs = []; this.edges = []; this.lifts = []; this.slices = [];
    for (let i = 0; i < E.N; i++) {
      const [x, y] = CENTERS[i];
      const tr = `translate(${x.toFixed(2)},${y.toFixed(2)})`;
      const ring = s('path', { class: 'ring', d: RING_D });
      if (this.o.stacks) {
        const slices = s('g', { class: 'slices' });
        const gu = s('use', { href: '#bc-g0', class: 'gl' });
        const ti = s('g', { class: 'ti' }, s('use', { href: '#bc-tile-shape', class: 'body-use' }), s('use', { href: '#bc-tile-marks' }), s('g', { class: 'glyph-in' }, gu), ring);
        const lift = s('g', { class: 'lift' }, slices, ti);
        const g = s('g', { class: 'tile', transform: tr, 'data-i': i }, lift);
        this.tiles[i] = g; this.lifts[i] = lift; this.slices[i] = slices;
        this.glyphs[i] = { g: null, use: gu };
      } else {
        const edge = s('use', { href: '#bc-tile-shape', transform: `translate(${x.toFixed(2)},${(y + 7).toFixed(2)})`, class: 'edge-use' });
        this.lEdges.appendChild(edge); this.edges[i] = edge;
        const ti = s('g', { class: 'ti' }, s('use', { href: '#bc-tile-shape', class: 'body-use' }), s('use', { href: '#bc-tile-marks' }), ring);
        const g = s('g', { class: 'tile', transform: tr, 'data-i': i }, ti);
        this.lTiles.appendChild(g); this.tiles[i] = g;
        const gu = s('use', { href: '#bc-g0', class: 'gl' });
        const gg = s('g', { class: 'glyph', transform: tr }, s('g', { class: 'ti' }, gu));
        this.lGlyphs.appendChild(gg); this.glyphs[i] = { g: gg, use: gu };
      }
    }
    if (this.o.stacks) for (const i of DEPTH_ORDER) this.lTiles.appendChild(this.tiles[i]);
    this.svg = svg;
    // sombra difusa precalculada: un SVG aparte, estático, que no se repinta al trazar
    this.shadowSvg = s('svg', { class: 'board-shadow', viewBox: FULL_VIEWBOX, 'aria-hidden': 'true', focusable: 'false' },
      s('defs', null, s('filter', { id: 'bc-bsh-' + uid, x: '-30%', y: '-30%', width: '160%', height: '170%' }, s('feGaussianBlur', { stdDeviation: 9 }))),
      this.lShadow = s('g', { filter: `url(#bc-bsh-${uid})`, fill: '#001a1f', 'fill-opacity': .45 }));
    this.el = h('div', { class: 'board-wrap', tabindex: this.o.interactive ? '0' : null, role: this.o.interactive ? 'application' : null, 'aria-label': this.o.interactive ? this.o.label + '. Arrastra o toca fichas vecinas para formar una operación.' : null }, this.shadowSvg, svg);
    this.updateShadow();
    if (this.o.interactive) this.bindPointer();
  }
  destroy() {
    this.stopGhost(); this.cancelStale();
    for (const t of this.timers) clearTimeout(t);
    this.timers.clear();
    this.dead = true;
  }

  /* ---------------- sombra ---------------- */
  updateShadow() {
    const key = this.mask + '|' + this.vals.map(v => (v < 0 ? 0 : 1)).join('');
    if (key === this._shadowKey) return;
    this._shadowKey = key;
    this.lShadow.innerHTML = '';
    for (let i = 0; i < E.N; i++) {
      if (!this.inShape(i) || (this.vals[i] < 0 && !this.o.editable)) continue;
      const [x, y] = CENTERS[i];
      this.lShadow.appendChild(s('use', { href: '#bc-tile-shape', transform: `translate(${x.toFixed(1)},${(y + 13).toFixed(1)})` }));
    }
  }

  /* ---------------- datos y estados ---------------- */
  setVals(vals) {
    this.vals = vals.slice();
    for (let i = 0; i < E.N; i++) {
      const v = vals[i];
      this.glyphs[i].use.setAttribute('href', v >= 0 ? glyphRef(v) : '#bc-g0');
      if (this.glyphs[i].g) this.glyphs[i].g.style.display = v >= 0 && this.inShape(i) ? '' : 'none';
      else this.glyphs[i].use.style.display = v >= 0 ? '' : 'none';
      this.cls(i, 'is-x', v === E.X);
      this.tiles[i].classList.toggle('is-empty', v < 0);
      this.tiles[i].setAttribute('data-label', E.CELLS[i].id + ': ' + (v >= 0 ? E.sym(v) : 'vacía'));
    }
    this.updateShadow();
  }
  inShape(i) { return (this.mask >>> i & 1) === 1; }
  /* Forma del panal (niveles): oculta las celdas que no forman parte y encuadra.
     frame: celdas que fijan el encuadre (si se quita una ficha con el martillo, el panal no se mueve) */
  setShape(mask, frame) {
    this.mask = mask == null ? E.FULL : mask;
    const fm = frame == null ? this.mask : frame;
    const xs = [], ys = [];
    for (let i = 0; i < E.N; i++) {
      const on = this.inShape(i);
      this.tiles[i].style.display = on ? '' : 'none';
      if (this.edges[i]) this.edges[i].style.display = on ? '' : 'none';
      if (this.glyphs[i].g) this.glyphs[i].g.style.display = on && this.vals[i] >= 0 ? '' : 'none';
      if (fm >>> i & 1) { xs.push(CENTERS[i][0]); ys.push(CENTERS[i][1]); }
    }
    let vb = FULL_VIEWBOX;
    if (fm !== E.FULL && xs.length) {
      const pad = 60, x0 = Math.min(...xs) - pad, x1 = Math.max(...xs) + pad, y0 = Math.min(...ys) - pad - 8, y1 = Math.max(...ys) + pad + 12;
      // mantenemos la proporción del contenedor (500×540)
      let w = x1 - x0, hh = y1 - y0; const ar = 500 / 540;
      if (w / hh > ar) hh = w / ar; else w = hh * ar;
      const cx = (x0 + x1) / 2, cy = (y0 + y1) / 2;
      vb = `${(cx - w / 2).toFixed(1)} ${(cy - hh / 2).toFixed(1)} ${w.toFixed(1)} ${hh.toFixed(1)}`;
    }
    this.svg.setAttribute('viewBox', vb);
    this.shadowSvg.setAttribute('viewBox', vb);
    this.updateShadow();
  }
  setGold(cells) { const on = new Set(cells || []); for (let i = 0; i < E.N; i++) this.cls(i, 'is-gold', on.has(i)); }
  /* BYD-033: partes del panal que aún no se han abierto. En su sitio no hay fichas: se dibuja un
     «solar» (hexágono de interfaz punteado) con el número de la parte. parts = [{ k, cells }] */
  setPlots(parts) {
    const L = this.lPlots; L.innerHTML = '';
    this.covered = new Set();
    for (const p of parts || []) {
      const g = s('g', { class: 'plot', 'data-part': p.k + 1, 'aria-hidden': 'true' });
      let sx = 0, sy = 0;
      for (const c of p.cells) {
        const [x, y] = CENTERS[c]; sx += x; sy += y; this.covered.add(c);
        g.appendChild(s('path', { class: 'plot-hex', d: PLOT_D, transform: `translate(${x.toFixed(1)},${y.toFixed(1)})` }));
      }
      // la etiqueta, en la casilla de la parte más cercana a su centro (siempre dentro de la parte)
      const cx = sx / p.cells.length, cy = sy / p.cells.length;
      const at = p.cells.reduce((b, c) => (Math.hypot(CENTERS[c][0] - cx, CENTERS[c][1] - cy) < Math.hypot(CENTERS[b][0] - cx, CENTERS[b][1] - cy) ? c : b), p.cells[0]);
      g.appendChild(s('g', { class: 'plot-badge', transform: `translate(${CENTERS[at][0].toFixed(1)},${CENTERS[at][1].toFixed(1)})` },
        s('circle', { r: 30 }), s('text', null, String(p.k + 1))));
      L.appendChild(g);
    }
  }
  /* casillas que se pueden elegir (martillo): una chapa con el martillo junto a cada torre, encima del canto (no de la ficha) */
  setPickBadges(cells) {
    this.lBadges.querySelectorAll('.pick-badge').forEach(n => n.remove());
    for (const c of cells || []) {
      const [x, y] = this.pt(c);
      const g = s('g', { class: 'pick-badge', transform: `translate(${(x + 38).toFixed(1)},${(y - 30).toFixed(1)})`, 'aria-hidden': 'true' },
        s('circle', { r: 19 }), s('g', { transform: 'translate(-12,-12)' }));
      g.lastChild.innerHTML = ICONS.hammer;
      this.lBadges.appendChild(g);
    }
  }
  /* la casilla más cercana aunque no esté en la forma (para avisar al tocar un solar) */
  anyCellAt(x, y) {
    let best = -1, bd = HEX_R * HEX_R;
    for (let i = 0; i < E.N; i++) { const [cx, cy] = CENTERS[i], d = (x - cx) ** 2 + (y - cy) ** 2; if (d < bd) { bd = d; best = i; } }
    return best;
  }
  cls(i, name, on) { this.tiles[i].classList.toggle(name, on); if (this.glyphs[i].g) this.glyphs[i].g.classList.toggle(name, on); }
  raise(i) { if (!this.o.stacks) this.lTiles.appendChild(this.tiles[i]); }

  /* Torres (modo Ciudad): stacks[i] = [{v, g}, …] de abajo arriba.
     Las fichas de debajo se ven como cantos; la altura se indica con un número
     junto a la torre (fuera de la ficha). Solo cuenta la ficha de arriba. */
  setStacks(stacks, dropped) {
    this.lBadges.querySelectorAll('.hbadge').forEach(n => n.remove());
    for (let i = 0; i < E.N; i++) {
      const st = stacks[i] || [];
      const hgt = Math.max(1, st.length);
      this.heights[i] = hgt;
      if (!this.o.stacks) continue;
      const lift = this.lifts[i];
      const L = (hgt - 1) * LIFT;
      const sl = this.slices[i];
      sl.innerHTML = '';
      for (let k = 0; k < hgt; k++) {
        const t = st[k] || {};
        const col = t.g ? (k % 2 ? '#C28A22' : '#B07A16') : (k % 2 ? '#A07A5C' : '#8F6B4E');
        sl.appendChild(s('use', { href: '#bc-tile-shape', transform: `translate(0,${(7 + (hgt - 1 - k) * LIFT).toFixed(1)})`, fill: col }));
      }
      // capa oscura sobre los pisos de debajo (no toca la ficha de arriba)
      if (hgt > 1) sl.appendChild(s('use', { href: '#bc-tile-shape', class: 'under-shade', transform: `translate(0,${(7 + LIFT).toFixed(1)})` }));
      if (dropped && dropped.includes(i) && !reducedMotion()) {
        lift.style.transition = 'none';
        lift.style.transform = `translateY(${-(L + 46)}px)`;
        lift.style.opacity = '0.2';
        void lift.getBoundingClientRect();
        lift.style.transition = 'transform .38s cubic-bezier(.3,1.45,.55,1), opacity .2s';
        lift.style.opacity = '1';
      }
      lift.style.transform = `translateY(${-L}px)`;
      const top = st[st.length - 1];
      this.cls(i, 'is-gold', !!(top && top.g));
      this.tiles[i].dataset.h = hgt;
      if (hgt > 1 && this.inShape(i)) {
        const [x, y] = CENTERS[i];
        this.lBadges.appendChild(s('g', { class: 'hbadge', transform: `translate(${(x - 36).toFixed(1)},${(y + 34).toFixed(1)})`, 'aria-hidden': 'true' },
          s('rect', { x: -15, y: -13, width: 30, height: 26, rx: 9 }), s('text', null, String(hgt))));
      }
    }
  }

  setGroups(groups, animateId) {
    this.groups = groups || [];
    const owner = new Array(E.N).fill(null);
    for (const g of this.groups) for (const c of g.path) owner[c] = g;
    for (let i = 0; i < E.N; i++) {
      const g = owner[i];
      this.cls(i, 'is-placed', !!g);
      if (g) this.tiles[i].style.setProperty('--tb', mixHex(g.color, '#E8CBAD', .5)); else this.tiles[i].style.removeProperty('--tb');
    }
    this.lTrails.querySelectorAll('.group').forEach(n => n.remove());
    for (const g of this.groups) {
      const pts = g.path.map(c => this.pt(c));
      const line = s('polyline', { class: 'trail group', points: pts.map(p => p[0].toFixed(1) + ',' + p[1].toFixed(1)).join(' '), stroke: trailColor(g.color) });
      this.lTrails.insertBefore(line, this.lTrails.firstChild);
      if (g.id === animateId) {
        let len = 0; for (let k = 1; k < pts.length; k++) len += Math.hypot(pts[k][0] - pts[k - 1][0], pts[k][1] - pts[k - 1][1]);
        line.style.setProperty('--len', len.toFixed(0)); line.classList.add('draw');
      }
    }
    this.setXValues(this.groups.filter(g => g.xv != null).map(g => [g.path.find(c => this.vals[c] === E.X), g.xv]));
  }
  /* Ciudad (BYD-044): mientras se traza, cada casilla del trazo enseña, en pequeño y
     encima, la ficha de la bolsa que caerá sobre ella. Son fichas del sprite a escala
     reducida, sin cambiarlas; la ficha del tablero no se toca. items = [[celda, {v, g}], …] */
  setIncoming(items) {
    this.lBadges.querySelectorAll('.incoming').forEach(n => n.remove());
    (items || []).forEach(([c, t], k) => {
      if (c == null || !t) return;
      const [x, y] = this.pt(c);
      const g = s('g', { class: 'incoming', transform: `translate(${(x + 32).toFixed(1)},${(y - 42).toFixed(1)}) scale(.46)`, 'data-c': c, 'data-v': t.v, 'aria-hidden': 'true' },
        s('use', { href: '#bc-tile-shape', transform: 'translate(0,7)', fill: '#8F6B4E' }),
        s('use', { href: '#bc-tile-shape', style: 'fill:' + (t.g ? '#F6C94C' : '#D0AA8A') }), s('use', { href: '#bc-tile-marks' }),
        s('use', { href: glyphRef(t.v), style: t.v === E.X ? 'fill:#EC680E' : 'fill:#005461' }),
        s('circle', { class: 'inc-order', cx: -52, cy: -50, r: 20 }), s('text', { class: 'inc-order-t', x: -52, y: -50 }, String(k + 1)));
      this.lBadges.appendChild(g);
    });
  }
  /* valor de cada X ya resuelta: etiqueta junto a la ficha (nunca dentro) */
  setXValues(pairs) {
    this.lBadges.querySelectorAll('.xval').forEach(n => n.remove());
    for (const [c, v] of pairs || []) {
      if (c == null || c < 0) continue;
      const [x, y] = this.pt(c);
      this.lBadges.appendChild(s('g', { class: 'xval', transform: `translate(${(x + 34).toFixed(1)},${(y - 34).toFixed(1)})` },
        s('circle', { r: 15 }), s('text', null, String(v))));
    }
  }
  /* centro visible (en torres, la ficha de arriba está levantada) */
  pt(c) { const [x, y] = CENTERS[c]; return [x, y - (this.o.stacks ? (this.heights[c] - 1) * LIFT : 0)]; }

  /* state: null (trazando) · 'ok' · 'ko' · 'dup' (cuenta ya encontrada: juntas en gris, BYD-047)
     · 'xpend' (pendiente de decir cuánto vale la X, BYD-046). Mientras se traza, o.pathState(path)
     puede marcar el trazo como repetido antes de soltar. */
  setPath(path, state) {
    this.path = path.slice();
    if (state == null && this.o.pathState && path.length) state = this.o.pathState(path) || null;
    this.pathState = state;
    const inPath = new Set(path);
    const pending = !state && path.length > 0;
    for (let i = 0; i < E.N; i++) { this.cls(i, 'is-path', inPath.has(i)); this.cls(i, 'is-end', pending && i === path[path.length - 1]); }
    for (const c of path) this.raise(c);
    this.drawTrail('current', path, state);
    this.drawBadges(path);
    this.drawGlow(path, state);
    this.drawPendingX(state === 'xpend' ? path : null);
    this.drawMarks();
  }
  /* «pendiente de X» (BYD-046): el trazo se queda en naranja discontinuo y la X brilla por debajo,
     con latido, mientras se teclea su valor. Nada encima de la ficha ni del número. */
  drawPendingX(path) {
    const c = path ? path.find(i => this.vals[i] === E.X) : -1;
    for (let i = 0; i < E.N; i++) this.cls(i, 'is-xq', i === c);
  }
  /* luz bajo las fichas marcadas (selección, pista, extremo del trazo…): la ficha solo cambia de color y se eleva */
  drawMarks() {
    const L = this.lMarks; if (!L) return;
    L.innerHTML = '';
    for (const kind of MARK_KINDS) for (let i = 0; i < E.N; i++) {
      if (!this.inShape(i) || !this.tiles[i].classList.contains(kind)) continue;
      const [x, y] = this.pt(i);
      L.appendChild(s('circle', { class: 'mk ' + kind, cx: x.toFixed(1), cy: (y + 3).toFixed(1), r: 80, fill: `url(#bc-mk-${kind.slice(3)}-${this.uid})` }));
    }
  }
  /* luz bajo las fichas: halo alrededor del trazo y brillo en cada junta, que
     asoma por el hueco del operador. La ficha no cambia. */
  drawGlow(path, state) {
    const L = this.lGlow; L.innerHTML = '';
    if (!path.length) return;
    const u = this.uid, grey = state === 'ko' || state === 'dup';
    L.setAttribute('class', 'glow-layer' + (state === 'ko' ? ' is-ko' : state === 'ok' ? ' is-ok' : state === 'dup' ? ' is-dup' : state === 'xpend' ? ' is-xpend' : ''));
    // repetida: la ficha final no se ilumina (BYD-047)
    path.forEach((c, k) => { if (state === 'dup' && k === path.length - 1) return; const [x, y] = this.pt(c); L.appendChild(s('circle', { class: 'halo', cx: x.toFixed(1), cy: (y + 3).toFixed(1), r: 74, fill: `url(#bc-halo-${u})` })); });
    for (let k = 0; k + 1 < path.length; k++) {
      const a = this.pt(path[k]), b = this.pt(path[k + 1]);
      L.appendChild(s('circle', { class: 'joint', cx: ((a[0] + b[0]) / 2).toFixed(1), cy: ((a[1] + b[1]) / 2).toFixed(1), r: 34, fill: `url(#bc-${grey ? 'jointko' : 'joint'}-${u})` }));
    }
  }
  drawTrail(kind, path, state) {
    let line = this.lTrails.querySelector('.trail.' + kind);
    if (path.length < 2) { if (line) line.remove(); return; }
    if (!line) { line = s('polyline', { class: 'trail ' + kind }); this.lTrails.appendChild(line); }
    line.setAttribute('points', path.map(c => { const p = this.pt(c); return p[0].toFixed(1) + ',' + p[1].toFixed(1); }).join(' '));
    line.classList.toggle('is-ok', state === 'ok'); line.classList.toggle('is-ko', state === 'ko');
    line.classList.toggle('is-dup', state === 'dup'); line.classList.toggle('is-xpend', state === 'xpend');
  }
  /* rectángulo en pantalla de unas casillas (para no tapar el trazo con el teclado de la X) */
  screenRect(cells) {
    const pts = (cells || []).map(c => this.screenPoint(c)); if (!pts.length) return null;
    const m = this.svg.getScreenCTM(), r = m ? HEX_R * Math.abs(m.a) : 30;
    return { left: Math.min(...pts.map(p => p[0])) - r, right: Math.max(...pts.map(p => p[0])) + r, top: Math.min(...pts.map(p => p[1])) - r, bottom: Math.max(...pts.map(p => p[1])) + r };
  }
  drawBadges(path, force) {
    this.lBadges.querySelectorAll('.opbadge').forEach(n => n.remove());
    if (!Settings.get().badges && !force) return;
    for (let k = 0; k + 1 < path.length; k++) {
      const a = this.pt(path[k]), b = this.pt(path[k + 1]), op = E.opBetween[path[k]][path[k + 1]];
      if (op < 0) continue;
      this.lBadges.appendChild(s('g', { class: 'opbadge', transform: `translate(${((a[0] + b[0]) / 2).toFixed(1)},${((a[1] + b[1]) / 2).toFixed(1)})` },
        s('circle', { r: 16, fill: OP_COLORS[op] }),
        s('text', { fill: OP_TEXT_COLORS[op], y: op === 1 ? -1 : 0 }, E.OP_CHAR[op])));
    }
  }
  setHighlight(path) {
    this.drawTrail('current', path || [], null);
    this.lBadges.querySelectorAll('.opbadge').forEach(n => n.remove());
    if (path && path.length) { this.drawBadges(path, true); for (const c of path) this.raise(c); }
    const on = new Set(path || []);
    for (let i = 0; i < E.N; i++) this.cls(i, 'is-path', on.has(i));
  }
  setDim(cells) { const on = cells ? new Set(cells) : null; for (let i = 0; i < E.N; i++) this.cls(i, 'is-dim', !!on && !on.has(i)); }
  setRing(cells, kind) { const on = new Set(cells || []); for (let i = 0; i < E.N; i++) this.cls(i, kind, on.has(i)); this.drawMarks(); }
  setSelected(i) { if (this.selected >= 0) this.cls(this.selected, 'is-sel', false); this.selected = i; if (i >= 0) this.cls(i, 'is-sel', true); this.drawMarks(); }
  clearBadges(kind) { this.lBadges.querySelectorAll(kind ? '.' + kind : '.heat-badge').forEach(n => n.remove()); }
  setHeat(counts) {
    this.clearBadges('heat-badge');
    if (!counts) return;
    counts.forEach((n, i) => {
      if (this.vals[i] < 0) return;
      const [x, y] = CENTERS[i];
      this.lBadges.appendChild(s('g', { class: 'heat-badge', transform: `translate(${(x + 30).toFixed(1)},${(y + 26).toFixed(1)})` },
        s('circle', { r: 15, style: n === 0 ? 'fill:#FF7A66;stroke:#FF7A66' : '' }), s('text', null, n > 999 ? '1k+' : String(n))));
    });
  }
  setResultMarks(cells) { this.setRing(cells, 'is-hint'); }
  pop(cells, delay) {
    (cells || []).forEach((c, k) => this.later(() => {
      this.cls(c, 'is-pop', false); void this.tiles[c].getBoundingClientRect(); this.cls(c, 'is-pop', true);
      this.later(() => this.cls(c, 'is-pop', false), 450);
    }, (delay || 0) * k));
  }
  /* igualdad incorrecta: sacudida de 6 px y juntas en gris (error amable) */
  shake(cells) {
    for (const c of cells || []) { this.cls(c, 'is-err', true); this.later(() => this.cls(c, 'is-err', false), 320); }
  }
  /* movimiento no permitido: la ficha rechazada da un respingo (150 ms) */
  nudge(c) {
    if (c == null || c < 0) return;
    this.cls(c, 'is-nudge', false); void this.tiles[c].getBoundingClientRect(); this.cls(c, 'is-nudge', true);
    this.later(() => this.cls(c, 'is-nudge', false), 170);
  }
  /* martillo: la ficha entera se encoge y se va (no se redibuja); después la oculta setShape */
  smash(c, done) {
    if (reducedMotion()) { done(); return; }
    this.cls(c, 'is-smash', true);
    if (this.edges[c]) this.edges[c].classList.add('is-smash');
    this.later(() => { this.cls(c, 'is-smash', false); if (this.edges[c]) this.edges[c].classList.remove('is-smash'); done(); }, 380);
  }
  /* etiqueta que sube y se desvanece (×2 de una dorada, +20 puntos…) */
  float(c, text, cls) {
    const [x, y] = this.pt(c);
    const g = s('g', { class: 'floaty' + (cls ? ' ' + cls : ''), transform: `translate(${x.toFixed(1)},${(y - 40).toFixed(1)})` }, s('text', null, text));
    this.lFx.appendChild(g);
    this.later(() => g.remove(), 1200);
  }
  screenPoint(i) {
    const m = this.svg.getScreenCTM(); if (!m) return [innerWidth / 2, innerHeight / 2];
    const p = this.svg.createSVGPoint(); const q0 = this.pt(i); p.x = q0[0]; p.y = q0[1];
    const q = p.matrixTransform(m); return [q.x, q.y];
  }
  /* "dedo fantasma" que enseña un trazo: ~1 s por tramo, se repite hasta que
     se toca el tablero (10 s como máximo); las fichas de la pista brillan */
  ghost(path, opts) {
    this.stopGhost();
    if (!path || path.length < 2) return;
    opts = Object.assign({ seg: 1000, pause: 700, max: 10000 }, opts || {});
    const g = s('g', { class: 'ghost-finger' });
    const trail = s('polyline', { class: 'trail ghost-trail', points: '' });
    const dot = s('circle', { class: 'ghost-dot', r: 22 });
    g.append(trail, dot); this.lBadges.appendChild(g);
    const halos = s('g', { class: 'ghost-halos' });
    for (const c of path) { const [x, y] = this.pt(c); halos.appendChild(s('circle', { cx: x.toFixed(1), cy: (y + 3).toFixed(1), r: 72, fill: `url(#bc-hintglow-${this.uid})` })); }
    this.lGlow.appendChild(halos);
    const pts = path.map(c => this.pt(c));
    const t0 = performance.now();
    let dead = false;
    const seg = opts.seg, total = seg * (pts.length - 1) + opts.pause;
    const loop = now => {
      if (dead) return;
      const el = Math.max(0, now - t0);
      if (el > opts.max) { this.stopGhost(); return; }
      const t = el % total;   // el primer fotograma puede llegar con una marca de tiempo anterior a t0
      const k = Math.min(pts.length - 1, Math.floor(t / seg)), f = Math.min(1, (t - k * seg) / seg);
      const ease = f < .5 ? 2 * f * f : 1 - Math.pow(-2 * f + 2, 2) / 2;
      const a = pts[k], b = pts[Math.min(pts.length - 1, k + 1)];
      const x = a[0] + (b[0] - a[0]) * ease, y = a[1] + (b[1] - a[1]) * ease;
      dot.setAttribute('cx', x.toFixed(1)); dot.setAttribute('cy', y.toFixed(1));
      trail.setAttribute('points', pts.slice(0, k + 1).concat([[x, y]]).map(p => p[0].toFixed(1) + ',' + p[1].toFixed(1)).join(' '));
      g.style.opacity = t > seg * (pts.length - 1) ? '0' : '1';
      this._ghostRaf = requestAnimationFrame(loop);
    };
    this._ghostRaf = requestAnimationFrame(loop);
    this._ghostKill = () => { dead = true; cancelAnimationFrame(this._ghostRaf); g.remove(); halos.remove(); if (opts.onEnd) opts.onEnd(); };
  }
  stopGhost() { if (this._ghostKill) { const k = this._ghostKill; this._ghostKill = null; k(); } }
  get ghosting() { return !!this._ghostKill; }

  /* ---------------- gesto ---------------- */
  toLocal(e) {
    const m = this.svg.getScreenCTM(); if (!m) return null;
    const p = this.svg.createSVGPoint(); p.x = e.clientX; p.y = e.clientY;
    const q = p.matrixTransform(m.inverse()); return [q.x, q.y];
  }
  /* la ficha más cercana (en un panal de hexágonos, la más cercana es la que está debajo) */
  cellAt(x, y, r) {
    let best = -1, bd = 1e9;
    for (let i = 0; i < E.N; i++) {
      if (!this.inShape(i)) continue;
      const p = this.pt(i), dx = x - p[0], dy = y - p[1], d = dx * dx + dy * dy;
      if (d < bd) { bd = d; best = i; }
    }
    return bd <= r * r ? best : -1;
  }
  canExtend(path, c) {
    if (path.includes(c)) return { ok: false };
    const last = path[path.length - 1];
    const op = E.opBetween[last][c];
    if (op < 0) return { ok: false };
    if (this.vals[c] < 0 || !this.inShape(c)) return { ok: false };
    if (this.blocked(c)) return { ok: false, reason: 'used' };
    if (path.length >= 2 && E.opBetween[path[path.length - 2]][last] === E.EQ) return { ok: false, reason: 'after-eq' };
    if (op === E.EQ && path.length < 2) return { ok: false, reason: 'eq-early' };
    if (path.length >= this.o.maxLen) return { ok: false, reason: 'max' };
    if (op !== E.EQ && path.length >= this.o.maxLen - 1) return { ok: false, reason: 'max-eq' };
    if (this.vals[c] === E.X && path.some(p => this.vals[p] === E.X)) return { ok: false, reason: 'two-x' };
    return { ok: true, op };
  }
  endsWithEq(path) { return path.length >= 2 && E.opBetween[path[path.length - 2]][path[path.length - 1]] === E.EQ; }
  /* gesto muy rápido que se salta una ficha: la vecina común más cercana a la línea del gesto */
  bridge(a, c, ax, ay, bx, by) {
    let best = -1, bd = HEX_R;
    for (const { j } of E.adj[a]) {
      if (j === c || this.path.includes(j) || !this.inShape(j) || this.vals[j] < 0) continue;
      if (E.opBetween[j][c] < 0) continue;
      const q = this.pt(j), d = distToSeg(q[0], q[1], ax, ay, bx, by);
      if (d < bd) { bd = d; best = j; }
    }
    return best;
  }

  emitPath() { this.setPath(this.path); if (this.o.onPathChange) this.o.onPathChange(this.path.slice()); }
  push(c, silent) { this.path.push(c); if (!silent) { Sound.step(this.path.length - 1); Haptics.tick(this.path.length - 1); } this.emitPath(); }
  startAt(c) { this.path = [c]; this.pathMode = null; this.emitPath(); Sound.step(0); Haptics.tick(0); }
  popLast() { this.path.pop(); Sound.back(); Haptics.back(); this.emitPath(); }
  clearPath() { this.cancelStale(); this.pathMode = null; if (this.path.length) { this.path = []; this.emitPath(); } }
  blockedMsg(reason, cell) {
    this._blockedNow = true;
    if (cell != null && cell >= 0 && cell !== this._lastBlockCell) { this._lastBlockCell = cell; this.nudge(cell); Sound.thud(); Haptics.block(); }
    if (reason && this.o.onBlocked && reason !== this._lastBlock) { this._lastBlock = reason; this.o.onBlocked(reason, cell); }
  }
  /* un trazo sin = que se queda a medias: aviso y, a los 2 s, se borra con suavidad */
  scheduleStale(ms, quiet) {
    this.cancelStale();
    const snap = this.path.join(',');
    if (this.o.onPending && !quiet) this.o.onPending(this.path.slice());
    this._stale = setTimeout(() => {
      this._stale = 0;
      if (this.path.join(',') !== snap || !this.path.length) return;
      this.el.classList.add('is-fading');
      this._stale = setTimeout(() => {
        this._stale = 0;
        this.el.classList.remove('is-fading');
        if (this.path.join(',') !== snap) return;
        this.path = []; this.pathMode = null; this.emitPath();
        if (this.o.onStale) this.o.onStale();
      }, 220);
    }, (ms || 2000) - 220);
  }
  cancelStale() { if (this._stale) { clearTimeout(this._stale); this._stale = 0; } this.el.classList.remove('is-fading'); }

  bindPointer() {
    const svg = this.svg;
    const R_PICK = HEX_R * .9;      // margen en las esquinas: no se "cuelan" fichas al rozar un vértice
    const R_BACK = HEX_R * .72;     // para volver atrás hay que entrar de verdad en la ficha anterior
    let drag = false, moved = 0, pressedLast = false, lastPt = null, pid = null;
    let placed = null;              // toque sobre una operación colocada: { c, x, y, pid, moved, timer }
    const PLACED_MOVE = 12;         // px: a partir de aquí es un arrastre, no un toque
    const endPlaced = () => { if (placed) { clearTimeout(placed.timer); placed = null; } };
    const step = (x, y, ax, ay) => {
      const p = this.path; if (!p.length) return;
      const c = this.cellAt(x, y, R_PICK);
      if (c < 0) return;
      const last = p[p.length - 1];
      if (c === last) return;
      if (p.length >= 2 && c === p[p.length - 2]) {
        const q = this.pt(c);
        if (Math.hypot(x - q[0], y - q[1]) <= R_BACK) { this.popLast(); moved++; }
        return;
      }
      if (p.includes(c)) return;
      if (E.opBetween[last][c] < 0) {
        const mid = this.bridge(last, c, ax, ay, x, y);
        if (mid < 0) return;
        const r0 = this.canExtend(p, mid);
        if (!r0.ok) { this.blockedMsg(r0.reason, mid); return; }
        this.push(mid); moved++; Diag.add('bridged'); Diag.add('dragTiles');
      }
      const r = this.canExtend(this.path, c);
      if (r.ok) { this.push(c); moved++; Diag.add('dragTiles'); } else this.blockedMsg(r.reason, c);
    };
    // muestrea el gesto cada 6 unidades: ningún hexágono por el que pase el dedo se queda fuera
    const trace = (a, b) => {
      const dx = b[0] - a[0], dy = b[1] - a[1], n = Math.max(1, Math.ceil(Math.hypot(dx, dy) / 6));
      for (let k = 1; k <= n; k++) step(a[0] + dx * k / n, a[1] + dy * k / n, a[0], a[1]);
    };
    svg.addEventListener('pointerdown', e => {
      if (e.button > 0) return;
      const pt = this.toLocal(e); if (!pt) return;
      Sound.unlock();
      if (this.o.onAnyPointer) this.o.onAnyPointer();
      const c = this.cellAt(pt[0], pt[1], HEX_R);
      this._lastBlock = null; this._lastBlockCell = -1; this._blockedNow = false;
      if (this.o.editable) { if (c >= 0 && this.o.onCellClick) this.o.onCellClick(c); return; }
      // modo «elegir ficha» (martillo): el toque elige, no empieza un trazo
      if (this.pickMode) { e.preventDefault(); this.pickMode(c >= 0 && this.vals[c] >= 0 ? c : -1); return; }
      if (c < 0) {
        const cc = this.anyCellAt(pt[0], pt[1]);
        if (cc >= 0 && this.covered && this.covered.has(cc) && this.o.onCovered) { e.preventDefault(); this.o.onCovered(cc); }
        this.clearPath(); return;
      }
      if (this.vals[c] < 0) return;
      e.preventDefault();
      this.stopGhost();
      this.cancelStale();
      if (this.blocked(c) && !this.path.includes(c)) {
        // operación colocada: arrastrar desde aquí no la quita; un toque o una pulsación larga ofrecen «Quitar»
        endPlaced();
        placed = { c, x: e.clientX, y: e.clientY, pid: e.pointerId, moved: false };
        placed.timer = setTimeout(() => { if (placed && !placed.moved) { const c0 = placed.c; placed = null; if (this.o.onTapPlaced) this.o.onTapPlaced(c0, 'long'); } }, 400);
        try { svg.setPointerCapture(e.pointerId); } catch (err) { /* noop */ }
        return;
      }
      pressedLast = false;
      const p = this.path, last = p[p.length - 1];
      if (p.length && c === last) pressedLast = true;                         // seguir desde el extremo resaltado
      else if (p.length && this.pathMode === 'tap' && E.opBetween[last][c] >= 0 && !p.includes(c)) {
        const r = this.canExtend(p, c);                                       // tocando ficha a ficha
        if (r.ok) this.push(c); else this.blockedMsg(r.reason, c);
      } else this.startAt(c);                                                 // cualquier otro gesto: trazo nuevo
      drag = true; moved = 0; lastPt = pt; pid = e.pointerId;
      try { svg.setPointerCapture(e.pointerId); } catch (err) { /* noop */ }
    });
    svg.addEventListener('pointermove', e => {
      if (placed && e.pointerId === placed.pid) {
        if (!placed.moved && Math.hypot(e.clientX - placed.x, e.clientY - placed.y) > PLACED_MOVE) {
          placed.moved = true; clearTimeout(placed.timer);
          if (this.o.onDragPlaced) this.o.onDragPlaced(placed.c);
        }
        return;
      }
      if (!drag || e.pointerId !== pid) return;
      const evs = (typeof e.getCoalescedEvents === 'function' && e.getCoalescedEvents()) || [];
      for (const ev of (evs.length ? evs : [e])) {
        const pt = this.toLocal(ev); if (!pt) continue;
        trace(lastPt, pt);
        lastPt = pt;
      }
    });
    const up = e => {
      if (placed && (!e || e.pointerId === placed.pid)) {
        const p0 = placed; endPlaced();
        if (e && !p0.moved && this.o.onTapPlaced) this.o.onTapPlaced(p0.c, 'tap');
        return;
      }
      if (!drag || (e && e.pointerId !== pid)) return;
      drag = false;
      if (pressedLast && moved === 0) {                                       // tocar el extremo lo quita
        if (this.path.length === 1) this.clearPath(); else this.popLast();
        this.pathMode = 'tap';
        return;
      }
      if (this.endsWithEq(this.path) && this.o.onSubmit) { this.pathMode = null; this.o.onSubmit(this.path.slice()); return; }
      this.pathMode = moved > 0 ? 'drag' : 'tap';
      // arrastre sin = o movimiento bloqueado: el trazo se queda 2 s (se puede seguir desde el extremo) y se borra
      if (this.path.length && (moved > 0 || this._blockedNow)) this.scheduleStale(2000, this._blockedNow);
    };
    svg.addEventListener('pointerup', up);
    svg.addEventListener('pointercancel', () => { drag = false; endPlaced(); });
    svg.addEventListener('lostpointercapture', () => { if (placed) endPlaced(); else if (drag) up(null); });
    svg.addEventListener('contextmenu', e => e.preventDefault());
    this.el.addEventListener('keydown', e => this.onKey(e));
    this.el.addEventListener('blur', () => { this.moveCursor(-1, true); this.cursor = -1; });
  }
  moveCursor(i, hide) {
    if (this.cursor >= 0) this.cls(this.cursor, 'is-sel', false);
    if (hide) { this.drawMarks(); return; }
    this.cursor = i;
    if (i >= 0) this.cls(i, 'is-sel', true);
    this.drawMarks();
  }
  firstCell() { for (const i of [9, 8, 10, 4, 5, 13, 14]) if (this.inShape(i) && this.vals[i] >= 0) return i; for (let i = 0; i < E.N; i++) if (this.inShape(i)) return i; return 0; }
  onKey(e) {
    if (this.o.onKey && this.o.onKey(e) === true) return;
    const dirs = { ArrowUp: [0, -1], ArrowDown: [0, 1], ArrowLeft: [-1, -.4], ArrowRight: [1, -.4] };
    if (dirs[e.key]) {
      e.preventDefault();
      if (this.cursor < 0) { this.moveCursor(this.firstCell()); return; }
      const cur = this.cursor, [vx, vy] = dirs[e.key];
      let best = -1, bs = .2;
      for (const { j } of E.adj[cur]) {
        if (!this.inShape(j)) continue;
        const dx = CENTERS[j][0] - CENTERS[cur][0], dy = CENTERS[j][1] - CENTERS[cur][1];
        const sc = (dx * vx + dy * vy) / Math.hypot(dx, dy) / Math.hypot(vx, vy);
        if (sc > bs) { bs = sc; best = j; }
      }
      if (best >= 0) this.moveCursor(best);
      return;
    }
    if (this.o.editable) return;
    if (this.pickMode && (e.key === 'Enter' || e.key === ' ' || e.key === 'Escape')) {
      e.preventDefault();
      if (e.key === 'Escape') { this.pickMode(-1); return; }
      if (this.cursor < 0) { this.moveCursor(this.firstCell()); return; }
      this.pickMode(this.cursor); return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (this.cursor < 0) { this.moveCursor(this.firstCell()); return; }
      const c = this.cursor;
      this.cancelStale();
      if (this.blocked(c)) { if (this.o.onTapPlaced) this.o.onTapPlaced(c, 'key'); return; }
      const p = this.path;
      if (p.length && c === p[p.length - 1]) { this.popLast(); return; }
      if (p.length) {
        const r = this.canExtend(p, c);
        if (r.ok) { this.push(c); this.pathMode = 'tap'; if (this.endsWithEq(this.path) && this.o.onSubmit) this.o.onSubmit(this.path.slice()); return; }
        this._lastBlockCell = -1; this.blockedMsg(r.reason, c); this._lastBlock = null;
        if (E.opBetween[p[p.length - 1]][c] >= 0 && !p.includes(c)) return;   // vecina pero no permitida: se queda el trazo
      }
      this.startAt(c); this.pathMode = 'tap';
    } else if (e.key === 'Backspace') { e.preventDefault(); if (this.path.length) this.popLast(); }
    else if (e.key === 'Escape') { this.clearPath(); }
  }
}

/* ======================================================================
   gameui.js — piezas comunes de las pantallas de juego:
   bocadillo de la operación, línea del entrenador (Hexi), teclado de la X
   y estrellas. Nada de esto toca las fichas.
   ====================================================================== */
const OP_TXT_CLASS = ['op-plus', 'op-minus', 'op-eq'];

/* Bocadillo con la operación en texto grande de interfaz («9 − 6 = 3 ✓»).
   Vive en una zona de altura fija: nunca empuja el tablero. */
class OpBubble {
  constructor() {
    this.b = h('div', { class: 'bubble is-idle' });
    this.el = h('div', { class: 'opzone', 'aria-live': 'polite' }, this.b);
    this.idleText = '';
    this.flashT = 0;
  }
  idle(text) { this.idleText = text || ''; if (!this.flashT && !this.busy) this.renderIdle(); }
  renderIdle() {
    this.busy = false; this._prev = null;
    this.b.className = 'bubble is-idle';
    this.b.replaceChildren(h('span', { class: 'idle' }, this.idleText));
  }
  tokens(vals, path, xv, keep) {
    const out = [];
    path.forEach((c, k) => {
      const old = k < (keep || 0) ? ' old' : '';
      if (k) { const op = E.opBetween[path[k - 1]][c]; out.push(' ', h('span', { class: 'tk-o ' + OP_TXT_CLASS[op] + old }, E.OP_CHAR[op]), ' '); }
      const isX = vals[c] === E.X;
      out.push(h('span', { class: 'tk-n' + (isX ? ' is-x' : '') + old }, isX && xv != null ? String(xv) : E.sym(vals[c])));
    });
    out.push(' ');
    return out;
  }
  /* trazo en curso */
  show(vals, path, opts) {
    opts = opts || {};
    if (this.flashT) { clearTimeout(this.flashT); this.flashT = 0; }
    if (!path.length) { this.renderIdle(); return; }
    this.busy = true;
    // fichas que ya estaban: sin animación (solo entra la nueva)
    let keep = 0; const prev = this._prev || [];
    while (keep < prev.length && keep < path.length && prev[keep] === path[keep]) keep++;
    this._prev = path.slice();
    const kids = this.tokens(vals, path, null, keep);
    const done = path.length >= 2 && E.opBetween[path[path.length - 2]][path[path.length - 1]] === E.EQ;
    if (!done && path.length >= 2 && opts.run != null) kids.push(h('span', { class: 'run' }, 'llevas ', h('b', null, TXT.minus(opts.run))));
    if (opts.ask) kids.push(h('span', { class: 'ask' }, opts.ask));
    this.b.className = 'bubble is-live' + (opts.warn ? ' is-warn' : '') + (opts.ask ? ' is-ask' : '');
    this.b.replaceChildren(...kids);
  }
  /* veredicto: ✓ o ✗; vuelve solo a reposo */
  flash(vals, path, ok, opts) {
    opts = opts || {};
    if (this.flashT) clearTimeout(this.flashT);
    this.busy = true;
    const kids = this.tokens(vals, path, opts.xv);
    // repetida (BYD-047): ni ✓ ni ✗, en gris
    if (!opts.dup) kids.push(h('span', { class: 'verdict' }, icon(ok ? 'check' : 'x')));
    if (opts.note) kids.push(h('span', { class: 'note' }, opts.note));
    this.b.className = 'bubble ' + (opts.dup ? 'is-dup' : ok ? 'is-ok' : 'is-ko');
    this.b.replaceChildren(...kids);
    void this.b.offsetWidth;
    this.b.classList.add('pulse');
    this.flashT = setTimeout(() => { this.flashT = 0; if (this.onSettle) this.onSettle(); else this.renderIdle(); }, opts.ms || (ok ? 1000 : 1300));
  }
  stop() { if (this.flashT) { clearTimeout(this.flashT); this.flashT = 0; } }
}

/* Línea del entrenador: Hexi y un bocadillo de dos líneas como máximo.
   Mensajes de bloqueo, fallos, pistas y ánimos van aquí (nunca sobre la cabecera). */
class Coach {
  constructor() {
    this.face = h('div', { class: 'coach-face' }, hexi('idle'));
    this.say = h('p', { class: 'say' });
    this.el = h('div', { class: 'coach', role: 'status', 'aria-live': 'polite' }, this.face, this.say);
    this.idleText = ''; this.mood = 'idle'; this.t = 0;
  }
  setMood(m) { if (m === this.mood) return; this.mood = m; this.face.replaceChildren(hexi(m)); }
  idle(text) { this.idleText = text || ''; if (!this.t && !this.sticky) this.render(this.idleText, 'info', 'idle'); }
  render(text, kind, mood) {
    this.el.className = 'coach is-' + (kind || 'info');
    if (typeof text === 'string') this.say.textContent = text; else this.say.replaceChildren(text);
    this.setMood(mood || 'idle');
    this.say.classList.remove('in'); void this.say.offsetWidth; this.say.classList.add('in');
  }
  /* mensaje temporal; ms = 0 lo deja fijo hasta el siguiente */
  tell(text, kind, ms) {
    if (this.t) clearTimeout(this.t);
    this.t = 0; this.sticky = ms === 0;                    // fijo: no lo pisa el texto de reposo hasta el siguiente aviso o stop()
    const mood = kind === 'bad' ? 'oops' : kind === 'good' ? 'happy' : kind === 'warn' ? 'oops' : 'idle';
    this.render(text, kind, mood);
    if (ms !== 0) this.t = setTimeout(() => { this.t = 0; this.render(this.idleText, 'info', 'idle'); }, ms || 2600);
  }
  stop() { if (this.t) clearTimeout(this.t); this.t = 0; this.sticky = false; }
  /* tras un bloqueo, en cuanto se añade una ficha válida el aviso desaparece */
  calm() { if (/\bis-warn\b/.test(this.el.className) && !this.sticky) { this.stop(); this.render(this.idleText, 'info', 'idle'); } }
}

/* Teclado «X = ?»: el jugador dice cuánto vale la X. onPick(d) devuelve true si
   acierta (se cierra) o false (sacudida y otro intento). Esc o «Cancelar»: onCancel.
   No es modal: no hay ninguna capa invisible encima del tablero. Si se toca fuera
   del teclado (por ejemplo, para empezar otro trazo), el teclado se cierra y ese
   mismo toque llega a su destino. */
function askX(opts) {
  const prevFocus = document.activeElement;
  const keys = h('div', { class: 'xpad-keys' });
  const sheet = h('div', { class: 'xpad', role: 'dialog', 'aria-label': '¿Cuánto vale X?' },
    h('div', { class: 'xpad-head' }, h('b', null, '¿Cuánto vale ', h('span', { class: 'x' }, 'X'), '?'), opts.expr ? h('span', { class: 'xpad-expr' }, opts.expr) : null),
    keys,
    h('div', { class: 'xpad-foot' }, h('button', { class: 'btn ghost xpad-cancel', onclick: () => api.cancel() }, 'Cancelar')));
  const back = h('div', { class: 'xpad-back' }, sheet);
  let closed = false;
  const api = {
    close(keepFocus) {
      if (closed) return; closed = true;
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('pointerdown', onOutside, true);
      back.classList.add('out'); setTimeout(() => back.remove(), 180);
      if (!keepFocus && prevFocus && prevFocus.focus) try { prevFocus.focus({ preventScroll: true }); } catch (e) { /* noop */ }
    },
    cancel(keepFocus) { if (closed) return; api.close(keepFocus); if (opts.onCancel) opts.onCancel(); },
    pick(d) {
      if (closed) return;
      Sound.key(); Haptics.key();
      const ok = opts.onPick(d);
      if (ok) { api.close(); return; }
      const btn = keys.children[d === 0 ? 9 : d - 1];
      sheet.classList.remove('shake'); void sheet.offsetWidth; sheet.classList.add('shake');
      if (btn) { btn.classList.add('wrong'); btn.disabled = true; btn.setAttribute('aria-label', d + ' (no es)'); }
    }
  };
  [1, 2, 3, 4, 5, 6, 7, 8, 9, 0].forEach(d => keys.appendChild(h('button', { class: 'xkey', 'aria-label': String(d), onclick: () => api.pick(d) }, String(d))));
  const onKey = e => {
    if (/^[0-9]$/.test(e.key)) { e.preventDefault(); e.stopPropagation(); const b = keys.children[+e.key === 0 ? 9 : +e.key - 1]; if (b && !b.disabled) api.pick(+e.key); }
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); api.cancel(); }
  };
  // un toque fuera del teclado lo cierra sin «comerse» el toque (el trazo nuevo empieza a la vez)
  const onOutside = e => { if (!sheet.contains(e.target)) api.cancel(true); };
  document.addEventListener('keydown', onKey, true);
  document.addEventListener('pointerdown', onOutside, true);
  layer('bc-modal-root').appendChild(back);
  if (opts.avoid) placeAway(back, sheet, opts.avoid, opts.board);
  setTimeout(() => { try { keys.firstChild.focus({ preventScroll: true }); } catch (e) { /* noop */ } }, 40);
  return api;
}
/* BYD-046: el teclado se pone en el lado contrario al trazo (arriba o abajo; si el trazo ocupa todo
   el alto, en la columna de al lado) para que la operación se siga viendo entera */
function placeAway(back, sheet, avoid, board) {
  const vw = innerWidth, vh = innerHeight, m = 12;
  back.classList.add('placed');
  const w = Math.min(420, vw - 2 * m);
  sheet.style.width = w + 'px';
  const hgt = sheet.getBoundingClientRect().height || 240;
  const cx = (avoid.left + avoid.right) / 2, cy = (avoid.top + avoid.bottom) / 2;
  const over = (r, x, y) => Math.max(0, Math.min(x + w, r.right) - Math.max(x, r.left)) * Math.max(0, Math.min(y + hgt, r.bottom) - Math.max(y, r.top));
  const inter = (x, y) => over(avoid, x, y);
  // en el ordenador (o en horizontal), primero la columna de al lado del panal: así no tapa ninguna ficha
  if (board) {
    const y = Math.max(m, vh - hgt - m);
    for (const x of [board.right + m, board.left - w - m]) {
      if (x < m || x + w > vw - m) continue;
      if (!over(board, x, y)) { sheet.style.left = Math.round(x) + 'px'; sheet.style.top = Math.round(y) + 'px'; return; }
    }
  }
  const midX = Math.max(m, Math.min(vw - w - m, cx - w / 2));
  const cands = [
    [midX, cy > vh / 2 ? m : vh - hgt - m],                 // el lado contrario (arriba o abajo)
    [midX, cy > vh / 2 ? vh - hgt - m : m],
    [Math.min(vw - w - m, avoid.right + m), Math.max(m, Math.min(vh - hgt - m, cy - hgt / 2))],   // a la derecha del trazo
    [Math.max(m, avoid.left - w - m), Math.max(m, Math.min(vh - hgt - m, cy - hgt / 2))]          // a la izquierda
  ];
  let best = cands[0], bi = inter(...cands[0]);
  for (const c of cands.slice(1)) { const i = inter(...c); if (i < bi) { bi = i; best = c; } if (!bi) break; }
  sheet.style.left = Math.round(best[0]) + 'px';
  sheet.style.top = Math.round(best[1]) + 'px';
}

/* Globo «Quitar» sobre una operación colocada (BYD-039). Quitar una operación
   necesita un gesto explícito: tocarla (o mantenerla pulsada) y elegir «Quitar».
   Arrastrar desde ella o a través de ella nunca la quita. No es modal: un toque
   fuera lo cierra sin perderse. Con teclado: Intro sobre la ficha lo abre con el
   foco en «Quitar»; Esc lo cierra. */
let _placedMenu = null;
function closePlacedMenu() { if (_placedMenu) _placedMenu.close(true); }
function placedMenu(opts) {
  closePlacedMenu();
  const board = opts.board, cells = opts.cells || [];
  const remove = h('button', { class: 'btn small danger pm-remove', onclick: () => { api.close(opts.how !== 'key'); opts.onRemove(); } }, icon('trash'), 'Quitar');
  const keep = h('button', { class: 'btn small pm-keep', onclick: () => api.close() }, 'Cancelar');
  const el = h('div', { class: 'placed-menu', role: 'dialog', 'aria-label': 'Operación colocada: ' + opts.label },
    h('span', { class: 'pm-expr' }, opts.label), h('div', { class: 'pm-btns' }, remove, keep));
  el.style.visibility = 'hidden';
  layer('bc-modal-root').appendChild(el);
  // encima de la operación (o debajo si no cabe; si tampoco, al lado), sin taparla y dentro de la pantalla
  const boxes = cells.map(c => { const t = board.tiles[c] && board.tiles[c].querySelector('.ring'); return t ? t.getBoundingClientRect() : null; }).filter(Boolean);
  const bb = boxes.length ? { l: Math.min(...boxes.map(b => b.left)), r: Math.max(...boxes.map(b => b.right)), t: Math.min(...boxes.map(b => b.top)), b: Math.max(...boxes.map(b => b.bottom)) }
    : { l: innerWidth / 2, r: innerWidth / 2, t: innerHeight / 2, b: innerHeight / 2 };
  const r = el.getBoundingClientRect(), gap = 10;
  let x = (bb.l + bb.r) / 2 - r.width / 2, y;
  if (bb.t - gap - r.height >= 8) y = bb.t - gap - r.height;
  else if (bb.b + gap + r.height <= innerHeight - 8) y = bb.b + gap;
  else { y = (bb.t + bb.b) / 2 - r.height / 2; x = bb.r + gap + r.width <= innerWidth - 8 ? bb.r + gap : bb.l - gap - r.width; }
  x = Math.max(8, Math.min(innerWidth - r.width - 8, x));
  y = Math.max(8, Math.min(innerHeight - r.height - 8, y));
  el.style.left = Math.round(x) + 'px'; el.style.top = Math.round(y) + 'px'; el.style.visibility = '';
  board.setRing(cells, 'is-picked');
  let closed = false;
  const onOutside = e => { if (!el.contains(e.target)) api.close(true); };
  const onKey = e => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); api.close(); } };
  const api = {
    close(quiet) {
      if (closed) return; closed = true;
      document.removeEventListener('pointerdown', onOutside, true);
      document.removeEventListener('keydown', onKey, true);
      el.remove(); board.setRing([], 'is-picked');
      if (_placedMenu === api) _placedMenu = null;
      if (!quiet && board.el && board.el.focus) try { board.el.focus({ preventScroll: true }); } catch (e) { /* noop */ }
      if (opts.onClose) opts.onClose();
    },
    el
  };
  // se registra en el siguiente ciclo: el mismo toque que lo abre no lo cierra
  setTimeout(() => { if (!closed) document.addEventListener('pointerdown', onOutside, true); }, 0);
  document.addEventListener('keydown', onKey, true);
  if (opts.how === 'key') try { remove.focus({ preventScroll: true }); } catch (e) { /* noop */ }
  _placedMenu = api;
  return api;
}

/* Comparte: hoja nativa del móvil si existe; si no, al portapapeles */
async function shareOrCopy(text, title) {
  emitEvent('share', {});
  if (navigator.share && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || '')) {
    try { await navigator.share({ text, title: title || 'BYŌDŌ City' }); return 'shared'; } catch (e) { if (e && e.name === 'AbortError') return 'cancel'; }
  }
  const ok = await copyText(text);
  toast(ok ? 'Copiado: pégalo donde quieras' : 'Selecciona el texto y cópialo', { type: ok ? 'ok' : '' });
  return ok ? 'copied' : 'fail';
}

/* Cuadrados por operación (no hay emoji de hexágono): un cuadrado por ficha */
const SQUARES = { '#F6A000': '🟨', '#EC680E': '🟧', '#41A7AD': '🟦', '#9BC255': '🟩', '#E7799A': '🟥', '#9C8BDB': '🟪' };
const squaresOf = groups => groups.map(g => (SQUARES[g.color] || '⬜').repeat(g.path.length)).join(' ');

/* ======================================================================
   daily.js — lo que el mapa necesita del reto del día sin cargar la partida:
   dificultad precalculada, dificultad que se enseña y aviso de cambio de día
   ====================================================================== */
/* dificultad de un reto sin generarlo: tabla precalculada (tools/daily-meta.js), solo en la app.
   Así el mapa no tiene que ejecutar el solver al arrancar (BYD-015). 0 = no está en la tabla. */
function dailyMetaLevel(date) {
  if (typeof BC_DAILY_META === 'undefined' || !BC_DAILY_META || BC_DAILY_META.v !== E.GEN_V || !date) return 0;
  const utc = ds => { const [y, m, d] = ds.split('-').map(Number); return Date.UTC(y, m - 1, d); };
  const k = Math.round((utc(date) - utc(BC_DAILY_META.from)) / 864e5);
  return k >= 0 && k < BC_DAILY_META.levels.length ? +BC_DAILY_META.levels[k] : 0;
}
/* dificultad que se enseña: la medida por el solver, no la prevista por el día de la semana */
function shownLevel(pz) {
  if (pz && pz.kind === 'daily') { const m = dailyMetaLevel(pz.date); if (m) return m; }
  return (pz.difficulty && pz.difficulty.level) || pz.level || 1;
}

/* Avisa cuando cambia el día en Madrid: app abierta a medianoche o vuelta de segundo plano */
function watchDay(onNewDay) {
  let last = todayStr();
  const check = () => { const t = todayStr(); if (t !== last) { last = t; onNewDay(t); } };
  // un único temporizador hasta la medianoche de Madrid (sin comprobaciones periódicas);
  // si el móvil duerme, al volver a la app se comprueba la fecha
  const arm = () => { clearTimeout(watchDay._t); watchDay._t = setTimeout(() => { check(); arm(); }, Math.max(1000, msToMadridMidnight() + 1500)); };
  document.addEventListener('visibilitychange', () => { if (!document.hidden) check(); });
  arm();
  return check;
}

/* ======================================================================
   play.js — partida: reto del día, práctica y panales compartidos
   Objetivos: "Cierra el panal" (contrarreloj, o sin reloj en modo Relax)
   y "Caza" (por niveles 3/4/5/6)
   ====================================================================== */
const HUNT_NAMES = { 3: 'Fácil', 4: 'Media', 5: 'Difícil', 6: 'Experta' };
const HUNT_SPAN = { 3: '3 fichas', 4: 'hasta 4', 5: 'hasta 5', 6: 'hasta 6' };
const huntSpanText = L => (L === 3 ? '3 fichas' : `hasta ${L} fichas`);
function blockMsg(reason, maxLen) {
  return ({
    used: 'Esa ficha ya está en una operación colocada. Tócala si quieres quitarla.',
    'after-eq': 'Después del = solo va el resultado.',
    'eq-early': 'Antes del = necesitas al menos dos fichas: a + b = c.',
    max: `Máximo ${maxLen} fichas por operación.`,
    'max-eq': `Llevas ${TXT.tiles(maxLen - 1)}: la siguiente tiene que ir tras un = (máximo ${maxLen}).`,
    'two-x': 'Solo puede haber una X en cada operación.'
  })[reason] || '';
}
const PENDING_MSG = 'Acaba con = y el resultado, o sigue desde la ficha resaltada.';
const HINTS = [
  { id: 'check', t: '¿Voy bien?', d: 'Comprueba si tus operaciones encajan en una solución.' },
  { id: 'structure', t: 'Estructura', d: 'Cuántas operaciones hay y de cuántas fichas.' },
  { id: 'results', t: 'Resultados', d: 'Marca las fichas que van después de un =.' },
  { id: 'reveal', t: 'Revela una operación', d: 'Coloca una operación correcta por ti.' }
];

function makePuzzle(kind, src) {
  if (kind === 'daily') {
    const b = E.dailyBoard(src);
    return { id: 'd-' + src, kind, date: src, number: b.number, level: b.level, vals: b.vals, rules: b.rules, analysis: b.analysis, difficulty: b.difficulty };
  }
  if (kind === 'practice') {
    const g = E.generatePuzzle(E.rngFrom('practice-' + E.GEN_V + '-' + src.seed), src.level);
    return { id: 'p-' + src.seed + '-' + src.level, kind, seed: src.seed, level: src.level, vals: g.vals, rules: g.rules, analysis: g.analysis, difficulty: g.difficulty };
  }
  const vals = src.vals.slice();
  const rules = E.normRules(src.rules || CONFIG.rules);
  const a = E.analyze(vals, rules, { limit: 60, cap: 20000 });
  const d = E.difficulty(a);
  return { id: 'c-' + E.encode(vals) + '-' + rules.maxLen, kind, vals, rules, analysis: a, difficulty: d, level: d.level };
}
class PlayView {
  constructor(root, kind) {
    this.root = root; this.kind = kind;
    this.active = false;
    this.bubble = new OpBubble();
    this.coach = new Coach();
    this.bubble.onSettle = () => this.updateExpr();
    this.board = new BoardView({
      maxLen: CONFIG.rules.maxLen,
      onPathChange: p => this.onPath(p),
      onSubmit: p => this.submit(p),
      pathState: p => this.pathState(p),
      onBlocked: r => this.coach.tell(blockMsg(r, this.board.o.maxLen), 'warn', 2400),
      onPending: () => this.coach.tell(PENDING_MSG, 'info', 2000),
      onStale: () => this.updateExpr(),
      onTapPlaced: (c, how) => this.askRemove(c, how),
      onDragPlaced: c => this.placedNotice(c)
    });
    this.board.blocked = c => this.obj === 'close' && this.owner && this.owner[c] != null;
    this.board.el.addEventListener('pointerdown', () => this.autoStart(), true);
    document.addEventListener('visibilitychange', () => { if (document.hidden) this.pause(true); else this.resume(); });
  }

  /* ---------- carga ---------- */
  load(pz) {
    this.pz = pz;
    this.vals = pz.vals;
    this.ops = pz.analysis.ops;                         // operaciones para cerrar (máx. reglas del reto)
    this.hunt = E.huntIndex(pz.vals, E.HUNT_MAX);       // caza: expresiones únicas hasta 6 fichas
    const saved = (Store.get('games', {}))[pz.id];
    this.st = Object.assign({ started: false, elapsed: 0, penalty: 0, solved: false, finalMs: 0, hints: [], groups: [], found: [], obj: 'close', undo: [], errors: 0, huntLevel: 0 }, saved || {});
    if (!pz.analysis.perfect.count) this.st.obj = 'hunt';
    this.obj = this.st.obj;
    // compatibilidad: progreso antiguo guardado por camino → expresión
    this.found = new Set(this.st.found.map(k => this.hunt.byExpr.has(k) ? k : this.hunt.byKey.get(k)).filter(Boolean));
    if (!this.st.huntLevel) this.st.huntLevel = this.firstOpenLevel();
    this.runStart = 0;
    this.hintOut = null;
    this.board.clearPath();
    this.board.setVals(pz.vals);
    this.board.setResultMarks([]);
    this.applyMaxLen();
    this.syncOwner();
    this.render();
    this.board.setGroups(this.obj === 'close' ? this.st.groups : []);
    this.board.setPath([]);
    if (this.st.started && !this.st.solved && this.pz.kind === 'daily' && !this.relax) this.st.paused = true;
    this.updateLock();
  }
  get relax() { return !!this.st.relax; }
  /* reto de un día pasado jugado desde el calendario: no cuenta para la racha ni para el tiempo oficial */
  get archive() {
    if (!this.pz || this.pz.kind !== 'daily') return false;
    if (this.st.startedOn) return this.st.startedOn !== this.pz.date;
    return !this.st.started && this.pz.date < todayStr();
  }
  applyMaxLen() { this.board.o.maxLen = this.obj === 'close' ? this.pz.rules.maxLen : E.HUNT_MAX; }
  save(now) {
    const all = Store.get('games', {});
    all[this.pz.id] = Object.assign({}, this.st, { found: Array.from(this.found), undo: (this.st.undo || []).slice(-30), touched: Date.now() });
    const keys = Object.keys(all);
    if (keys.length > 80) keys.sort((a, b) => (all[a].touched || 0) - (all[b].touched || 0)).slice(0, keys.length - 80).forEach(k => delete all[k]);
    if (now) Store.setNow('games', all); else Store.set('games', all);
  }
  syncOwner() {
    this.owner = new Array(E.N).fill(null);
    this.st.groups.forEach((g, k) => g.path.forEach(c => { this.owner[c] = k; }));
  }
  get placedMask() { return this.st.groups.reduce((m, g) => m | E.maskOf(g.path), 0); }

  /* ---------- tiempo ---------- */
  elapsed() { return this.st.elapsed + (this.runStart ? Date.now() - this.runStart : 0); }
  shownTime() { return this.st.solved ? this.st.finalMs : this.elapsed() + (this.relax ? 0 : this.st.penalty); }
  start(relax) {
    if (this.st.solved) return;
    if (!this.st.started) { this.st.startedOn = todayStr(); if (relax != null) this.st.relax = !!relax; }
    this.st.started = true; this.st.paused = false;
    if (this.pz.kind === 'daily') { Stats.markStarted(this.pz.date); emitEvent('daily_start', { reto: this.pz.number, relax: this.relax, archive: this.archive }); emitEvent('start', { reto: this.pz.number }); }
    if (!this.runStart && this.active && !document.hidden) this.runStart = Date.now();
    this.save(true); this.updateLock(); this.renderTimer();
  }
  /* al pulsar «Empezar» en el móvil: que se vean el tablero y el reloj enteros */
  ensureVisible() {
    try {
      const sb = this.root.querySelector('.statusbar'); if (!sb || !sb.getBoundingClientRect) return;
      const top = sb.getBoundingClientRect().top, bottom = this.board.el.getBoundingClientRect().bottom;
      const reserve = ROOT() && ROOT().classList.contains('bc-app') ? 90 : 8;   // barra inferior de la app
      if (top < 0 || bottom > window.innerHeight - reserve) window.scrollBy({ top: top - 10, behavior: 'smooth' });
    } catch (e) { /* noop */ }
  }
  autoStart() { if (this.pz && this.pz.kind !== 'daily' && !this.st.started && !this.st.solved) this.start(); }
  pause(byVisibility) {
    if (this.runStart) { this.st.elapsed += Date.now() - this.runStart; this.runStart = 0; this.save(true); }
    if (byVisibility && this.pz && this.pz.kind === 'daily' && this.st.started && !this.st.solved && !this.relax) { this.st.paused = true; this.updateLock(); }
  }
  resume() { if (this.pz && this.st.started && !this.st.solved && !this.st.paused && this.active && !document.hidden && !this.runStart) this.runStart = Date.now(); }
  /* el reloj solo late con el reto a la vista */
  onShow() {
    this.active = true;
    if (!this.timerInt) this.timerInt = setInterval(() => this.tick(), 250);
    if (this.pz && this.pz.kind === 'daily' && this.st.started && !this.st.solved && !this.relax) { this.st.paused = true; this.updateLock(); } else this.resume();
    this.tick();
  }
  onHide() { this.active = false; closePlacedMenu(); this.pause(false); if (this.timerInt) { clearInterval(this.timerInt); this.timerInt = 0; } this.board.clearPath(); }
  tick() {
    if (!this.timerEl || !this.pz || this.relax) return;
    const txt = fmtTime(this.shownTime());
    if (this.timerTxt.textContent !== txt) this.timerTxt.textContent = txt;
    this.timerEl.classList.toggle('is-paused', !this.runStart && !this.st.solved);
    this.timerEl.classList.toggle('is-done', !!this.st.solved);
    if (this.runStart && Date.now() - (this._lastSave || 0) > 5000) { this._lastSave = Date.now(); this.st.elapsed = this.elapsed(); this.runStart = Date.now(); this.save(); }
  }
  renderTimer() {
    if (!this.timerEl) return;
    this.timerEl.classList.toggle('is-relax', this.relax);
    if (this.relax) { this.timerEl.replaceChildren(icon('flame'), h('span', null, 'Relax')); this.timerEl.title = 'Modo Relax: sin reloj'; }
    else { this.timerEl.replaceChildren(icon('clock'), this.timerTxt); this.timerEl.title = 'Tiempo (incluye las pistas)'; this.tick(); }
  }

  /* ---------- render ---------- */
  render() {
    const pz = this.pz;
    this.root.innerHTML = '';
    const stage = h('div', { class: 'stage' });
    const side = h('div', { class: 'side' });
    this.root.appendChild(h('div', { class: 'play-grid' }, stage, side));

    let eyebrow, title;
    if (pz.kind === 'daily') { eyebrow = (pz.number >= 1 ? TXT.daily + ' · #' + pz.number : 'Reto de prueba'); title = dateLabel(pz.date); }
    else if (pz.kind === 'practice') { eyebrow = 'Práctica'; title = 'Panales sin fin'; }
    else { eyebrow = 'Panal compartido · ' + E.encode(pz.vals); title = '¿Puedes cerrarlo?'; }
    this.timerTxt = h('span', null, '0:00');
    this.timerEl = h('div', { class: 'timer' }, icon('clock'), this.timerTxt);
    stage.appendChild(h('div', { class: 'statusbar' },
      h('div', { class: 'status-title' }, h('span', { class: 'eyebrow' }, eyebrow), h('h1', null, title)),
      pz.analysis.perfect.count && pz.kind !== 'practice' ? h('span', { class: 'level-pill', title: 'Dificultad medida por el solver' }, levelDots(shownLevel(pz)), E.LEVELS[shownLevel(pz)]) : null,
      this.timerEl));
    this.renderTimer();

    if (pz.kind === 'practice' || pz.kind === 'custom') stage.appendChild(this.practiceBar());

    this.objBtns = {};
    const seg = h('div', { class: 'segmented obj-seg', role: 'group', 'aria-label': 'Objetivo' });
    if (pz.analysis.perfect.count) {
      this.objBtns.close = h('button', { 'aria-pressed': String(this.obj === 'close'), onclick: () => this.setObj('close') }, icon('hex'), h('span', null, 'Cierra el panal'));
      seg.appendChild(this.objBtns.close);
    }
    this.huntCount = h('span', { class: 'count' }, '');
    this.objBtns.hunt = h('button', { 'aria-pressed': String(this.obj === 'hunt'), onclick: () => this.setObj('hunt') }, icon('search'), h('span', null, 'Caza'), this.huntCount);
    seg.appendChild(this.objBtns.hunt);
    stage.appendChild(h('div', { class: 'center-row' }, seg));

    stage.appendChild(this.bubble.el);
    // el tablero es el mismo de un reto a otro: la portada del reto anterior se quita (si no, al cambiar de día
    // desde el calendario quedaba debajo y reaparecía al pulsar «Empezar»)
    if (this.overlay) this.overlay.remove();
    this.overlay = h('div', { class: 'overlay', hidden: true });
    this.board.el.appendChild(this.overlay);
    stage.appendChild(h('div', { class: 'board-slot' }, this.board.el));
    stage.appendChild(this.coach.el);

    stage.appendChild(h('div', { class: 'toolbar' },
      h('button', { class: 'btn small', title: TXT.undo, onclick: () => this.undo() }, icon('undo'), h('span', { class: 'lbl' }, TXT.undo)),
      h('button', { class: 'btn small', title: TXT.restart, onclick: () => this.restart() }, icon('restart'), h('span', { class: 'lbl' }, TXT.restart))));

    this.side = side;
    this.renderSide();
    this.updateExpr();
    this.updateCounts();
  }
  practiceBar() {
    const bar = h('div', { class: 'practice-bar' });
    if (typeof App === 'undefined' || !App.newPractice) return bar;
    if (this.pz.kind === 'practice') {
      const seg = h('div', { class: 'segmented small', role: 'group', 'aria-label': 'Dificultad' });
      [1, 2, 3, 4].forEach(l => seg.appendChild(h('button', { 'aria-pressed': String(this.pz.level === l), title: `Máximo ${E.PRESETS[l].maxLen} fichas por operación`, onclick: () => App.newPractice(l) }, E.LEVELS[l])));
      appendKids(bar, [seg, h('button', { class: 'btn small primary', onclick: () => App.newPractice(this.pz.level) }, icon('shuffle'), 'Nuevo')]);
    } else {
      appendKids(bar, [h('button', { class: 'btn small', onclick: () => App.newPractice() }, icon('left'), 'Volver a práctica'),
        App.openInLab ? h('button', { class: 'btn small', onclick: () => App.openInLab(this.vals) }, icon('edit'), 'Abrir en el laboratorio') : null]);
    }
    return bar;
  }
  setObj(o) {
    if (o === this.obj) return;
    closePlacedMenu();
    this.obj = this.st.obj = o;
    this.applyMaxLen();
    this.board.clearPath();
    this.board.setGroups(o === 'close' ? this.st.groups : []);
    this.board.setResultMarks([]);
    Object.entries(this.objBtns).forEach(([k, b]) => b.setAttribute('aria-pressed', String(k === o)));
    this.renderSide(); this.updateExpr(); this.save(); this.updateLock();
  }
  updateLock() {
    if (!this.pz) return;
    const ov = this.overlay; ov.innerHTML = '';
    let locked = false;
    if (this.pz.kind === 'daily' && !this.st.solved && (!this.st.started || this.st.paused)) {
      locked = true;
      const card = h('div', { class: 'card' });
      if (!this.st.started) {
        let relaxPick = !!Settings.get().relax;
        const sw = h('button', { class: 'switch', role: 'switch', 'aria-checked': String(relaxPick), 'aria-label': 'Modo Relax' });
        sw.addEventListener('click', () => { relaxPick = !relaxPick; sw.setAttribute('aria-checked', String(relaxPick)); Settings.set({ relax: relaxPick }); });
        appendKids(card, [h('span', { class: 'eyebrow' }, this.pz.number >= 1 ? TXT.daily : 'Reto de prueba'), this.pz.number >= 1 ? h('span', { class: 'num' }, '#' + this.pz.number) : null,
          h('div', { class: 'sub' }, dateLabel(this.pz.date) + ' · ' + E.LEVELS[shownLevel(this.pz)]),
          h('p', null, `Reparte las 19 fichas en operaciones correctas de 3 a ${this.pz.rules.maxLen} fichas, sin repetir ninguna.`),
          this.archive ? h('p', { class: 'small muted' }, 'Reto de otro día: cuenta como completado, pero no para la racha ni para tu mejor tiempo.') : null,
          h('div', { class: 'relax-row' }, h('div', { class: 'lbl' }, h('b', null, 'Modo Relax'), h('small', null, 'Sin reloj; las pistas no suman tiempo')), sw),
          h('button', { class: 'btn primary big', onclick: () => { Sound.unlock(); Haptics.tick(0); this.start(relaxPick); this.ensureVisible(); } }, icon('play'), 'Empezar')]);
      } else {
        appendKids(card, [h('h2', null, 'En pausa'), h('p', null, 'El reloj está parado mientras no miras.'),
          h('button', { class: 'btn primary big', onclick: () => { this.st.paused = false; this.resume(); this.updateLock(); } }, icon('play'), 'Seguir')]);
      }
      ov.appendChild(card);
    }
    ov.hidden = !locked;
    this.board.el.classList.toggle('is-locked', locked);
    if (!locked) this.resume();
  }

  /* ---------- bocadillo y entrenador ---------- */
  onPath(p) {
    if (this.st.paused) return;
    if (p.length && !this.st.started && this.pz.kind !== 'daily') this.start();
    if (p.length > (this._lastLen || 0)) this.coach.calm();
    this._lastLen = p.length;
    this.updateExpr();
  }
  idleText() {
    if (this.st.solved && this.obj === 'close') return '¡Panal cerrado! Ahora, la caza';
    if (this.obj === 'close') return 'Traza una operación';
    return `Caza ${HUNT_NAMES[this.st.huntLevel].toLowerCase()} · ${huntSpanText(this.st.huntLevel)}`;
  }
  coachIdle() {
    if (this.st.solved && this.obj === 'close') return 'Sigue con la caza: encuentra todas las operaciones del panal.';
    if (this.obj === 'close') return 'Arrastra de ficha en ficha. El = va justo antes del resultado.';
    return `Encuentra todas las operaciones de ${huntSpanText(this.st.huntLevel)}.`;
  }
  updateExpr() {
    this.bubble.idle(this.idleText());
    this.coach.idle(this.coachIdle());
    const p = this.board.path;
    if (this.bubble.flashT) return;
    if (!p.length) { this.bubble.renderIdle(); return; }
    this.bubble.show(this.vals, p, { run: runningValue(this.vals, p) });
  }

  /* ---------- jugar ---------- */
  /* BYD-047: en la caza, una cuenta ya encontrada se pone gris antes de soltar (y la ficha final no se ilumina) */
  huntMode() { return this.obj === 'hunt' || (this.obj === 'close' && this.st.solved); }
  pathState(p) {
    if (!this.pz || !this.huntMode() || p.length < 3 || !this.board.endsWithEq(p)) return null;
    const r = E.evaluatePath(this.vals, p, 'classic');
    return r.ok && this.found.has(E.exprString(this.vals, p, r.ops)) ? 'dup' : null;
  }
  submit(path) {
    if (this.st.paused) return;
    const r = E.evaluatePath(this.vals, path, 'classic');
    if (!r.ok) return this.reject(path, r);
    if (this.huntMode() && this.found.has(E.exprString(this.vals, path, r.ops))) return this.repeated(path, r);
    if (r.xv != null) return this.askX(path, r);
    this.accept(path, r);
  }
  /* repetida: trazo gris, sonido neutro, aviso que se puede leer (≥ 2,5 s) y la cuenta parpadea en la lista; no puntúa */
  repeated(path, r) {
    const expr = E.exprString(this.vals, path, r.ops);
    Sound.thud(); Haptics.block();
    this.board.setPath(path, 'dup');
    this.bubble.flash(this.vals, path, false, { note: 'ya la tenías', ms: 1600, dup: true });
    this.coach.tell('Esa ya la tenías: no suma. Busca otra.', 'info', 2800);
    this._flashExpr = expr; this.renderSide();
    emitEvent('hunt_repeat', { reto: this.pz.number || null });
    setTimeout(() => { if (this.board.path.join() === path.join()) this.board.clearPath(); }, 700);
  }
  reject(path, r) {
    if (r.reason === 'xrange') {          // X fuera de 0–9: aviso propio (ámbar), sin sacudida ni fallo
      Sound.thud(); Haptics.block();
      this.board.setPath(path, 'ko');
      this.bubble.flash(this.vals, path, false, { note: `X tendría que ser ${TXT.minus(r.xNeed)}`, ms: 1600 });
      this.bubble.b.classList.add('is-warn');
      this.coach.tell(wrongMsg(this.vals, path, r), 'warn', 3000);
      setTimeout(() => { if (this.board.path.join() === path.join()) this.board.clearPath(); }, 700);
      return;
    }
    this.st.errors = (this.st.errors || 0) + 1;
    Sound.bad(); Haptics.bad();
    this.board.setPath(path, 'ko'); this.board.shake(path);
    this.bubble.flash(this.vals, path, false);
    this.coach.tell(wrongMsg(this.vals, path, r), 'bad', 3000);
    setTimeout(() => { if (this.board.path.join() === path.join()) this.board.clearPath(); }, 650);
    this.save();
  }
  /* ficha X: el jugador dice cuánto vale antes de colocar la operación */
  askX(path, r) {
    this.board.setPath(path, 'xpend');
    this.bubble.show(this.vals, path, { ask: 'X = ?' });
    this.coach.tell('¿Cuánto tiene que valer la X para que la operación sea cierta?', 'info', 0);
    // el reloj se para mientras se teclea la X
    const wasRunning = !!this.runStart; if (wasRunning) this.pause(false);
    const back = () => { if (wasRunning) this.resume(); };
    askX({
      expr: exprOf(this.vals, path), avoid: this.board.screenRect(path), board: this.board.svg.getBoundingClientRect(),
      onPick: d => {
        if (d === r.xv) { this.coach.stop(); back(); this.accept(path, r); return true; }
        this.st.errors = (this.st.errors || 0) + 1; this.save();
        Sound.bad(); Haptics.bad();
        this.coach.tell(`Con X = ${d} no sale. Prueba otro número.`, 'bad', 0);
        return false;
      },
      onCancel: () => { this.coach.stop(); back(); this.board.clearPath(); this.updateExpr(); }
    });
  }
  accept(path, r) {
    const expr = E.exprString(this.vals, path, r.ops);
    const inHunt = this.hunt.byExpr.has(expr);
    const isNew = inHunt && !this.found.has(expr);
    const hadLevelDone = this.levelProgress(this.st.huntLevel).done;
    if (isNew) this.found.add(expr);
    this.board.setPath(path, 'ok');
    const xNote = r.xv != null ? `X = ${r.xv}` : '';
    if (this.obj === 'close' && !this.st.solved) {
      this.pushUndo();
      const used = new Set(this.st.groups.map(g => g.color));
      const color = CONFIG.groupColors.find(c => !used.has(c)) || CONFIG.groupColors[this.st.groups.length % 6];
      const g = { path: path.slice(), color, id: 'g' + Date.now(), xv: r.xv };
      this.st.groups.push(g);
      this.syncOwner();
      setTimeout(() => { if (this.board.path.join() === path.join()) this.board.clearPath(); this.board.setGroups(this.st.groups, g.id); this.board.pop(path, 45); }, 160);
      Sound.good(); Haptics.place();
      const placed = this.st.groups.reduce((a, x) => a + x.path.length, 0);
      this.bubble.flash(this.vals, path, true, { xv: r.xv, note: xNote });
      this.coach.tell(`Operación colocada · ${placed}/19 fichas` + (isNew ? ` · +${TXT.points(E.points(path.length))} en la caza` : ''), 'good', 1800);
      this.board.setResultMarks([]);
      if (this.placedMask === E.FULL) setTimeout(() => this.win(), 380);
    } else {
      setTimeout(() => { if (this.board.path.join() === path.join()) this.board.clearPath(); this.board.pop(path, 45); }, 160);
      this.bubble.flash(this.vals, path, true, { xv: r.xv, note: xNote });
      if (isNew) {
        Sound.found(); Haptics.ok();
        const lvl = Math.max(3, path.length);
        const ahead = lvl > this.st.huntLevel ? ` · cuenta para «${HUNT_NAMES[lvl]}»` : '';
        this.coach.tell(`¡Nueva! +${TXT.points(E.points(path.length))}${ahead}`, 'good', 1800);
        this.flashCell(this.hunt.byExpr.get(expr));
      } else { Sound.good(); Haptics.ok(); this.coach.tell(inHunt ? 'Correcta, pero ya la tenías.' : 'Correcta.', 'info', 1600); }
    }
    // nivel de caza completado → celebración y siguiente nivel (aviso en el entrenador: no tapa nada)
    const prog = this.levelProgress(this.st.huntLevel);
    if (isNew && prog.done && !hadLevelDone) {
      const lv = this.st.huntLevel, next = E.HUNT_LEVELS.find(l => l > lv && !this.levelProgress(l).done);
      setTimeout(() => {
        this.coach.tell(next ? `¡Caza ${HUNT_NAMES[lv].toLowerCase()} completada! Ahora, ${huntSpanText(next)}.` : '¡Has cazado todas las operaciones del panal!', 'good', 3600);
        Sound.win(); Haptics.win();
        if (!next) { const [x, y] = this.board.screenPoint(9); confetti(x, y, 150); }
        if (next) { this.st.huntLevel = next; this.save(); this.renderSide(); this.updateExpr(); }
      }, 700);
    }
    this.save();
    this.updateCounts();
    this.renderSide();
    if (isNew && this.pz.kind === 'daily' && typeof App !== 'undefined' && App.queueHunt) App.queueHunt(this.pz.date, this.huntPoints());
  }
  /* BYD-039: tocar una operación colocada ofrece «Quitar»; arrastrar desde ella no la quita */
  askRemove(c, how) {
    if (this.obj !== 'close' || this.st.solved || this.st.paused) return;
    const k = this.owner[c]; if (k == null) return;
    const g = this.st.groups[k];
    placedMenu({ board: this.board, cells: g.path, how, label: exprOf(this.vals, g.path) + (g.xv != null ? `  (X = ${g.xv})` : ''), onRemove: () => this.removeGroupAt(g.path[0]) });
  }
  placedNotice(c) {
    const k = this.owner[c]; if (k == null) return;
    this.board.setRing(this.st.groups[k].path, 'is-picked');
    setTimeout(() => this.board.setRing([], 'is-picked'), 900);
    this.coach.tell('Esa operación ya está colocada. Tócala si quieres quitarla.', 'info', 2200);
  }
  removeGroupAt(c) {
    if (this.obj !== 'close' || this.st.solved) return;
    const k = this.owner[c]; if (k == null) return;
    this.pushUndo();
    const g = this.st.groups.splice(k, 1)[0];
    this.syncOwner();
    this.board.setGroups(this.st.groups);
    this.board.pop(g.path, 0);
    Sound.remove(); Haptics.remove();
    this.save(); this.renderSide();
    toast('Operación retirada', { action: { label: TXT.undo, fn: () => this.undo() } });
  }
  pushUndo() { this.st.undo = (this.st.undo || []).concat([JSON.stringify(this.st.groups)]).slice(-30); }
  undo() {
    if (this.board.path.length) { this.board.clearPath(); return; }
    if (this.st.solved || !this.st.undo || !this.st.undo.length) { this.coach.tell('No hay nada que deshacer.', 'info', 1600); return; }
    this.st.groups = JSON.parse(this.st.undo.pop());
    this.syncOwner(); this.board.setGroups(this.obj === 'close' ? this.st.groups : []);
    Sound.back(); Haptics.back(); this.save(); this.renderSide();
  }
  restart() {
    if (this.st.solved) { this.coach.tell('Este panal ya está cerrado. ¡Sigue con la caza!', 'info', 2400); return; }
    if (!this.st.groups.length) { this.board.clearPath(); return; }
    this.pushUndo();
    this.st.groups = []; this.syncOwner(); this.board.setGroups([]); this.board.clearPath(); this.board.setResultMarks([]);
    this.save(); this.renderSide();
    toast('Panal vacío', { action: { label: TXT.undo, fn: () => this.undo() } });
  }
  win() {
    this.pause(false);
    this.st.solved = true;
    this.st.finalMs = this.relax ? 0 : this.st.elapsed + this.st.penalty;
    this.st.solvedAt = new Date().toISOString();
    this.save(true); this.tick();
    Sound.win(); Haptics.win();
    const order = []; this.st.groups.forEach(g => order.push(...g.path));
    this.board.pop(order, 40);
    const [x, y] = this.board.screenPoint(9);
    setTimeout(() => confetti(x, y, 140), 250);
    if (this.pz.kind === 'daily') {
      Stats.recordWin(this.pz, this.st, { archive: this.archive });
      if (typeof App !== 'undefined' && App.syncStreak) App.syncStreak();
      emitEvent('daily_solved', { reto: this.pz.number, ms: this.relax ? null : this.st.finalMs, hints: this.st.hints.length, relax: this.relax, archive: this.archive });
      emitEvent('solved', { reto: this.pz.number, ms: this.st.finalMs, hints: this.st.hints.length });
    }
    this.renderSide(); this.updateExpr();
    setTimeout(() => UI.openWin(this), 1100);
  }

  /* ---------- pistas ---------- */
  compatible() { return E.perfect(this.ops, E.FULL, { fixed: this.placedMask, limit: 40, cap: 5000 }); }
  opForMask(m) { const ks = this.pz.analysis.perfect.byMask.get(m); return ks ? this.ops[ks[0]] : this.ops.find(o => o.mask === m); }
  conflictText() {
    for (let k = 0; k < this.st.groups.length; k++) {
      const rest = this.st.groups.filter((_, j) => j !== k).reduce((m, g) => m | E.maskOf(g.path), 0);
      if (E.perfect(this.ops, E.FULL, { fixed: rest, limit: 1, cap: 1 }).count) {
        const g = this.st.groups[k];
        this.board.setRing(g.path, 'is-dead'); setTimeout(() => this.board.setRing([], 'is-dead'), 2600);
        return { text: `No encaja: ${exprOf(this.vals, g.path)}. Quítala y sigue.`, bad: true };
      }
    }
    return { text: 'Varias operaciones no encajan. Prueba a reiniciar.', bad: true };
  }
  cost(id) { return this.relax ? 0 : CONFIG.penalties[id]; }
  useHint(id) {
    if (this.st.solved || this.obj !== 'close') return;
    if (!this.st.started) this.start();
    const pf = this.compatible();
    let out, charge = id;
    if (!pf.count) { out = this.conflictText(); charge = 'check'; }
    else if (id === 'check') {
      out = { text: this.st.groups.length ? `Vas bien: tus ${plural(this.st.groups.length, 'operación encaja', 'operaciones encajan')} en una solución.` : 'Aún no has colocado ninguna operación: ¡todo encaja!' };
    } else if (id === 'structure') {
      const sol = pf.sols[0];
      const all = this.st.groups.map(g => g.path.length).concat(sol.map(m => E.popcount(m))).sort((a, b) => b - a);
      const P = this.pz.analysis.perfect.count;
      out = { text: `${P > 1 ? `Hay ${P} formas de cerrar este panal. Una de ellas usa` : 'La solución usa'} ${TXT.opSizes(all)}.` };
    } else if (id === 'results') {
      const res = pf.sols[0].map(m => { const o = this.opForMask(m); return o.path[o.path.length - 1]; });
      this.board.setResultMarks(res);
      out = { text: `${TXT.marked(res.length)} en las operaciones que faltan.` };
    } else if (id === 'reveal') {
      const sol = pf.sols[0];
      let bestM = sol[0], bestN = 1e9;
      for (const m of sol) for (const c of E.cellsOf(m)) {
        const n = this.ops.filter(o => (o.mask >>> c & 1) && !(o.mask & this.placedMask)).length;
        if (n < bestN) { bestN = n; bestM = m; }
      }
      const o = this.opForMask(bestM);
      this.st.penalty += this.cost('reveal') * 1000; this.st.hints.push('reveal');
      this.flashTimer();
      this.hintOut = { text: `Revelada: ${o.expr}` };
      this.save();
      const r = E.evaluatePath(this.vals, o.path, 'classic');
      if (r.ok) this.accept(o.path.slice(), r);
      return;
    }
    this.st.penalty += this.cost(charge) * 1000; this.st.hints.push(charge);
    this.flashTimer();
    this.hintOut = out; this.save(); this.renderSide(); this.tick();
  }
  flashTimer() { if (!this.relax && this.timerEl && this.timerEl.animate) this.timerEl.animate([{ background: '#FF7A66', color: '#fff' }, {}], { duration: 900 }); }

  /* ---------- caza: niveles por número de fichas ---------- */
  levelList(L) { return this.hunt.list.filter(o => o.len <= L); }
  levelProgress(L) { const list = this.levelList(L); const got = list.filter(o => this.found.has(o.expr)).length; return { got, total: list.length, done: list.length > 0 && got === list.length }; }
  firstOpenLevel() { return E.HUNT_LEVELS.find(l => !this.levelProgress(l).done) || 6; }
  huntPoints() { let p = 0; for (const e of this.found) { const o = this.hunt.byExpr.get(e); if (o) p += E.points(o.len); } return p; }
  /* BYD-038: una sola definición visible. El selector y el panel enseñan el nivel de caza
     con su nombre («fácil 1/4»); el resultado y el texto para compartir, el nivel y el total. */
  huntLine() {
    const L = this.st.huntLevel, pr = this.levelProgress(L), all = this.found.size, tot = this.hunt.list.length;
    return L >= 6 || pr.total === tot ? `Caza ${all}/${tot} en total` : `Caza ${HUNT_NAMES[L].toLowerCase()} ${pr.got}/${pr.total} · ${all}/${tot} en total`;
  }
  updateCounts() {
    if (!this.huntCount) return;
    const L = this.st.huntLevel, pr = this.levelProgress(L);
    this.huntCount.textContent = `${HUNT_NAMES[L].toLowerCase()} ${pr.got}/${pr.total}`;
    if (this.objBtns && this.objBtns.hunt) this.objBtns.hunt.setAttribute('aria-label', `Caza ${HUNT_NAMES[L].toLowerCase()}: ${pr.got} de ${pr.total}. En total, ${this.found.size} de ${this.hunt.list.length}.`);
  }
  flashCell(o) { this._flashKey = o ? o.result + '-' + o.len : null; }

  renderSide() {
    const side = this.side; if (!side) return;
    side.innerHTML = '';
    if (this.st.solved && this.pz.analysis.perfect.count) side.appendChild(this.resultCard());
    if (this.obj === 'close') { side.appendChild(this.groupsCard()); if (!this.st.solved) side.appendChild(this.hintsCard()); }
    else { side.appendChild(this.huntCard()); side.appendChild(this.huntHintsCard()); side.appendChild(this.foundCard()); }
    if (UI.promoCard) { const p = UI.promoCard(this); if (p) side.appendChild(p); }
    if (this.pz.kind === 'daily') {
      const row = h('div', { class: 'toolbar left' });
      const y = addDays(this.pz.date, -1);
      if (E.dayNumber(y) >= 1) row.appendChild(h('button', { class: 'btn small', onclick: () => UI.openSolutions(y) }, icon('eye'), `Soluciones de ayer (${retoLabel(E.dayNumber(y)).toLowerCase()})`));
      if (UI.openTable) row.appendChild(h('button', { class: 'btn small', onclick: () => UI.openTable(this.pz) }, icon('hex'), 'Móntalo en la mesa'));
      if (row.children.length) side.appendChild(row);
    }
    this.updateCounts();
  }
  resultCard() {
    const isDaily = this.pz.kind === 'daily';
    return h('div', { class: 'card' },
      h('h3', null, icon('award'), 'Panal cerrado'),
      h('div', { class: 'result-row' },
        h('span', { class: 'result-time' }, this.relax ? 'Relax' : fmtTime(this.st.finalMs)),
        h('span', { class: 'muted small' }, this.st.hints.length ? `con ${TXT.hints(this.st.hints.length)}` + (this.relax ? '' : ` (+${Math.round(this.st.penalty / 1000)} s)`) : 'sin pistas')),
      isDaily ? h('p', { class: 'small muted', style: { margin: '6px 0 0' } }, 'Próximo reto en ', liveCountdown()) : null,
      h('div', { class: 'toolbar left', style: { marginTop: '10px' } },
        h('button', { class: 'btn small primary', onclick: () => UI.openWin(this) }, icon(isDaily ? 'award' : 'share'), isDaily ? 'Tu resultado' : 'Compartir'),
        this.pz.kind !== 'daily' && typeof App !== 'undefined' && App.newPractice ? h('button', { class: 'btn small', onclick: () => App.newPractice(this.pz.level || 2) }, icon('shuffle'), 'Otro panal') : null));
  }
  groupsCard() {
    const placed = this.st.groups.reduce((a, g) => a + g.path.length, 0);
    const list = h('ul', { class: 'oplist' });
    this.st.groups.forEach(g => {
      list.appendChild(h('li', null, h('span', { class: 'dot', style: { background: g.color } }), h('span', { class: 'ex' }, exprOf(this.vals, g.path) + (g.xv != null ? `  (X = ${g.xv})` : '')),
        this.st.solved ? null : h('button', { 'aria-label': 'Quitar operación', title: 'Quitar', onclick: () => this.removeGroupAt(g.path[0]) }, icon('x'))));
    });
    return h('div', { class: 'card' },
      h('h3', null, h('span', { class: 'grow' }, 'Tus operaciones'), h('span', null, placed + '/19 fichas')),
      this.st.groups.length ? list : h('p', { class: 'empty-note' }, `Cada operación correcta (de 3 a ${this.pz.rules.maxLen} fichas) se queda fija en el panal. Para quitar una, tócala y pulsa «Quitar».`));
  }
  hintsCard() {
    const card = h('div', { class: 'card' }, h('h3', null, icon('bulb'), 'Pistas'));
    const list = h('div', { class: 'hint-list' });
    for (const ht of HINTS) list.appendChild(h('button', { class: 'hint-btn', onclick: () => this.useHint(ht.id) },
      h('span', null, h('span', { class: 't' }, ht.t), h('br'), h('span', { class: 'd' }, ht.d)),
      h('span', { class: 'cost' }, this.relax ? 'gratis' : '+' + CONFIG.penalties[ht.id] + ' s')));
    card.appendChild(list);
    if (this.hintOut) card.appendChild(h('div', { class: 'hint-out' + (this.hintOut.bad ? ' bad' : '') }, this.hintOut.text));
    if (this.st.hints.length && !this.relax) card.appendChild(h('p', { class: 'muted small', style: { margin: '10px 0 0' } }, `Llevas ${TXT.hints(this.st.hints.length)}: +${Math.round(this.st.penalty / 1000)} s en tu tiempo.`));
    return card;
  }
  huntCard() {
    const L = this.st.huntLevel, pr = this.levelProgress(L);
    const chips = h('div', { class: 'hunt-levels', role: 'group', 'aria-label': 'Nivel de caza' });
    for (const l of E.HUNT_LEVELS) {
      const p = this.levelProgress(l);
      chips.appendChild(h('button', { class: 'hl' + (p.done ? ' done' : ''), 'aria-pressed': String(l === L), onclick: () => { this.st.huntLevel = l; this.save(); this.renderSide(); this.updateExpr(); } },
        h('b', null, HUNT_NAMES[l]), h('span', null, HUNT_SPAN[l]), h('span', { class: 'prog' }, p.done ? icon('check') : `${p.got}/${p.total}`)));
    }
    return h('div', { class: 'card' },
      h('div', { class: 'progress' },
        h('div', { class: 'top' }, h('span', { class: 'rank' }, 'Caza ' + HUNT_NAMES[L].toLowerCase()), h('span', { class: 'pts' }, TXT.points(this.huntPoints()))),
        h('div', { class: 'bar' }, h('div', { class: 'fill', style: { width: (pr.total ? pr.got / pr.total * 100 : 0).toFixed(1) + '%' } })),
        h('div', { class: 'small muted' }, pr.done ? '¡Nivel completado! Elige otro nivel.' : `Caza ${HUNT_NAMES[L].toLowerCase()}: ${pr.got} de ${TXT.ops(pr.total)} de ${huntSpanText(L)}`),
        h('div', { class: 'small hunt-total' }, `En total: ${this.found.size} de ${TXT.ops(this.hunt.list.length)} del panal (hasta 6 fichas)`)),
      chips,
      h('p', { class: 'small muted', style: { margin: '10px 0 0' } }, 'Cada operación cuenta una vez aunque aparezca en dos sitios del panal. ' + TXT.huntPoints));
  }
  huntHintsCard() {
    const L = this.st.huntLevel, lens = E.HUNT_LEVELS.filter(l => l <= L);
    const total = {}, got = {};
    for (const o of this.levelList(L)) { const k = o.result + '-' + o.len; total[k] = (total[k] || 0) + 1; if (this.found.has(o.expr)) got[k] = (got[k] || 0) + 1; }
    const results = Array.from(new Set(this.levelList(L).map(o => o.result))).sort((a, b) => a - b);
    const tbl = h('table', { class: 'hints' });
    tbl.appendChild(h('tr', null, h('th', null, 'Resultado'), lens.map(l => h('th', null, l + ' fichas'))));
    const flashKey = this._flashKey; this._flashKey = null;
    for (const r of results) {
      tbl.appendChild(h('tr', null, h('th', null, '= ' + r), lens.map(l => {
        const k = r + '-' + l, t = total[k] || 0, g = got[k] || 0;
        const cls = (!t ? 'zero' : t === g ? 'done' : g ? 'part' : '') + (k === flashKey ? ' flash' : '');
        return h('td', { class: cls, title: t ? TXT.found(g, t) : '' }, !t ? '·' : t === g ? icon('check') : `${g}/${t}`);
      })));
    }
    return h('div', { class: 'card' }, h('h3', null, icon('bulb'), 'Pistas de la caza'),
      h('p', { class: 'small muted', style: { margin: '0 0 8px' } }, 'Encontradas / total, según el resultado y el número de fichas.'),
      results.length ? h('div', { class: 'table-scroll' }, tbl) : h('p', { class: 'empty-note' }, 'Este panal no tiene operaciones de este tamaño.'));
  }
  foundCard() {
    const flashExpr = this._flashExpr; this._flashExpr = null;
    const card = h('div', { class: 'card' }, h('h3', null, h('span', { class: 'grow' }, 'Encontradas'), h('span', null, String(this.found.size))));
    if (!this.found.size) { card.appendChild(h('p', { class: 'empty-note' }, 'Traza operaciones en el panal: aquí aparecerán agrupadas por resultado.')); return card; }
    const byRes = new Map();
    for (const e of this.found) { const o = this.hunt.byExpr.get(e); if (!o) continue; if (!byRes.has(o.result)) byRes.set(o.result, []); byRes.get(o.result).push(o); }
    const wrap = h('div', { class: 'found-groups' });
    Array.from(byRes.keys()).sort((a, b) => a - b).forEach(r => {
      const chips = h('div', { class: 'chips' });
      byRes.get(r).sort((a, b) => a.len - b.len || (a.expr < b.expr ? -1 : 1)).forEach(o => chips.appendChild(h('span', { class: 'chip' + (o.len > this.st.huntLevel ? ' ahead' : '') + (o.expr === flashExpr ? ' blink' : '') }, o.expr)));
      wrap.appendChild(h('div', { class: 'found-group' }, h('div', { class: 'lbl' }, 'Resultado ' + r), chips));
    });
    card.appendChild(wrap);
    return card;
  }
}

/* ---------- utilidades compartidas con la app ---------- */
function runningValue(vals, p) {
  if (p.some(c => vals[c] === E.X)) return null;
  let v = vals[p[0]];
  for (let k = 1; k < p.length; k++) { const op = E.opBetween[p[k - 1]][p[k]]; if (op === E.EQ) return null; v = op === E.PLUS ? v + vals[p[k]] : v - vals[p[k]]; }
  return v;
}
function wrongMsg(vals, path, r) {
  if (r.reason === 'wrong') {
    const left = E.exprString(vals, path.slice(0, r.e + 1), r.ops.slice(0, r.e));
    return `${left} da ${TXT.minus(r.left)}, no ${r.right}.`;
  }
  if (r.reason === 'xrange') return `La X tendría que valer ${TXT.minus(r.xNeed)}, y solo puede ir de 0 a 9.`;
  if (r.reason === 'manyx') return 'Solo puede haber una X en cada operación.';
  if (r.reason === 'short') return 'Mínimo tres fichas: a + b = c.';
  return 'Esa operación no es correcta.';
}
/* cuenta atrás hasta el próximo reto; al llegar a 0 avisa a la app (cambio de día) */
function liveCountdown() {
  const c = h('span', { class: 'countdown' }, fmtCountdown(msToMadridMidnight()));
  let fired = false;
  const it = setInterval(() => {
    if (!c.isConnected) return clearInterval(it);
    const ms = msToMadridMidnight();
    c.textContent = fmtCountdown(ms);
    if (ms <= 1000 && !fired) { fired = true; setTimeout(() => { if (typeof App !== 'undefined' && App.checkDay) App.checkDay(); }, 1600); }
    if (ms > 60000) fired = false;
  }, 1000);
  return c;
}

/* ======================================================================
   stats.js — estadísticas del reto del día (en este dispositivo)
   Racha con 48 h de margen: un día sin jugar no la rompe; dos seguidos, sí.
   Retos de otros días (calendario) cuentan como completados, no para la racha.
   Modo Relax: cuenta para la racha, no para los tiempos.
   ====================================================================== */
const dayDiff = (a, b) => Math.round((Date.UTC(...b.split('-').map((x, i) => i === 1 ? x - 1 : +x)) - Date.UTC(...a.split('-').map((x, i) => i === 1 ? x - 1 : +x))) / 86400000);
const Stats = {
  get() { return Object.assign({ solved: 0, streak: 0, maxStreak: 0, lastSolved: null, best: null, history: {}, started: {} }, Store.get('stats', {})); },
  markStarted(date) { const s0 = this.get(); if (!s0.started[date]) { s0.started[date] = 1; Store.setNow('stats', s0); } },
  recordWin(pz, st, opts) {
    opts = opts || {};
    const s0 = this.get();
    s0.started[pz.date] = 1;
    if (!s0.history[pz.date]) {
      const rec = { ms: st.relax ? null : st.finalMs, hints: st.hints.length, n: pz.number };
      if (st.relax) rec.relax = 1;
      if (opts.archive) rec.archive = 1;
      s0.history[pz.date] = rec;
      s0.solved++;
      if (!opts.archive && !st.relax) s0.best = s0.best == null ? st.finalMs : Math.min(s0.best, st.finalMs);
      if (!opts.archive) s0.lastSolved = s0.lastSolved && s0.lastSolved > pz.date ? s0.lastSolved : pz.date;
      s0.streak = this.chain(s0.history, todayStr());
      s0.maxStreak = Math.max(s0.maxStreak || 0, s0.streak);
      Store.setNow('stats', s0);
    }
    if (Stats.onWin) Stats.onWin(pz, st, opts);          // la versión con ranking compartido envía aquí el tiempo
  },
  days(history) { return Object.keys(history).filter(d => !history[d].archive).sort(); },
  /* longitud de la cadena que termina en el último día jugado (≤ upTo) */
  chain(history, upTo) {
    const days = this.days(history).filter(d => d <= upTo);
    if (!days.length) return 0;
    let n = 1;
    for (let k = days.length - 1; k > 0; k--) { if (dayDiff(days[k - 1], days[k]) <= 2) n++; else break; }
    return n;
  },
  streakNow() {
    const s0 = this.get(), t = todayStr(), days = this.days(s0.history);
    if (!days.length || dayDiff(days[days.length - 1], t) > 2) return 0;
    return this.chain(s0.history, t);
  },
  /* tiempos válidos (sin Relax ni retos de otros días) */
  times() { const hst = this.get().history; return Object.keys(hst).filter(d => hst[d].ms != null && !hst[d].archive).sort().map(d => Object.assign({ date: d }, hst[d])); }
};
function solutionString(pz, st) { return st.groups.map(g => g.path.join('.')).join('|'); }

/* ======================================================================
   ranking.js — ranking compartido del reto (versión interna y web)
   ====================================================================== */
/* ---------- Capacidades del visor de Claude (opcionales) ---------- */
const Caps = {
  _p: {},
  use(name) {
    if (!this._p[name]) this._p[name] = (window.claude && typeof window.claude.use === 'function') ? window.claude.use(name).catch(() => null) : Promise.resolve(null);
    return this._p[name];
  }
};

/* ---------- Ranking --------------------------------------------------------
   'artifact': base de datos del artifact de Claude (versión interna)
   'supabase': tabla retos_scores en Supabase (versión web; ver README)
   Sin proveedor: solo tus tiempos en este dispositivo.
   Una sola entrada por persona y día: la primera (el reto se juega una vez).
------------------------------------------------------------------------- */
const Ranking = {
  provider: null, auto: false, ready: Promise.resolve(false), lastWrite: null,
  init() {
    if (CONFIG.supabaseUrl && CONFIG.supabaseKey) {
      this.provider = 'supabase'; this.auto = false; this.ready = Promise.resolve(true);
    } else if (TARGET === 'internal') {
      this.provider = 'artifact'; this.auto = true;
      this.ready = (async () => {
        const [db, user] = await Promise.all([Caps.use('db'), Caps.use('user')]);
        let uid = null;
        try { uid = user ? await user.id() : null; } catch (e) { uid = null; }
        Object.assign(this, { db, user, uid });
        return !!(db && uid);
      })();
    }
    return this.ready;
  },
  playerId() {
    let id = Store.get('pid');
    if (!id) { id = (crypto.randomUUID ? crypto.randomUUID() : 'xxxxxxxx-xxxx-4xxx-8xxx-xxxxxxxxxxxx'.replace(/x/g, () => (Math.random() * 16 | 0).toString(16))); Store.set('pid', id); }
    return id;
  },
  f(prefix, date) { return prefix + date.replace(/-/g, ''); },
  submittedFor(date) { return !!(Store.get('submitted', {})[date]); },
  markSubmitted(date, info) { const m = Store.get('submitted', {}); m[date] = info || 1; Store.set('submitted', m); },
  /* código privado de jugador: solo lo ve esta persona; sirve para reclamar un premio */
  code() { return this.playerId().replace(/-/g, '').slice(0, 6).toUpperCase(); },
  async submit(date, ms, hints, solution) {
    if (!(await this.ready)) return false;
    if (this.provider === 'supabase') {
      const nick = (Settings.get().alias || '').trim().slice(0, 24);
      if (!nick) return false;
      try {
        const r = await fetch(CONFIG.supabaseUrl.replace(/\/$/, '') + '/rest/v1/retos_scores', {
          method: 'POST', headers: this.sbHeaders({ 'Content-Type': 'application/json', Prefer: 'return=minimal' }),
          body: JSON.stringify({ day: date, player_id: this.playerId(), nickname: nick, ms: Math.round(ms), hints, solution: solution || '' })
        });
        const ok = r.ok || r.status === 409;
        if (ok) this.markSubmitted(date, { nick, ms: Math.round(ms) });
        this.lastWrite = ok ? 'ok' : 'denied';
        return ok;
      } catch (e) { this.lastWrite = 'denied'; return false; }
    }
    // artifact: un documento por persona; solo el primer tiempo de cada día
    try {
      const ref = this.db.doc('scores/' + this.uid);
      const snap = await ref.get();
      const t = this.f('t', date), hk = this.f('h', date);
      const data = { [t]: Math.round(ms), [hk]: hints, updated: Date.now() };
      const alias = (Settings.get().alias || '').trim(); if (alias) data.alias = alias.slice(0, 24);
      if (snap.exists) { const cur = snap.data() || {}; if (cur[t] != null) { this.lastWrite = 'ok'; return true; } await ref.update(data); }
      else await ref.set(data);
      this.markSubmitted(date); this.lastWrite = 'ok';
      return true;
    } catch (e) { this.lastWrite = 'denied'; return false; }
  },
  async submitHunt(date, pts) {
    if (this.provider !== 'artifact' || !(await this.ready)) return false;
    try {
      const ref = this.db.doc('scores/' + this.uid); const snap = await ref.get(); const p = this.f('p', date);
      if (snap.exists) { const cur = snap.data() || {}; if ((cur[p] || 0) >= pts) return true; await ref.update({ [p]: pts }); } else await ref.set({ [p]: pts });
      return true;
    } catch (e) { return false; }
  },
  sbHeaders(extra) { return Object.assign({ apikey: CONFIG.supabaseKey, Authorization: 'Bearer ' + CONFIG.supabaseKey }, extra || {}); },
  async top(date, kind) {
    if (!(await this.ready)) return null;
    if (this.provider === 'supabase') {
      if (kind === 'hunt') return null;
      try {
        // el identificador de cada jugador no es público: solo se leen apodo, tiempo y pistas
        const u = CONFIG.supabaseUrl.replace(/\/$/, '') + `/rest/v1/retos_scores?select=nickname,ms,hints,created_at&day=eq.${date}&order=ms.asc,created_at.asc&limit=100`;
        const res = await fetch(u, { headers: this.sbHeaders() });
        if (!res.ok) return null;
        const rows = await res.json();
        const mine = Store.get('submitted', {})[date];
        let meDone = false;
        return rows.map((r, k) => {
          const me = !meDone && mine && typeof mine === 'object' && r.nickname === mine.nick && r.ms === mine.ms;
          if (me) meDone = true;
          return { id: 'r' + k, v: r.ms, hints: r.hints, name: r.nickname, me };
        });
      } catch (e) { return null; }
    }
    const field = this.f(kind === 'hunt' ? 'p' : 't', date), hk = this.f('h', date);
    try {
      const snap = await this.db.collection('scores').where(field, '>', 0).orderBy(field, kind === 'hunt' ? 'desc' : 'asc').limit(100).get();
      const rows = snap.docs.map(d => { const x = d.data() || {}; return { id: d.id, v: x[field], hints: x[hk] || 0, alias: x.alias || '' }; });
      let prof = {};
      try { prof = rows.length ? await this.user.profiles(rows.map(r => r.id)) : {}; } catch (e) { prof = {}; }
      rows.forEach(r => { const p = prof[r.id]; r.name = (p && p.name) || r.alias || 'Jugador/a'; r.avatar = p ? p.avatarUrl : ''; r.me = r.id === this.uid; });
      return rows;
    } catch (e) { return null; }
  }
};

/* ======================================================================
   ui.js — ventanas comunes: cómo se juega (con la leyenda de operadores),
   estadísticas, ajustes, soluciones, «Móntalo en la mesa» y resultado del reto
   ====================================================================== */
const UI = {
  /* ----- Cómo se juega ----- */
  openHelp(extra) {
    openModal({
      title: 'Cómo se juega',
      render(body, api) {
        const X = 1.5 * E.RAD;
        const legend = (cells, path, ops, name, how, sample) => h('div', { class: 'legend-row' },
          miniBoard(cells, { path, ops, glow: true, noTrail: true, noBadges: true, cls: 'pair' }),
          h('div', null, h('b', null, name), h('span', { class: 'small muted' }, how), h('span', { class: 'sample' }, sample)));
        const step2 = miniBoard([{ pos: [0, 0], v: 6 }, { pos: [X, 50], v: 3 }, { pos: [2 * X, 0], v: 1 }, { pos: [3 * X, -50], v: 5 }, { pos: [3 * X, 50], v: 9 }], { path: [0, 1, 2, 3, 4], ops: [1, 0, 0, 2] });
        const logo = s('svg', { viewBox: '-62 -56 124 112', class: 'mini' }); logo.appendChild(s('use', { href: '#bc-logo-mark' }));
        const isApp = TARGET === 'app';
        appendKids(body, [
          h('section', null, h('h4', null, 'Las juntas forman los signos'),
            h('p', { class: 'small' }, 'Al juntar dos fichas, sus ranuras forman un signo. Igual que en la mesa: ' + TXT.ops3 + '.'),
            h('div', { class: 'legend' },
              legend([{ pos: [0, 0], v: 3 }, { pos: [X, -50], v: 4 }], [0, 1], [0], 'Suma', 'Junta en diagonal ↗', '3 + 4'),
              legend([{ pos: [0, 0], v: 9 }, { pos: [X, 50], v: 6 }], [0, 1], [1], 'Resta', 'Junta en diagonal ↘', '9 − 6'),
              legend([{ pos: [0, 0], v: 6 }, { pos: [0, 100], v: 3 }], [0, 1], [2], 'Igual', 'Junta vertical, justo antes del resultado', '… = 3'))),
          h('div', { class: 'help-steps' },
            h('div', { class: 'help-step' }, step2, h('div', null, h('b', null, 'Traza una operación'), h('p', null, 'Arrastra de ficha en ficha (o tócalas una a una). El = va justo antes del resultado: 6 − 3 + 1 + 5 = 9. Sin repetir fichas.'))),
            h('div', { class: 'help-step' }, logo, h('div', null, h('b', null, 'Cierra el panal'), h('p', null, 'Reparte todas las fichas en operaciones correctas, usando cada ficha una sola vez. Cada nivel y cada reto dicen cuántas fichas puede tener una operación (de 3 a 5).')))),
          isApp ? h('section', null, h('h4', null, 'En los niveles'),
            h('p', null, 'Cada nivel dice al empezar, y en la cabecera, cómo se ganan sus estrellas. En Cierra el panal (desde el nivel 4): ★★ si lo cierras sin deshacer ni quitar ninguna operación, y ★★★ si además es al primer intento. En la Caza, por puntos; en la Ciudad, por movimientos. En los niveles 1 a 3, ★★★ sin pistas y como mucho 1 fallo.'),
            h('p', null, 'Las pistas y las ayudas no quitan estrellas. Equivocarse no hace perder: se pierde si se acaba el tiempo o los movimientos.')) : null,
          h('section', null, h('h4', null, TXT.daily),
            h('p', null, 'Un reto nuevo cada medianoche (hora de Madrid), igual para todo el mundo. Las pistas suman segundos a tu tiempo; en modo Relax no hay reloj. Los lunes y martes son más amables; el fin de semana, más difíciles.'),
            h('p', null, 'Racha: si un día no juegas, la racha aguanta; si pasan dos días seguidos sin jugar, empieza de cero.')),
          h('section', null, h('h4', null, 'Modo Caza (dentro del reto del día)'),
            h('p', null, 'Encuentra todas las operaciones del panal por niveles: fácil (3 fichas), media (hasta 4), difícil (hasta 5) y experta (hasta 6). Cada operación cuenta una vez aunque aparezca en dos sitios. ' + TXT.huntPoints)),
          extra || null,
          h('section', null, h('h4', null, 'Detalles'),
            h('p', null, 'Sin paréntesis: se calcula de izquierda a derecha, como con las fichas, y por el camino se puede pasar por debajo de 0 (2 − 5 + 4 = 1). Toca la última ficha del trazo para quitarla. Para liberar una operación colocada, tócala y pulsa «Quitar» (arrastrar sobre ella no la quita); «Deshacer» la recupera.')),
          h('div', { class: 'toolbar modal-cta' }, h('button', { class: 'btn primary', onclick: () => api.close() }, '¡A jugar!'))]);
      }
    });
    Settings.set({ seenHelp: true });
  },

  /* ----- Estadísticas ----- */
  openStats(tab) {
    openModal({
      title: 'Estadísticas',
      render: body => {
        const st = Stats.get(), t = todayStr();
        const played = Object.keys(st.started).length;
        const times = Stats.times().map(x => x.ms);
        const avg = times.length ? times.reduce((a, b) => a + b, 0) / times.length : 0;
        const best = times.length ? Math.min(...times) : null;
        body.appendChild(h('div', { class: 'stat-grid' },
          h('div', { class: 'stat' }, h('b', null, String(played)), h('span', null, 'Jugados')),
          h('div', { class: 'stat' }, h('b', null, String(st.solved)), h('span', null, 'Cerrados')),
          h('div', { class: 'stat streak' }, h('b', null, '🔥 ' + Stats.streakNow()), h('span', null, 'Racha')),
          h('div', { class: 'stat' }, h('b', null, String(Math.max(st.maxStreak || 0, Stats.streakNow()))), h('span', null, 'Mejor racha'))));
        body.appendChild(h('section', null, h('h4', null, 'Últimos 7 días'), UI.week(st, t)));
        appendKids(body, [h('section', null, h('h4', null, 'Tus tiempos'),
          h('div', { class: 'stat-grid two' },
            h('div', { class: 'stat' }, h('b', null, best != null ? fmtTime(best) : '—'), h('span', null, 'Mejor')),
            h('div', { class: 'stat' }, h('b', null, times.length ? fmtTime(avg) : '—'), h('span', null, 'Media'))),
          times.length ? UI.histogram(times, st.history[t] && st.history[t].ms) : h('p', { class: 'small muted', style: { marginTop: '8px' } }, 'Cuando cierres retos con reloj, aquí verás tus tiempos.'))]);
        const extra = UI.statsExtra ? UI.statsExtra(t, tab) : null; if (extra) body.appendChild(extra);
      }
    });
  },
  week(st, t) {
    const week = h('div', { class: 'week' });
    for (let k = 6; k >= 0; k--) {
      const d = addDays(t, -k), rec = st.history[d];
      week.appendChild(h('div', { class: 'd' + (rec ? ' ok' : '') + (k === 0 ? ' today' : '') }, h('i', null, rec ? icon('check') : ''), dateLabel(d, 'short').split(' ')[0].replace('.', '').replace(',', '')));
    }
    return week;
  },
  histogram(times, mine) {
    const buckets = [[0, 60e3, '< 1 min'], [60e3, 120e3, '1–2 min'], [120e3, 240e3, '2–4 min'], [240e3, 480e3, '4–8 min'], [480e3, 1e12, '> 8 min']];
    const counts = buckets.map(b => times.filter(x => x >= b[0] && x < b[1]).length);
    const max = Math.max(1, ...counts);
    return h('div', { class: 'histo', style: { marginTop: '10px' } }, buckets.map((b, k) =>
      h('div', { class: 'row' + (mine != null && mine >= b[0] && mine < b[1] ? ' me' : '') }, h('span', { class: 'small muted' }, b[2]), h('div', { class: 'b', style: { width: (counts[k] / max * 100) + '%' } }), h('span', { class: 'small' }, String(counts[k])))));
  },
  /* tus últimos retos (sin tabla compartida) */
  myTimes(date) {
    const hst = Stats.get().history;
    const days = Object.keys(hst).sort().reverse().slice(0, 5);
    const list = h('ul', { class: 'rank-list mine' });
    days.forEach(d => {
      const r = hst[d];
      list.appendChild(h('li', { class: d === date ? 'me' : '' }, h('span', { class: 'pos' }, '#' + (r.n || E.dayNumber(d))), h('span'),
        h('span', { class: 'nm' }, dateLabel(d, 'short') + (r.archive ? ' · otro día' : '')),
        h('span', { class: 'tm' }, r.relax ? 'Relax' : fmtTime(r.ms), r.hints ? h('small', null, '💡' + r.hints) : null)));
    });
    if (days.length < 2) {
      const r = days.length ? hst[days[0]] : null;
      return h('section', null, h('h4', null, 'Tus tiempos'), h('p', { class: 'small' }, r ? `Este es tu primer reto cerrado${r.relax ? ' (en modo Relax)' : ': ' + fmtTime(r.ms)}. Vuelve mañana para seguir tu racha 🔥.` : 'Aún no has cerrado ningún reto.'));
    }
    return h('section', null, h('h4', null, 'Tus tiempos'), list);
  },
  /* ----- Ajustes ----- */
  openSettings() {
    openModal({
      title: 'Ajustes',
      render: body => {
        const row = (key, label, sub, after, extra) => {
          const sw = h('button', { class: 'switch', role: 'switch', 'aria-checked': String(!!Settings.get()[key]), 'aria-label': label });
          sw.addEventListener('click', () => { const v = !Settings.get()[key]; Settings.set({ [key]: v }); sw.setAttribute('aria-checked', String(v)); if (after) after(v); });
          return h('div', { class: 'switch-row' }, h('div', { class: 'lbl' }, label, sub ? h('small', null, sub) : null), extra || null, sw);
        };
        const confirmBox = h('div', { hidden: true, class: 'hint-out bad' },
          h('p', { style: { margin: '0 0 10px', color: 'var(--crema)' } }, 'Se borrarán tus partidas, rachas, estrellas y ajustes de este dispositivo.'),
          h('div', { class: 'toolbar left' },
            h('button', { class: 'btn small danger', onclick: () => { Store.reset(); location.hash = ''; location.reload(); } }, 'Sí, borrar todo'),
            h('button', { class: 'btn small', onclick: () => { confirmBox.hidden = true; } }, 'Cancelar')));
        const errs = Store.get('errors', []);
        appendKids(body, [
          h('section', null,
            row('sound', 'Sonido', 'Clics de madera y avisos', () => App.syncSoundBtn && App.syncSoundBtn()),
            row('haptics', 'Vibración', Haptics.supported ? 'Al trazar, acertar y fallar' : 'Este dispositivo no permite vibrar desde la web', null,
              Haptics.supported ? h('button', { class: 'btn small ghost', onclick: () => Haptics.test() }, 'Probar') : null),
            row('badges', 'Signos sobre el trazo', 'Muestra +, − e = en las juntas mientras trazas'),
            row('relax', 'Modo Relax en el reto del día', 'Sin reloj; las pistas no suman tiempo')),
          UI.profileRow ? UI.profileRow() : null,
          UI.settingsExtra ? UI.settingsExtra() : null,
          h('section', null, h('h4', null, 'Datos'),
            h('div', { class: 'toolbar left' },
              h('button', { class: 'btn small', onclick: () => { confirmBox.hidden = false; } }, icon('trash'), 'Borrar mi progreso'),
              h('button', { class: 'btn small', onclick: () => UI.copyReport() }, icon('copy'), 'Copiar informe técnico' + (errs.length ? ` (${errs.length})` : ''))),
            confirmBox),
          h('section', null, h('h4', null, 'Sobre el juego'),
            h('p', { class: 'small' }, 'BYŌDŌ City: las mismas fichas y reglas que en la mesa. Cada reto y cada nivel tienen solución comprobada por un solver que encuentra todas las operaciones.'),
            h('p', { class: 'small muted' }, 'Versión ' + (typeof BC_VERSION !== 'undefined' ? BC_VERSION : 'dev')))]);
      }
    });
  },
  /* para el tester: versión, dispositivo y últimos errores, sin datos personales */
  async copyReport() {
    const d = Diag.get();
    const rep = { version: typeof BC_VERSION !== 'undefined' ? BC_VERSION : 'dev', target: TARGET, ua: navigator.userAgent, screen: [innerWidth, innerHeight, devicePixelRatio], url: location.pathname + location.hash,
      gesture: { dragTiles: d.dragTiles, bridged: d.bridged, note: 'fichas añadidas arrastrando / de ellas, puestas por el puente (gesto que se salta una ficha)' }, errors: Store.get('errors', []) };
    const ok = await copyText(JSON.stringify(rep, null, 1));
    toast(ok ? 'Informe copiado' : 'No se ha podido copiar', { type: ok ? 'ok' : 'ko' });
  },

  /* ----- Soluciones de un reto pasado ----- */
  openSolutions(date) {
    const b = E.dailyBoard(date), a = b.analysis;
    openModal({
      title: `Soluciones del ${retoLabel(b.number).toLowerCase()}`,
      render: body => {
        const sol = a.perfect.sols[0] || [];
        const groups = sol.map((m, k) => { const op = a.ops[a.perfect.byMask.get(m)[0]]; return { path: op.path, color: CONFIG.groupColors[k % 6], op }; });
        const bv = new BoardView({ interactive: false, label: 'Solución del reto' });
        bv.setVals(b.vals); bv.setGroups(groups);
        bv.el.classList.add('modal-board');
        appendKids(body, [h('p', { class: 'small' }, `${dateLabel(date)} · ${E.LEVELS[shownLevel({ difficulty: b.difficulty, level: b.level })]} · ${a.perfect.count === 1 ? 'una única solución perfecta' : a.perfect.count + ' soluciones perfectas'}`), bv.el]);
        body.appendChild(h('ul', { class: 'oplist', style: { marginTop: '12px' } }, groups.map(g => h('li', null, h('span', { class: 'dot', style: { background: g.color } }), h('span', { class: 'ex' }, g.op.expr)))));
        const hunt = E.huntIndex(b.vals, E.HUNT_MAX);
        const byRes = new Map();
        for (const o of hunt.list) { if (!byRes.has(o.result)) byRes.set(o.result, []); byRes.get(o.result).push(o.expr); }
        const wrap = h('div', { class: 'found-groups' });
        Array.from(byRes.keys()).sort((x, y) => x - y).forEach(r => wrap.appendChild(h('div', { class: 'found-group' }, h('div', { class: 'lbl' }, 'Resultado ' + r), h('div', { class: 'chips' }, byRes.get(r).map(e => h('span', { class: 'chip' }, e))))));
        body.appendChild(h('section', null, h('h4', null, TXT.the(hunt.list.length, 'operación de la caza', 'operaciones de la caza')), wrap));
      }
    });
  },

  /* ----- Móntalo en la mesa: fichas por cifra y esquema imprimible ----- */
  openTable(pz) {
    openModal({
      title: 'Móntalo en la mesa',
      cls: 'table-sheet',
      render: body => {
        const inv = E.inventory(pz.vals), boxes = E.setsNeeded(pz.vals);
        const bv = new BoardView({ interactive: false, label: 'Esquema del panal' });
        bv.setVals(pz.vals);
        bv.el.classList.add('modal-board');
        const grid = h('div', { class: 'inv-grid' });
        inv.forEach((n, d) => { if (!n) return; grid.appendChild(h('div', { class: 'inv-cell' }, miniBoard([{ pos: [0, 0], v: d }], { cls: 'inv-tile' }), h('b', null, '×' + n))); });
        appendKids(body, [
          h('p', { class: 'small' }, `${pz.kind === 'daily' ? retoLabel(pz.number) + ' · ' + dateLabel(pz.date) : 'Panal'} · ${E.inventory(pz.vals).reduce((a, b) => a + b, 0)} fichas · ${boxes === 1 ? 'te basta con una caja de BYŌDŌ' : `necesitas ${boxes} cajas de BYŌDŌ`}.`),
          grid, bv.el,
          h('p', { class: 'small muted' }, 'Coloca las fichas como en el esquema, con la misma orientación: las juntas forman los mismos signos que aquí.'),
          h('div', { class: 'toolbar' }, h('button', { class: 'btn', onclick: () => { document.documentElement.classList.add('bc-printing'); setTimeout(() => { window.print(); document.documentElement.classList.remove('bc-printing'); }, 50); } }, icon('download'), 'Imprimir')),
          UI.tableExtra ? UI.tableExtra(pz) : null]);
      }
    });
  },

  /* ----- Resultado + compartir ----- */
  shareText(pv) {
    const pz = pv.pz, st = pv.st;
    const lines = [];
    if (pz.kind === 'daily') lines.push(`BYŌDŌ City · ${retoLabel(pz.number)} · ${E.LEVELS[shownLevel(pz)]}`);
    else if (pz.kind === 'practice') lines.push(`BYŌDŌ City · Práctica · ${E.LEVELS[pz.level]}`);
    else lines.push('BYŌDŌ City · Panal compartido');
    const parts = [st.relax ? '⏱ Relax' : `⏱ ${fmtTime(st.finalMs)}`, `💡 ${TXT.hints(st.hints.length)}`];
    if (pz.kind === 'daily' && !pv.archive) parts.push(`🔥 racha ${Stats.streakNow()}`);
    lines.push(parts.join(' · '));
    lines.push(squaresOf(st.groups));
    lines.push('🔎 ' + pv.huntLine());
    if (pz.kind !== 'daily') lines.push('Panal: ' + E.encode(pz.vals));
    if (CONFIG.shareUrl) lines.push(CONFIG.shareUrl + (pz.kind === 'custom' ? '#p-' + E.encode(pz.vals) : ''));
    return lines.join('\n');
  },
  openWin(pv) {
    const pz = pv.pz, st = pv.st;
    openModal({
      title: '¡Panal cerrado!',
      render: (body, m) => {
        const txt = UI.shareText(pv);
        const sub = (pz.kind === 'daily' ? retoLabel(pz.number) + ' · ' : '') + E.LEVELS[shownLevel(pz)] + ' · ' + (st.hints.length ? TXT.hints(st.hints.length) + (st.relax ? '' : ` (+${Math.round(st.penalty / 1000)} s)`) : 'sin pistas');
        body.appendChild(h('div', { class: 'win-hero' }, h('div', { class: 'big' }, st.relax ? 'Relax' : fmtTime(st.finalMs)), h('div', { class: 'sub' }, sub)));
        if (st.relax) body.appendChild(h('p', { class: 'small muted center' }, 'Cerrado en modo Relax: cuenta para la racha, no para los tiempos.'));
        if (pv.archive) body.appendChild(h('p', { class: 'small muted center' }, 'Reto de otro día: cuenta como completado, no para la racha.'));
        const reward = UI.rewardCard ? UI.rewardCard(pv) : null; if (reward) body.appendChild(reward);
        body.appendChild(h('section', null, h('div', { class: 'share-preview' }, txt)));
        body.appendChild(h('div', { class: 'toolbar', style: { marginTop: '12px' } },
          h('button', { class: 'btn primary', onclick: () => shareOrCopy(txt) }, icon('share'), 'Compartir'),
          h('button', { class: 'btn', onclick: () => { m.close(); pv.setObj('hunt'); } }, icon('search'), 'Seguir con la caza'),
          pz.kind !== 'daily' && typeof App !== 'undefined' && App.newPractice ? h('button', { class: 'btn', onclick: () => { m.close(); App.newPractice(pz.level || 2); } }, icon('shuffle'), 'Otro panal') : null));
        if (pz.kind === 'daily') {
          const times = Stats.times().map(x => x.ms);
          body.appendChild(h('div', { class: 'stat-grid', style: { marginTop: '18px' } },
            h('div', { class: 'stat streak' }, h('b', null, '🔥 ' + Stats.streakNow()), h('span', null, 'Racha')),
            h('div', { class: 'stat' }, h('b', null, String(Stats.get().solved)), h('span', null, 'Cerrados')),
            h('div', { class: 'stat' }, h('b', null, times.length ? fmtTime(Math.min(...times)) : '—'), h('span', null, 'Mejor')),
            h('div', { class: 'stat' }, h('b', { class: 'cd' }, liveCountdown()), h('span', null, 'Próximo reto'))));
          if (!(UI.winExtra && UI.winExtra(body, pv))) body.appendChild(UI.myTimes(pz.date));
        }
      }
    });
  }
};

/* ======================================================================
   ranking-ui.js — interfaz del ranking compartido (versiones interna y web).
   La app no lleva este archivo: ui.js solo llama a estas piezas si existen.
   ====================================================================== */
Object.assign(UI, {
  rankingSection(date, tab) {
    const sec = h('section', null, h('h4', null, 'Ranking de hoy'));
    let kind = tab === 'hunt' ? 'hunt' : 'time';
    const seg = h('div', { class: 'segmented small', style: { marginBottom: '10px' } });
    const out = h('div');
    const draw = async () => {
      seg.querySelectorAll('button').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.k === kind)));
      out.innerHTML = ''; out.appendChild(h('p', { class: 'muted small' }, 'Cargando ranking…'));
      const rows = await Ranking.top(date, kind);
      out.innerHTML = '';
      if (rows == null) { out.appendChild(h('p', { class: 'small muted' }, 'El ranking no está disponible ahora mismo.')); return; }
      if (Ranking.lastWrite === 'denied') out.appendChild(h('p', { class: 'small muted' }, 'No se ha podido guardar tu tiempo en el ranking compartido; lo tienes en este dispositivo.'));
      if (!rows.length) { out.appendChild(h('p', { class: 'small muted' }, kind === 'hunt' ? 'Aún nadie ha cazado operaciones hoy.' : 'Aún nadie ha cerrado el panal de hoy. ¡Puedes ser la primera persona!')); return; }
      const list = h('ul', { class: 'rank-list' });
      rows.slice(0, 50).forEach((r, k) => {
        const img = h('img', { alt: '' }); if (r.avatar) img.src = r.avatar; else img.style.visibility = 'hidden';
        const nm = h('span', { class: 'nm' }); nm.textContent = r.me ? r.name + ' (tú)' : r.name;
        list.appendChild(h('li', { class: r.me ? 'me' : '' }, h('span', { class: 'pos' }, String(k + 1)), img, nm,
          h('span', { class: 'tm' }, kind === 'hunt' ? r.v + ' pts' : fmtTime(r.v), kind !== 'hunt' && r.hints ? h('small', null, '💡' + r.hints) : null)));
      });
      out.appendChild(list);
    };
    const kinds = Ranking.provider === 'artifact' ? [['time', 'Cerrar panal'], ['hunt', 'Caza']] : [['time', 'Cerrar panal']];
    kinds.forEach(([k, l]) => seg.appendChild(h('button', { 'data-k': k, onclick: () => { kind = k; draw(); } }, l)));
    if (kinds.length > 1) sec.appendChild(seg);
    sec.appendChild(out);
    draw();
    return sec;
  },
  /* Estadísticas: el ranking de hoy, si hay ranking compartido */
  statsExtra(date, tab) { return Ranking.provider ? UI.rankingSection(date, tab) : null; },
  /* Ajustes: el nombre con el que se sale en el ranking */
  settingsExtra() {
    if (!Ranking.provider) return null;
    const alias = h('input', { class: 'input', id: 'bc-alias-input', maxlength: '24', placeholder: 'Ej.: Gabi', value: Settings.get().alias || '' });
    alias.addEventListener('change', () => Settings.set({ alias: alias.value.trim() }));
    return h('section', null, h('h4', null, 'Tu nombre en el ranking'),
      h('div', { class: 'field' }, h('label', { for: 'bc-alias-input' }, Ranking.provider === 'artifact' ? 'Opcional. Si no pones nada, se usa el nombre de tu cuenta.' : 'Es el nombre que verán los demás jugadores.'), alias));
  },
  /* Resultado del reto: entrar en el ranking y verlo. Devuelve false si no hay ranking (entonces se enseñan «Tus tiempos») */
  winExtra(body, pv) {
    const pz = pv.pz, st = pv.st;
    if (!Ranking.provider) return false;
    let rk = UI.rankingSection(pz.date, 'time');
    if (Ranking.provider === 'supabase' && UI.joinRanking && !Ranking.submittedFor(pz.date) && !st.relax && !pv.archive) body.appendChild(UI.joinRanking(pv, () => { const n = UI.rankingSection(pz.date, 'time'); rk.replaceWith(n); rk = n; }));
    body.appendChild(rk);
    const prize = UI.prizeNote ? UI.prizeNote() : null; if (prize) body.appendChild(prize);
    return true;
  }
});
/* al cerrar el reto: si el ranking es automático (artifact), el tiempo se envía solo */
Stats.onWin = (pz, st, opts) => { if (Ranking.auto && !opts.archive && !st.relax) Ranking.submit(pz.date, st.finalMs, st.hints.length, solutionString(pz, st)); };

/* ======================================================================
   shop.js — versión web (tienda): entrar en el ranking, premio, descuento
   al cerrar y tarjeta del juego físico. Se añade al objeto UI.
   ====================================================================== */
Object.assign(UI, {
  /* entrar en el ranking con apodo (versión web) */
  joinRanking(pv, done) {
    const input = h('input', { class: 'input', id: 'bc-nick', maxlength: '24', placeholder: 'Tu nombre o apodo', value: Settings.get().alias || '' });
    const btn = h('button', { class: 'btn primary small' }, icon('award'), 'Entrar en el ranking');
    const box = h('section', { class: 'join' }, h('h4', null, 'Ranking de hoy'), h('div', { class: 'codebox' }, input, btn), h('p', { class: 'small muted' }, 'Solo cuenta tu primera partida del día.'));
    btn.addEventListener('click', async () => {
      const nick = input.value.trim(); if (!nick) { input.focus(); return; }
      Settings.set({ alias: nick }); btn.disabled = true;
      const ok = await Ranking.submit(pv.pz.date, pv.st.finalMs, pv.st.hints.length, solutionString(pv.pz, pv.st));
      if (ok) { box.remove(); toast('¡Ya estás en el ranking!', { type: 'ok' }); done && done(); } else { btn.disabled = false; toast('No se ha podido guardar. Inténtalo de nuevo.', { type: 'ko' }); }
    });
    return box;
  },

  /* premio (opcional): el código privado permite comprobar quién es quién sin pedir datos personales */
  prizeNote() {
    if (!CONFIG.prizeText || Ranking.provider !== 'supabase') return null;
    return h('div', { class: 'reward prize' },
      h('div', { class: 'r-ico' }, icon('award')),
      h('div', null, h('b', null, CONFIG.prizeText),
        h('p', null, 'Tu código de jugador: ', h('code', null, Ranking.code()),
          CONFIG.prizeContact ? `. Si ganas, escríbenos a ${CONFIG.prizeContact} con este código.` : '. Guárdalo: lo necesitarás si ganas.')));
  },

  /* ----- Tienda: recompensa al cerrar y tarjeta del juego físico ----- */
  /* /discount/CÓDIGO aplica el código en el carrito de Shopify y redirige al producto */
  discountLink() {
    if (!CONFIG.discountCode) return '';
    let origin = '', path = '/';
    if (CONFIG.productUrl) {
      const m = /^(https?:\/\/[^/]+)(\/.*)?$/.exec(CONFIG.productUrl);
      if (m) { origin = m[1]; path = m[2] || '/'; } else path = CONFIG.productUrl;
    }
    if (origin === location.origin) origin = '';
    return origin + '/discount/' + encodeURIComponent(CONFIG.discountCode) + '?redirect=' + encodeURIComponent(path);
  },
  rewardCard(pv) {
    if (TARGET !== 'web' || !CONFIG.discountCode || pv.pz.kind !== 'daily') return null;
    emitEvent('reward_shown', { reto: pv.pz.number });
    return h('div', { class: 'reward' },
      h('div', { class: 'r-ico' }, icon('gift')),
      h('div', null, h('b', null, `Has desbloqueado ${CONFIG.discountText || 'un descuento'}`),
        h('p', null, 'Juega con las fichas de verdad. Código: ', h('code', null, CONFIG.discountCode))),
      h('a', { class: 'btn primary small', href: UI.discountLink(), target: '_top', onclick: () => emitEvent('reward_click', { reto: pv.pz.number }) }, icon('cart'), 'Usar descuento'));
  },
  /* «Móntalo en la mesa» (BYD-025): el enlace al juego físico */
  tableExtra() {
    if (TARGET !== 'web' || !CONFIG.productUrl) return null;
    return h('p', { class: 'small table-buy' }, '¿Aún no tienes las fichas? ',
      h('a', { class: 'btn small', href: CONFIG.productUrl, target: '_top', onclick: () => emitEvent('product_click', { where: 'table' }) }, icon('cart'), 'Ver el ' + (CONFIG.productName || 'juego')));
  },
  promoCard(pv) {
    if (TARGET !== 'web') return null;
    const card = h('div', { class: 'card promo' });
    if (CONFIG.productUrl) {
      const mb = miniBoard([{ pos: [0, 0], v: 3 }, { pos: [1.5 * E.RAD, -50], v: 4 }, { pos: [1.5 * E.RAD, 50], v: 7 }]);
      card.append(h('div', { class: 'promo-top' }, mb, h('div', null, h('b', null, 'Estas fichas existen'), h('p', null, CONFIG.productBlurb || `Juega a BYŌDŌ City en la mesa, con las fichas de madera de ${CONFIG.productName || 'BYŌDŌ'}.`))),
        h('a', { class: 'btn small primary', href: pv.st.solved && CONFIG.discountCode ? UI.discountLink() : CONFIG.productUrl, target: '_top', onclick: () => emitEvent('product_click', {}) }, icon('cart'), pv.st.solved && CONFIG.discountCode ? `Ver el juego con ${CONFIG.discountText || 'descuento'}` : 'Ver el juego' + (CONFIG.productPrice ? ' · ' + CONFIG.productPrice : '')));
    }
    const links = h('div', { class: 'promo-links' });
    if (CONFIG.newsletterUrl) links.appendChild(h('a', { href: CONFIG.newsletterUrl, target: '_top', onclick: () => emitEvent('newsletter_click', {}) }, icon('mail'), 'Recibe el reto cada mañana'));
    if (CONFIG.educatorsUrl) links.appendChild(h('a', { href: CONFIG.educatorsUrl, target: '_top', onclick: () => emitEvent('educators_click', {}) }, icon('school'), '¿Eres docente? Úsalo en clase'));
    if (links.children.length) card.appendChild(links);
    return card.children.length ? card : null;
  }
});

/* ======================================================================
   main-web.js — versión para la web de BYŌDŌ: solo el reto del día
   Se monta dentro de <div id="bc-root" class="bc"> (sección de Shopify)
   ====================================================================== */
const App = {
  init() {
    const R = ROOT();
    this.daily = new PlayView($('#bc-view-daily'), 'daily');
    this.daily.load(makePuzzle('daily', todayStr()));
    $('#bc-btn-help').appendChild(icon('help'));
    $('#bc-btn-stats').appendChild(icon('chart'));
    $('#bc-btn-settings').appendChild(icon('sliders'));
    $('#bc-btn-help').addEventListener('click', () => UI.openHelp());
    $('#bc-btn-stats').addEventListener('click', () => UI.openStats());
    $('#bc-btn-settings').addEventListener('click', () => UI.openSettings());
    $('#bc-btn-sound').addEventListener('click', () => { Settings.set({ sound: !Settings.get().sound }); this.syncSoundBtn(); if (Settings.get().sound) Sound.good(); });
    this.syncSoundBtn();
    this.syncStreak();
    this.daily.onShow();
    window.addEventListener('pagehide', () => { this.daily.pause(false); Store.flush(); });
    watchDay(() => this.checkDay());
    Ranking.init();
    emitEvent('view', { reto: this.daily.pz.number });
    if (!Settings.get().seenHelp) setTimeout(() => UI.openHelp(), 500);
  },
  checkDay() {
    const t = todayStr(); if (t === this.daily.pz.date) return;
    const st = this.daily.st;
    if (!st.started || st.solved) { this.daily.pause(false); this.daily.load(makePuzzle('daily', t)); this.daily.onShow(); toast('¡Hay un reto nuevo!', { type: 'ok' }); }
    else if (this._dayNotified !== t) { this._dayNotified = t; toast('Ya está disponible el reto de hoy', { ms: 6000, action: { label: 'Ir', fn: () => { this.daily.pause(false); this.daily.load(makePuzzle('daily', t)); this.daily.onShow(); } } }); }
  },
  syncStreak() {
    const b = $('#bc-btn-stats'); const old = b.querySelector('.badge'); if (old) old.remove();
    const n = Stats.streakNow();
    if (n > 0) { b.appendChild(h('span', { class: 'badge', 'aria-hidden': 'true' }, String(n))); b.setAttribute('aria-label', `Estadísticas y ranking (racha de ${n})`); }
  },
  syncSoundBtn() { const b = $('#bc-btn-sound'); b.innerHTML = ''; b.appendChild(icon(Settings.get().sound ? 'volume' : 'mute')); b.setAttribute('aria-label', Settings.get().sound ? 'Silenciar' : 'Activar sonido'); }
};
if (window.__BC_TEST__) window.__BC__ = { App, E, PlayView, keyOf };
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => App.init()); else App.init();

})();
